import { useState, useEffect, useRef } from "react";
import {
  collection, doc, getDoc, onSnapshot, setDoc, updateDoc,
  addDoc, serverTimestamp, orderBy, query, deleteDoc, where,
  arrayUnion, arrayRemove, writeBatch, getDocs
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage, auth } from "./firebase";
import {
  signOut as firebaseSignOut, signInAnonymously, signInWithEmailAndPassword,
  createUserWithEmailAndPassword, onAuthStateChanged, sendPasswordResetEmail,
} from "firebase/auth";
import { jsPDF } from "jspdf";
import EstimatorGate from "./EstimatorGate";
import PortalAccessGate from "./PortalAccessGate";
import { syncSeatCount, reportError, setAdminAccount as saveAdminAccountRemote, useLicenseStatus, redirectToLicenseEndpoint, sendSupplyRequest, sendWorkOrderEmail, sendWorkOrderText, upgradeToBundle, getCustomerId, teamMemberLogin } from "./license";

// Calls this project's OWN Cloud Functions (senate-portal-app/functions —
// dailyReportTick/sendTestReport), not the shared license service. Same
// unauthenticated-POST trust model as every other admin-triggered call in
// this codebase (see src/license.js) — the caller is always this
// already-PIN-gated Admin Setup screen.
async function callOwnFunction(name, body) {
  const res = await fetch(`https://us-central1-${getCustomerId()}.cloudfunctions.net/${name}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `${name} failed`);
  return data;
}

// ── Branding (single source of truth: public/brand-config.js) ───────────────
const BRAND = window.BRAND;

// Admin/Admin-Setup PINs and the company logo live in this instance's own
// Firestore (settings/auth, settings/branding), not in brand-config.js —
// so an admin can change them from inside the app, no redeploy needed.
// brand-config.js's ADMIN_PIN/ADMIN_SETUP_PIN/logoPath are no longer read
// by the app; DEFAULT_LOGO_URL below is only the generic fallback shown
// before a real logo has been uploaded.
// Client PINs are stored per-client in Firestore (clients/{id}.pin).
const DEFAULT_LOGO_URL = BRAND.logoPath;

// ── Design tokens ─────────────────────────────────────────────────────────
const C = BRAND.colors;

// A richer royal-blue used for the mobile-redesign nav chrome shared by
// every role (bottom nav / top nav active state, Client's FAB, Home hero
// card) — not promoted to a BRAND.colors token since existing content
// elsewhere (message bubbles, buttons inside each tab's own content) keeps
// using C.accent unchanged. (Not arbitrary — this exact hex was already
// used ad hoc for the admin sidebar's active-row highlight, see
// S.sideItem below.)
const NAV_ACCENT = "#2E5EA8";

// ── Vendor (Contractor) invite codes ─────────────────────────────────────
// Single-use, human-readable codes an admin hands off to a contractor to
// redeem on the "Join as a Contractor" screen — see doJoinVendor in
// LoginScreen and the "vendors" tab in AdminSetup.
function genInviteCode() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"; // no 0/O/1/I — easier to read aloud
  let s = "";
  for (let i = 0; i < 8; i++) s += chars[Math.floor(Math.random() * chars.length)];
  return s;
}

// ── Channels config ───────────────────────────────────────────────────────
const CHANNELS = [
  { id:"general",   label:"General",     icon:"💬", color:C.accent,  desc:"Day-to-day communication" },
  { id:"emergency", label:"Emergency",   icon:"🚨", color:C.red,     desc:"24-hour urgent issues" },
  { id:"special",   label:"Special Jobs",icon:"🧹", color:C.orange,  desc:"Floor work, deep cleans, one-time jobs" },
];

// ── i18n (Team Member view only) ─────────────────────────────────────────
// A per-device toggle (localStorage), not a per-user setting — a crew
// member on a shared tablet flips it themselves, no admin config needed.
// Scoped deliberately to team-facing screens only; Admin/Client views
// always render English regardless of this value.
const LANG_KEY = "cv_team_lang";
const TXT = {
  en: {
    teamSignInTitle: "Team Sign In",
    teamSignInSub: "Enter your PIN.",
    noTeamMembers: "No team members added yet. Use Admin Setup.",
    back: "← Back",
    yourPin: "Your PIN",
    signInAs: "Sign in as {name}",
    signIn: "Sign In",
    signedInAs: "Signed in as {name}",
    wrongPin: "Wrong PIN",
    switchClient: "Switch Client",
    signOut: "Sign Out",
    tabHome: "🏠 Home",
    tabTimeCard: "🕐 Time Card",
    tabMessages: "💬 Messages",
    tabPhotos: "📸 Photos",
    tabChecklist: "✅ Checklist",
    tabMore: "⋯ More",
    tabSupplies: "📦 Request Supplies",
    channel_general: "General",
    channel_emergency: "Emergency",
    channel_special: "Special Jobs",
    documentArea: "📷 Document an area",
    documentAreaSub: "QR code missing or damaged? Pick the area here instead of scanning.",
    readyCheckOut: "Ready to check out?",
    completeChecklistThenCheckOut: "Complete your checklist above, then check out below.",
    uploading: "Uploading…",
    clockOutBtn: "Clock Out — Photo Front Door",
    itemsComplete: "{done}/{total} complete",
    markedBy: "Marked by {name} · {date}",
    addItemPlaceholder: "Add item…",
    addBtn: "+ Add",
    clockedInAt: "Clocked in {date}",
    finishCheckOut: "✅ Finish & Check Out",
    completeChecklistToCheckOut: "Complete the checklist to check out",
    clockInBtn: "Clock In — Photo Front Door",
    accessCodes: "🔑 Access Codes",
    noAccessCodesTeam: "No access codes saved for this client yet.",
    myTimeLog: "🕐 My Time Log",
    noTimeEntries: "No time entries yet.",
    active: "ACTIVE",
    notYet: "Not yet",
    clockIn: "Clock In",
    clockOut: "Clock Out",
    requestSupplies: "Request Supplies",
    noSuppliesTracked: "No supplies are tracked for this client yet — ask your admin to add some in Setup.",
    photoOfSupplies: "Photo of current supplies (optional)",
    retakePhoto: "Retake photo",
    addPhoto: "Add photo",
    notesOptional: "Notes (optional)",
    selectAtLeastOneSupply: "Select at least one supply.",
    noRestockEmails: "No restock emails set for this client — ask your admin to add them in Setup.",
    sendRequestFailed: "Failed to send request — check connection and try again.",
    sending: "Sending…",
    sendRequest: "Send Request",
    requestSent: "Request sent",
    restockContactsEmailed: "Your restock contacts have been emailed.",
    sendAnotherRequest: "Send another request",
    clockedInStep1: "You're clocked in — Step 1 of 2",
    takeBeforePhotos: "Take BEFORE photo(s)",
    addAnotherPhoto: "+ Add another photo",
    tapBeforePhoto: "Tap to take BEFORE photo",
    continueToAfter: "Continue to After →",
    step2of2: "Step 2 of 2",
    takeAfterPhotos: "Take AFTER photo(s)",
    tapAfterPhoto: "Tap to take AFTER photo",
    saveFinish: "Save & Finish",
    skipForNow: "Skip for now",
    uploadFailed: "Upload failed — check connection and try again",
    photoCheckIn: "Photo Check-In",
    startCheckIn: "Start Check-In →",
    step1of2: "Step 1 of 2",
    photoAreaBeforeCleaning: "Photo the area as-is, before cleaning begins",
    photoAreaAfterCleaning: "Photo the same area after cleaning is complete",
    checkInComplete: "Check-in Complete!",
    areaDocumentedFor: "{area} documented for {client}",
    beforeAfterSaved: "Before & after photos saved ✓",
    documentAnotherArea: "+ Document another area",
    noPhotosYet: "No photos yet",
    photosEmptyHint: "Cleaners document areas by scanning QR codes. Photos appear here after each session.",
    allAreas: "All Areas",
    before: "Before",
    after: "After",
    emergencyBanner: "Emergency channel — for urgent issues requiring a response within 24 hours.",
    specialBanner: "Special jobs — floor work, deep cleans, quarterly services, one-time requests.",
    noMessagesIn: "No messages in {channel} yet.",
    messagePlaceholder: "Message {channel}…",
    send: "Send",
    greeting: "Hi, {name}",
    whichClient: "Which client are you working with?",
    mySchedule: "My Schedule",
    noClientsAssigned: "No clients assigned yet — check with your admin.",
    backClients: "← Clients",
    noUpcomingShifts: "No upcoming shifts scheduled.",
    today: "Today",
    allDay: "All day",
  },
  es: {
    teamSignInTitle: "Inicio de Sesión del Equipo",
    teamSignInSub: "Ingresa tu PIN.",
    noTeamMembers: "Aún no se han agregado miembros del equipo. Usa Configuración de Administrador.",
    back: "← Atrás",
    yourPin: "Tu PIN",
    signInAs: "Iniciar sesión como {name}",
    signIn: "Iniciar Sesión",
    signedInAs: "Sesión iniciada como {name}",
    wrongPin: "PIN incorrecto",
    switchClient: "Cambiar Cliente",
    signOut: "Cerrar Sesión",
    tabHome: "🏠 Inicio",
    tabTimeCard: "🕐 Tarjeta de Tiempo",
    tabMessages: "💬 Mensajes",
    tabPhotos: "📸 Fotos",
    tabChecklist: "✅ Lista de Verificación",
    tabMore: "⋯ Más",
    tabSupplies: "📦 Solicitar Suministros",
    channel_general: "General",
    channel_emergency: "Emergencia",
    channel_special: "Trabajos Especiales",
    documentArea: "📷 Documentar un área",
    documentAreaSub: "¿Falta el código QR o está dañado? Elige el área aquí en lugar de escanear.",
    readyCheckOut: "¿Listo para salir?",
    completeChecklistThenCheckOut: "Completa tu lista de verificación arriba, luego marca tu salida abajo.",
    uploading: "Subiendo…",
    clockOutBtn: "Marcar Salida — Foto de la Puerta Principal",
    itemsComplete: "{done}/{total} completado",
    markedBy: "Marcado por {name} · {date}",
    addItemPlaceholder: "Agregar artículo…",
    addBtn: "+ Agregar",
    clockedInAt: "Marcó entrada {date}",
    finishCheckOut: "✅ Terminar y Marcar Salida",
    completeChecklistToCheckOut: "Completa la lista de verificación para marcar salida",
    clockInBtn: "Marcar Entrada — Foto de la Puerta Principal",
    accessCodes: "🔑 Códigos de Acceso",
    noAccessCodesTeam: "Aún no hay códigos de acceso guardados para este cliente.",
    myTimeLog: "🕐 Mi Registro de Tiempo",
    noTimeEntries: "Aún no hay registros de tiempo.",
    active: "ACTIVO",
    notYet: "Aún no",
    clockIn: "Entrada",
    clockOut: "Salida",
    requestSupplies: "Solicitar Suministros",
    noSuppliesTracked: "Aún no se rastrean suministros para este cliente — pide a tu administrador que agregue algunos en Configuración.",
    photoOfSupplies: "Foto de los suministros actuales (opcional)",
    retakePhoto: "Volver a tomar foto",
    addPhoto: "Agregar foto",
    notesOptional: "Notas (opcional)",
    selectAtLeastOneSupply: "Selecciona al menos un suministro.",
    noRestockEmails: "No hay correos de reabastecimiento configurados para este cliente — pide a tu administrador que los agregue en Configuración.",
    sendRequestFailed: "Error al enviar la solicitud — verifica tu conexión e intenta de nuevo.",
    sending: "Enviando…",
    sendRequest: "Enviar Solicitud",
    requestSent: "Solicitud enviada",
    restockContactsEmailed: "Se ha enviado un correo a tus contactos de reabastecimiento.",
    sendAnotherRequest: "Enviar otra solicitud",
    clockedInStep1: "Marcaste entrada — Paso 1 de 2",
    takeBeforePhotos: "Toma foto(s) del ANTES",
    addAnotherPhoto: "+ Agregar otra foto",
    tapBeforePhoto: "Toca para tomar foto del ANTES",
    continueToAfter: "Continuar al Después →",
    step2of2: "Paso 2 de 2",
    takeAfterPhotos: "Toma foto(s) del DESPUÉS",
    tapAfterPhoto: "Toca para tomar foto del DESPUÉS",
    saveFinish: "Guardar y Terminar",
    skipForNow: "Omitir por ahora",
    uploadFailed: "Error al subir — verifica tu conexión e intenta de nuevo",
    photoCheckIn: "Registro Fotográfico",
    startCheckIn: "Iniciar Registro →",
    step1of2: "Paso 1 de 2",
    photoAreaBeforeCleaning: "Fotografía el área tal como está, antes de comenzar la limpieza",
    photoAreaAfterCleaning: "Fotografía la misma área después de terminar la limpieza",
    checkInComplete: "¡Registro Completo!",
    areaDocumentedFor: "{area} documentado para {client}",
    beforeAfterSaved: "Fotos de antes y después guardadas ✓",
    documentAnotherArea: "+ Documentar otra área",
    noPhotosYet: "Aún no hay fotos",
    photosEmptyHint: "Los limpiadores documentan áreas escaneando códigos QR. Las fotos aparecen aquí después de cada sesión.",
    allAreas: "Todas las Áreas",
    before: "Antes",
    after: "Después",
    emergencyBanner: "Canal de emergencia — para asuntos urgentes que requieren respuesta dentro de 24 horas.",
    specialBanner: "Trabajos especiales — trabajo de pisos, limpiezas profundas, servicios trimestrales, solicitudes únicas.",
    noMessagesIn: "Aún no hay mensajes en {channel}.",
    messagePlaceholder: "Mensaje para {channel}…",
    send: "Enviar",
    greeting: "Hola, {name}",
    whichClient: "¿Con qué cliente estás trabajando?",
    mySchedule: "Mi Horario",
    noClientsAssigned: "Aún no se te han asignado clientes — consulta con tu administrador.",
    backClients: "← Clientes",
    noUpcomingShifts: "No hay turnos próximos programados.",
    today: "Hoy",
    allDay: "Todo el día",
  },
};
const t = (lang, key, vars) => {
  let s = (TXT[lang] && TXT[lang][key]) ?? TXT.en[key] ?? key;
  if (vars) Object.entries(vars).forEach(([k,v]) => { s = s.split(`{${k}}`).join(v); });
  return s;
};
function LangToggle({ lang, onToggle, style }) {
  return (
    <button onClick={onToggle} style={{ padding:"5px 12px", borderRadius:"20px", border:"1.5px solid rgba(255,255,255,0.35)", background:"transparent", color:"inherit", fontSize:"12px", fontWeight:"700", cursor:"pointer", letterSpacing:".03em", ...style }}>
      {lang==="en" ? "ES" : "EN"}
    </button>
  );
}

// ── Utilities ─────────────────────────────────────────────────────────────
const uid     = () => Math.random().toString(36).slice(2, 10);
const fmtDate = (ts) => {
  if (!ts) return "";
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString("en-US", { month:"short", day:"numeric", hour:"numeric", minute:"2-digit", hour12:true });
};
// ── Schedule date helpers (local time — deliberately not toISOString(),
// which is UTC and can roll the date over near midnight) ───────────────────
const DOW = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
const dateKey = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
const todayKey = () => dateKey(new Date());
const addDays  = (d, n) => { const r = new Date(d); r.setDate(r.getDate()+n); return r; };
const startOfWeek = (d) => addDays(d, -d.getDay()); // Sunday
const daysInMonth = (y, m) => new Date(y, m, 0).getDate(); // m is 1-indexed
const fmtDateKey = (key) => {
  const [y,m,day] = key.split("-").map(Number);
  return new Date(y, m-1, day).toLocaleDateString("en-US", { weekday:"short", month:"short", day:"numeric" });
};
// Strips a leading emoji+space from an i18n tab label (e.g. "🕐 Time Card")
// to get the short text used under a bottom-nav icon, without duplicating
// translation strings just for that shorter rendering.
const stripIcon = (s) => s.replace(/^\S+\s*/, "");
const fmtTime12 = (hhmm) => {
  if (!hhmm) return "";
  const [h,m] = hhmm.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2,"0")} ${period}`;
};

// Admin-facing "unread" — a client has a client/team message newer than
// the last time Admin opened that client's own Messages tab. Both
// timestamps live denormalized on the client doc itself (see
// MessagesPanel's send() and AdminPortal's activeTab effect), so this
// computes straight from the already-subscribed `clients` collection —
// no per-client listener needed for a sidebar/dashboard badge.
const isClientUnread = (c) => {
  if (!c?.lastMessageAt || c.lastMessageByRole === "admin") return false;
  const last = c.lastMessageAt?.toMillis ? c.lastMessageAt.toMillis() : 0;
  const read = c.adminLastReadAt?.toMillis ? c.adminLastReadAt.toMillis() : 0;
  return last > read;
};

const DEFAULT_CHECKLIST_COMMERCIAL = [
  "Entrance & lobby swept and mopped","Restrooms sanitized and restocked",
  "Trash collected and bins re-lined","Breakroom surfaces wiped down",
  "Windows and glass surfaces cleaned","Floors vacuumed / swept",
];
const DEFAULT_CHECKLIST_RESIDENTIAL = [
  "Kitchen counters, sink, and appliances wiped down","Bathrooms cleaned and sanitized",
  "Bedrooms dusted and tidied","Trash collected and bins re-lined",
  "Floors vacuumed / swept and mopped","Windows and mirrors cleaned",
];
// kept as the default export name for any older call sites; equals the commercial list
const DEFAULT_CHECKLIST = DEFAULT_CHECKLIST_COMMERCIAL;
const DEFAULT_AREAS_COMMERCIAL = [
  "Lobby & Entrance","Restrooms","Break Room","Offices","Conference Room","Hallways",
];
const DEFAULT_AREAS_RESIDENTIAL = [
  "Kitchen","Bathrooms","Bedrooms","Living Room","Laundry Room",
];
const slugifyArea = (s) => s.trim().toLowerCase().replace(/\s+/g,"-");
const DEFAULT_SUPPLIES = [
  "Gloves","Trash bags S","Trash bags L","Cleaning Solution","Glass Cleaner",
  "Microfiber rags","Paper Towels","Toilet paper","Hand soap","Toilet liners","Air freshener",
];
const uploadFile = async (path, file) => {
  const r = ref(storage, path);
  await uploadBytes(r, file);
  return getDownloadURL(r);
};

// jsPDF's addImage needs a data URI, not a remote URL — fetches a signature
// image (a Storage download URL) and re-encodes it in memory.
const urlToDataUrl = async (url) => {
  const blob = await (await fetch(url)).blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

// Renders a signed Work Order as a one-page PDF — title/description, who
// it's for, and each assigned crew member's signature (or "not yet signed")
// — shared by Admin Setup's Work Orders tab and the Contractor Portal, since
// both need the identical document for Download/Email/Text.
async function buildWorkOrderPdf(wo, { vendorName, clientName }) {
  const doc = new jsPDF();
  doc.setFontSize(18); doc.setTextColor(26,47,72);
  doc.text(wo.title, 14, 20);
  doc.setFontSize(11); doc.setTextColor(90,110,138);
  doc.text([vendorName, clientName].filter(Boolean).join("  ·  "), 14, 28);
  let y = 40;
  if (wo.description) {
    doc.setFontSize(11); doc.setTextColor(26,47,72);
    const lines = doc.splitTextToSize(wo.description, 180);
    doc.text(lines, 14, y);
    y += lines.length * 6 + 8;
  }
  doc.setFontSize(13); doc.setTextColor(26,47,72);
  doc.text("Signatures", 14, y); y += 8;
  for (const c of (wo.assignedCrew||[])) {
    const sig = (wo.signatures||[]).find(s=>s.crewId===c.id);
    doc.setFontSize(11);
    doc.text(`${c.name} — ${sig ? `Signed by ${sig.clientName || "client"} on ` + fmtDateKey(sig.signedAt) : "Not yet signed"}`, 14, y);
    y += 6;
    if (sig) {
      try {
        const dataUrl = await urlToDataUrl(sig.signatureUrl);
        doc.addImage(dataUrl, "PNG", 14, y, 60, 26);
      } catch { /* image fetch failed — leave the text line, skip the image */ }
      y += 32;
    } else {
      y += 4;
    }
  }
  return doc;
}

// Download/Email/Text actions for one Work Order — identical whether shown
// to Admin or to the contractor who signed it. Regenerates the PDF fresh on
// every action rather than caching it, since a work order can pick up a new
// signature at any time.
function WorkOrderActions({ wo, vendorName, clientName, prefillEmail }) {
  const [busy, setBusy] = useState(false);
  const [shareMode, setShareMode] = useState(null); // "email" | "text" | null
  const [recipient, setRecipient] = useState("");
  const [msg, setMsg] = useState("");

  const openShare = (mode) => {
    setShareMode(mode); setMsg("");
    setRecipient(mode === "email" ? (prefillEmail || "") : "");
  };

  const download = async () => {
    setBusy(true); setMsg("");
    try {
      const doc = await buildWorkOrderPdf(wo, { vendorName, clientName });
      doc.save(`${wo.title.replace(/[^a-z0-9]+/gi,"-")}.pdf`);
    } catch (e) { setMsg("Failed to generate PDF."); }
    setBusy(false);
  };

  const sendEmail = async () => {
    if (!recipient.trim()) { setMsg("Enter an email address."); return; }
    setBusy(true); setMsg("");
    try {
      const doc = await buildWorkOrderPdf(wo, { vendorName, clientName });
      const base64 = doc.output("datauristring").split("base64,")[1];
      await sendWorkOrderEmail(recipient.trim(), wo.title, base64);
      setMsg("✓ Emailed."); setShareMode(null); setRecipient("");
    } catch (e) { setMsg("✗ " + e.message); }
    setBusy(false);
  };

  const sendText = async () => {
    if (!recipient.trim()) { setMsg("Enter a phone number."); return; }
    setBusy(true); setMsg("");
    try {
      const doc = await buildWorkOrderPdf(wo, { vendorName, clientName });
      const url = await uploadFile(`workOrders/${wo.id}/shared_${Date.now()}.pdf`, doc.output("blob"));
      await sendWorkOrderText(recipient.trim(), `Signed work order "${wo.title}": ${url}`);
      setMsg("✓ Texted."); setShareMode(null); setRecipient("");
    } catch (e) { setMsg("✗ " + e.message); }
    setBusy(false);
  };

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:"6px" }}>
      <div style={{ display:"flex", gap:"6px", flexWrap:"wrap" }}>
        <button style={S.btnSm(C.offWhite,C.accent)} onClick={download} disabled={busy}>⬇️ Download</button>
        <button style={S.btnSm(C.offWhite,C.accent)} onClick={()=>openShare("email")} disabled={busy}>📧 Email</button>
        <button style={S.btnSm(C.offWhite,C.accent)} onClick={()=>openShare("text")} disabled={busy}>📱 Text</button>
      </div>
      {shareMode && (
        <div style={{ display:"flex", gap:"6px", alignItems:"center", flexWrap:"wrap" }}>
          <input
            style={{ ...S.input, marginBottom:0, flex:1, minWidth:"160px" }}
            type={shareMode==="email"?"email":"tel"}
            placeholder={shareMode==="email"?"Recipient email":"Recipient phone"}
            value={recipient} onChange={e=>setRecipient(e.target.value)}
          />
          <button style={S.btnSm(C.green)} onClick={shareMode==="email"?sendEmail:sendText} disabled={busy}>{busy?"Sending…":"Send"}</button>
          <button style={S.btnSm(C.offWhite,C.textMid)} onClick={()=>setShareMode(null)}>✕</button>
        </div>
      )}
      {msg && <div style={{ fontSize:"12px", color:msg.startsWith("✓")?C.green:C.red }}>{msg}</div>}
    </div>
  );
}

// ── QR code printing ─────────────────────────────────────────────────────
// Opens a plain, print-formatted window (no app chrome) and triggers the
// browser print dialog once the QR image has actually loaded — printing
// before the <img> resolves would otherwise leave a blank code on paper.
const qrImageSrc = (url, size=300) =>
  `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(url)}`;

function printQrCode(clientName, area, url) {
  const w = window.open("", "_blank", "width=420,height=560");
  if (!w) return;
  w.document.write(`<!DOCTYPE html><html><head><title>QR Code — ${area}</title>
    <style>
      @page { margin: 24px; }
      body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; text-align: center; padding: 32px; }
      h1 { font-size: 18px; margin: 0 0 2px; }
      h2 { font-size: 15px; font-weight: 500; color: #667; margin: 0 0 24px; text-transform: capitalize; }
      img { width: 280px; height: 280px; }
    </style></head>
    <body>
      <h1>${clientName}</h1>
      <h2>${area.replace(/-/g, " ")}</h2>
      <img src="${qrImageSrc(url, 280)}" alt="QR code" onload="window.print()" />
    </body></html>`);
  w.document.close();
}

function printAllQrCodes(clientName, areas, buildUrl) {
  const w = window.open("", "_blank", "width=480,height=640");
  if (!w) return;
  const sections = areas.map(area => `
    <section>
      <h1>${clientName}</h1>
      <h2>${area.replace(/-/g, " ")}</h2>
      <img src="${qrImageSrc(buildUrl(area), 280)}" alt="QR code for ${area}" class="qr-img" />
    </section>`).join("");
  w.document.write(`<!DOCTYPE html><html><head><title>QR Codes — ${clientName}</title>
    <style>
      @page { margin: 24px; }
      body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; text-align: center; }
      section { page-break-after: always; padding: 48px 32px; }
      section:last-child { page-break-after: auto; }
      h1 { font-size: 18px; margin: 0 0 2px; }
      h2 { font-size: 15px; font-weight: 500; color: #667; margin: 0 0 24px; text-transform: capitalize; }
      img { width: 280px; height: 280px; }
    </style></head>
    <body>${sections}</body></html>`);
  w.document.close();
  // Multiple <img> tags means no single onload to hang the print trigger on —
  // wait for every image in the window to finish loading instead.
  const imgs = () => Array.from(w.document.images);
  const waitAndPrint = () => {
    if (imgs().every(img => img.complete)) { w.print(); }
    else { setTimeout(waitAndPrint, 150); }
  };
  waitAndPrint();
}

// ── CustodialView attribution footer ─────────────────────────────────────
// Shown on every screen of every deployed instance (a buyer's own portal
// and CustodialView's own template alike) — hardcoded colors rather than
// the buyer's `C` palette since this is attribution to CustodialView
// itself, not part of the buyer's own branding.
function CustodialViewBadge({ gradId, size=14 }) {
  return (
    <svg viewBox="0 0 120 120" width={size} height={size} style={{ display:"inline-block", verticalAlign:"-3px", marginRight:"5px" }}>
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="120" y2="120" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#173D6B"/>
          <stop offset="1" stopColor="#0A2647"/>
        </linearGradient>
      </defs>
      <rect width="120" height="120" rx="27" fill={`url(#${gradId})`}/>
      <rect x="28" y="28" width="46" height="46" rx="12" fill="#FFFFFF" fillOpacity="0.4"/>
      <rect x="42" y="42" width="46" height="46" rx="12" fill="#FFFFFF" fillOpacity="0.68"/>
      <rect x="56" y="56" width="34" height="34" rx="10" fill="#4C8DFF"/>
    </svg>
  );
}
function BrandFooterOverlay() {
  return (
    <a href="https://custodialview.com" target="_blank" rel="noopener noreferrer"
      style={{ position:"fixed", bottom:"14px", left:0, right:0, textAlign:"center",
        fontSize:"11px", color:"rgba(255,255,255,0.45)", textDecoration:"none" }}>
      <CustodialViewBadge gradId="footerBadgeOverlay"/>Powered by <b style={{ color:"rgba(255,255,255,0.75)" }}>CustodialView</b>
    </a>
  );
}
function BrandFooterBar() {
  return (
    <div style={{ height:"28px", flexShrink:0, boxSizing:"border-box", display:"flex",
      alignItems:"center", justifyContent:"center", fontSize:"11px", color:"#8A99AD",
      borderTop:"1px solid #E4E9F0", background:"#FFFFFF" }}>
      <a href="https://custodialview.com" target="_blank" rel="noopener noreferrer"
        style={{ color:"inherit", textDecoration:"none" }}>
        <CustodialViewBadge gradId="footerBadgeBar"/>Powered by <b style={{ color:"#2F6BE0" }}>CustodialView</b>
      </a>
    </div>
  );
}

// ── Avatar ────────────────────────────────────────────────────────────────
function Avatar({ src, name, size=32, style={} }) {
  const initials = (name||"?").split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase();
  return src
    ? <img src={src} alt={name} style={{ width:size,height:size,borderRadius:"50%",objectFit:"cover",flexShrink:0,...style }} />
    : <div style={{ width:size,height:size,borderRadius:"50%",background:C.accent,color:C.white,display:"flex",alignItems:"center",justifyContent:"center",fontSize:size*0.38,fontWeight:"700",flexShrink:0,...style }}>{initials}</div>;
}

// ── Styles ────────────────────────────────────────────────────────────────
const S = {
  app:      { fontFamily:"'Poppins','Segoe UI',sans-serif", background:C.offWhite, minHeight:"100vh", color:C.textDark },
  center:   { minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", padding:"24px", background:`linear-gradient(160deg,${C.navy} 0%,${C.navyMid} 100%)` },
  card:     { background:C.white, borderRadius:"16px", padding:"36px 32px", maxWidth:"420px", width:"100%", boxShadow:"0 24px 64px rgba(0,0,0,0.22)" },
  title:    { fontSize:"20px", fontWeight:"700", marginBottom:"6px", color:C.textDark },
  sub:      { fontSize:"13px", color:C.textMid, marginBottom:"22px" },
  input:    { width:"100%", padding:"11px 14px", borderRadius:"8px", border:`1.5px solid ${C.border}`, fontSize:"15px", outline:"none", boxSizing:"border-box", marginBottom:"12px" },
  btn:      { width:"100%", padding:"13px", background:C.accent, color:C.white, border:"none", borderRadius:"10px", fontWeight:"700", fontSize:"15px", cursor:"pointer" },
  btnSm:    (bg=C.accent,color=C.white) => ({ padding:"8px 16px", background:bg, color, border:"none", borderRadius:"8px", fontWeight:"600", fontSize:"13px", cursor:"pointer", whiteSpace:"nowrap" }),
  ghostBtn: { width:"100%", padding:"13px", background:"transparent", color:C.textMid, border:`1px solid ${C.border}`, borderRadius:"10px", fontWeight:"600", fontSize:"14px", cursor:"pointer", marginTop:"10px" },
  err:      { color:C.red, fontSize:"13px", marginBottom:"10px" },

  // Shell
  // No overflow:hidden here (even though this is a fixed-height row) — on
  // a flex item, `overflow` anything but `visible` resets the browser's
  // default content-based min-height to 0, which lets this row collapse
  // or balloon unpredictably under flex pressure instead of holding
  // 56px. headerLeft (below) already clips its own overflow on its own,
  // which is all that's actually needed for the truncating name span.
  header:     { background:C.navy, color:C.white, padding:"0 16px", paddingTop:"env(safe-area-inset-top)", display:"flex", alignItems:"center", justifyContent:"space-between", height:"56px", flexShrink:0, position:"sticky", top:0, zIndex:50 },
  // minWidth:0 lets this flex child actually shrink below its content's
  // natural width (the flex default is to refuse to, which is what made a
  // long client name wrap onto 3 lines and blow out the fixed-height
  // header on a real phone instead of truncating) — overflow:hidden backs
  // that up so a still-too-long child clips instead of pushing the row.
  headerLeft: { display:"flex", alignItems:"center", gap:"10px", minWidth:0, overflow:"hidden" },
  // Right-side header buttons (role pill, language toggle, Switch Client,
  // Sign Out, …) — never shrinks/wraps its buttons illegibly; if there
  // genuinely isn't room for all of them, this scrolls horizontally
  // instead, so everything stays reachable on a narrow phone.
  headerRight:{ display:"flex", gap:"8px", alignItems:"center", flexShrink:0, overflowX:"auto" },
  pill:       (bg,color) => ({ background:bg, color, borderRadius:"6px", padding:"4px 10px", fontSize:"12px", fontWeight:"700", whiteSpace:"nowrap", flexShrink:0 }),
  logoutBtn:  { background:"transparent", border:"1px solid #3A5275", color:"#A8C0E8", borderRadius:"6px", padding:"5px 12px", fontSize:"12px", cursor:"pointer", whiteSpace:"nowrap", flexShrink:0 },

  // Admin layout
  adminShell: { display:"flex", height:"calc(100vh - 56px - 28px - env(safe-area-inset-top))" },
  sidebar:    { width:"240px", minWidth:"240px", background:C.navy, borderRight:`1px solid #1E3655`, overflowY:"auto", display:"flex", flexDirection:"column" },
  sideSection:{ padding:"12px 12px 4px", fontSize:"11px", fontWeight:"700", color:"#4A6A90", letterSpacing:"0.8px", textTransform:"uppercase" },
  sideItem:   (a) => ({ padding:"10px 14px", cursor:"pointer", display:"flex", alignItems:"center", gap:"10px", background:a?"rgba(46,94,168,0.3)":"transparent", borderLeft:a?`3px solid ${C.accent}`:"3px solid transparent", color:a?C.white:"#A8C0E8", fontSize:"14px", fontWeight:a?"600":"400" }),
  adminMain:  { flex:1, display:"flex", flexDirection:"column", overflowY:"auto" },
  channelBar: { display:"flex", background:C.white, borderBottom:`1px solid ${C.border}`, padding:"0 16px", position:"sticky", top:0, zIndex:30 },
  channelTab: (a,color) => ({ padding:"13px 14px", fontSize:"13px", fontWeight:a?"700":"500", color:a?color:C.textMid, borderBottom:a?`2px solid ${color}`:"2px solid transparent", cursor:"pointer", display:"flex", alignItems:"center", gap:"6px" }),

  // Portal tabs
  tabs:    { display:"flex", background:C.white, borderBottom:`1px solid ${C.border}`, padding:"0 16px", position:"sticky", top:"calc(56px + env(safe-area-inset-top))", zIndex:40, overflowX:"auto" },
  tab:     (a,color=C.accent) => ({ padding:"13px 14px", fontSize:"13px", fontWeight:a?"700":"500", color:a?color:C.textMid, borderBottom:a?`2px solid ${color}`:"2px solid transparent", cursor:"pointer", whiteSpace:"nowrap", display:"flex", alignItems:"center", gap:"5px" }),
  // Longhand padding (not shorthand) — mobile nav overrides just
  // paddingBottom to clear the fixed bottom nav, and mixing shorthand
  // `padding` with a longhand override in the same inline style across
  // renders triggers a React dev warning.
  body:    { maxWidth:"780px", margin:"0 auto", paddingTop:"16px", paddingRight:"16px", paddingBottom:"16px", paddingLeft:"16px" },

  // Messages
  msgList:    { display:"flex", flexDirection:"column", gap:"14px", marginBottom:"16px" },
  msgRow:     (mine) => ({ display:"flex", flexDirection:mine?"row-reverse":"row", gap:"8px", alignItems:"flex-end" }),
  msgBubble:  (mine,color=C.accent) => ({ maxWidth:"72%", background:mine?color:C.white, color:mine?C.white:C.textDark, borderRadius:mine?"16px 16px 4px 16px":"16px 16px 16px 4px", padding:"11px 14px", boxShadow:"0 2px 8px rgba(0,0,0,0.07)", border:mine?"none":`1px solid ${C.border}` }),
  msgMeta:    (mine) => ({ fontSize:"11px", color:mine?"rgba(255,255,255,0.65)":C.textMid, marginTop:"5px", textAlign:mine?"right":"left" }),
  msgImg:     { maxWidth:"220px", borderRadius:"8px", marginTop:"8px", display:"block" },
  msgCompose: (color=C.accent) => ({ display:"flex", gap:"8px", alignItems:"flex-end", position:"sticky", bottom:0, background:C.offWhite, paddingTop:"10px", paddingBottom:"calc(12px + env(safe-area-inset-bottom))", borderTop:`2px solid ${color}22` }),
  msgInput:   { flex:1, padding:"11px 14px", borderRadius:"10px", border:`1.5px solid ${C.border}`, fontSize:"14px", outline:"none", resize:"none", minHeight:"42px", maxHeight:"120px" },
  sendBtn:    (color=C.accent) => ({ padding:"10px 18px", background:color, color:C.white, border:"none", borderRadius:"10px", fontWeight:"700", fontSize:"14px", cursor:"pointer" }),
  attachBtn:  { padding:"10px 12px", background:C.offWhite, border:`1.5px solid ${C.border}`, borderRadius:"10px", fontSize:"18px", cursor:"pointer" },

  // Emergency banner
  emergencyBanner: { background:"#FFF0EF", border:`1px solid ${C.red}44`, borderRadius:"10px", padding:"12px 16px", marginBottom:"14px", display:"flex", alignItems:"center", gap:"10px", fontSize:"13px", color:C.red, fontWeight:"600" },

  // Checklist
  checkCard:  { background:C.white, borderRadius:"12px", border:`1px solid ${C.border}`, overflow:"hidden", marginBottom:"12px" },
  checkItem:  (done) => ({ display:"flex", alignItems:"flex-start", gap:"12px", padding:"13px 16px", background:done?"#F0FBF4":C.white }),
  checkBox:   (done) => ({ width:"22px", height:"22px", borderRadius:"6px", border:`2px solid ${done?C.green:C.border}`, background:done?C.green:C.white, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", flexShrink:0, marginTop:"1px" }),
  checkText:  (done) => ({ fontSize:"14px", fontWeight:"500", color:done?C.textMid:C.textDark, textDecoration:done?"line-through":"none", lineHeight:"1.4" }),
  checkMeta:  { fontSize:"11px", color:C.textMid, marginTop:"3px" },
  addRow:     { display:"flex", gap:"8px", padding:"12px 16px", background:C.offWhite },
  addInput:   { flex:1, padding:"9px 12px", borderRadius:"8px", border:`1.5px solid ${C.border}`, fontSize:"14px", outline:"none" },
  progressWrap:{ background:C.border, borderRadius:"99px", height:"8px", marginBottom:"18px", overflow:"hidden" },
  progressBar: (p) => ({ width:`${p}%`, height:"100%", background:p===100?C.green:C.accent, borderRadius:"99px", transition:"width .4s" }),

  // Settings
  sectionTitle: { fontSize:"12px", fontWeight:"700", color:C.textMid, marginBottom:"10px", textTransform:"uppercase", letterSpacing:"0.5px" },
  settingRow:   { display:"flex", alignItems:"center", justifyContent:"space-between", padding:"14px 16px", borderBottom:`1px solid ${C.border}` },

  // Admin setup
  memberCard: { background:C.white, borderRadius:"10px", border:`1px solid ${C.border}`, padding:"14px 16px", marginBottom:"10px", display:"flex", alignItems:"center", gap:"12px" },
  spinner:    { display:"flex", alignItems:"center", justifyContent:"center", minHeight:"100vh", background:C.navy, color:C.white, fontSize:"15px", gap:"10px" },

  // Unread badge
  badge: (color) => ({ background:color, color:C.white, borderRadius:"99px", fontSize:"10px", fontWeight:"700", padding:"1px 6px", minWidth:"16px", textAlign:"center" }),

  // Shared mobile/desktop nav shell (Client, Team, Admin, Contractor) —
  // bottom nav on mobile, top S.tabs bar on desktop. The FAB is Client-only
  // ("Report Issue" — no other role has one dominant floating action).
  bottomNav:     { position:"fixed", bottom:0, left:0, right:0, display:"flex", background:C.white, borderTop:`1px solid ${C.border}`, paddingBottom:"env(safe-area-inset-bottom)", zIndex:45 },
  bottomNavItem: (active) => ({ flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:"2px", padding:"8px 0 6px", color: active?NAV_ACCENT:C.textMid, cursor:"pointer" }),
  fab:           { position:"fixed", right:"16px", bottom:"calc(64px + env(safe-area-inset-bottom) + 12px)", background:NAV_ACCENT, color:C.white, border:"none", borderRadius:"999px", padding:"13px 18px", fontWeight:"700", fontSize:"14px", boxShadow:"0 8px 20px rgba(46,94,168,0.4)", cursor:"pointer", zIndex:46, display:"flex", alignItems:"center", gap:"6px" },
  backRow:       { fontSize:"13px", fontWeight:"600", color:NAV_ACCENT, marginBottom:"14px", cursor:"pointer" },

  // Client Home dashboard
  homeTileGrid:  { display:"grid", gridTemplateColumns:"1fr 1fr", gap:"10px", marginBottom:"18px" },
  homeTile:      { background:C.white, border:`1px solid ${C.border}`, borderRadius:"12px", padding:"14px", cursor:"pointer", position:"relative" },
  homeTileBadge: { position:"absolute", top:"10px", right:"10px" },
  homeTileLabel: { fontSize:"13px", fontWeight:"600", color:C.textDark, marginTop:"8px" },
  homeTileSub:   { fontSize:"11px", color:C.textMid, marginTop:"2px" },
  homeCard:      { background:`linear-gradient(135deg, ${C.navy}, ${NAV_ACCENT})`, color:C.white, borderRadius:"14px", padding:"18px", marginBottom:"18px", cursor:"pointer" },

  // Visits / Requests — also reused by Contractor's crew/schedule rows
  visitCard:   { display:"flex", alignItems:"center", gap:"12px", background:C.white, border:`1px solid ${C.border}`, borderRadius:"12px", padding:"12px 14px", marginBottom:"8px" },
  requestCard: { display:"flex", alignItems:"center", gap:"10px", background:C.white, border:`1px solid ${C.border}`, borderRadius:"12px", padding:"12px 14px", marginBottom:"8px" },
  statusPill:  (status) => ({
    fontSize:"10px", fontWeight:"700", padding:"4px 9px", borderRadius:"999px", whiteSpace:"nowrap", border:"none",
    ...(status==="done" ? { background:"#E7F8EE", color:C.green }
      : status==="in_progress" ? { background:"#FFF5E6", color:"#B8720A" }
      : status==="scheduled" ? { background:"#EEF3FB", color:NAV_ACCENT }
      : { background:"#FFF0EF", color:C.red }),
  }),

  // Messages channel switcher (pill row instead of top-level tabs) — shared
  // by Client and Team
  channelPillRow: { display:"flex", gap:"8px", marginBottom:"16px", overflowX:"auto" },
  channelPill:    (active,color) => ({ padding:"7px 14px", borderRadius:"999px", fontSize:"12px", fontWeight:"700", whiteSpace:"nowrap", cursor:"pointer", background: active?color:C.offWhite, color: active?C.white:C.textMid, border:`1px solid ${active?color:C.border}` }),
};

// ─────────────────────────────────────────────────────────────────────────
// MESSAGES PANEL (reused across all roles/channels)
// ─────────────────────────────────────────────────────────────────────────
const dateToStr = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;

function MessagesPanel({ clientId, channelId, role, member, senderName, senderPhoto, lang="en" }) {
  const ch      = CHANNELS.find(c=>c.id===channelId) || CHANNELS[0];
  const [messages,  setMessages]  = useState([]);
  const [text,      setText]      = useState("");
  const [imgFile,   setImgFile]   = useState(null);
  const [imgPreview,setImgPreview]= useState(null);
  const [uploading, setUploading] = useState(false);
  // A long-running channel means loading (and scrolling past) its entire
  // history every time it's opened — this narrows what's fetched/rendered
  // instead of always pulling everything. Defaults to 30 days back, not
  // all-time, same default used for Edifice View's Activity feed filter.
  const [sinceDate, setSinceDate] = useState(() => dateToStr(addDays(new Date(), -30)));
  const fileRef   = useRef();
  const bottomRef = useRef();

  useEffect(() => {
    if (!clientId) return;
    setMessages([]);
    const constraints = [orderBy("createdAt","asc")];
    if (sinceDate) constraints.push(where("createdAt", ">=", new Date(`${sinceDate}T00:00:00`)));
    const q = query(collection(db,"clients",clientId,"channels",channelId,"messages"), ...constraints);
    return onSnapshot(q, snap => setMessages(snap.docs.map(d=>({id:d.id,...d.data()}))));
  }, [clientId, channelId, sinceDate]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior:"smooth" }); }, [messages]);

  const handleFile = (e) => {
    const f = e.target.files[0]; if (!f) return;
    setImgFile(f);
    const reader = new FileReader();
    reader.onload = () => setImgPreview(reader.result);
    reader.readAsDataURL(f);
  };

  const send = async () => {
    if (!text.trim() && !imgFile) return;
    setUploading(true);
    let imgUrl = null;
    if (imgFile) imgUrl = await uploadFile(`clients/${clientId}/channels/${channelId}/${Date.now()}`, imgFile);
    await addDoc(collection(db,"clients",clientId,"channels",channelId,"messages"), {
      role, text:text.trim(), imgUrl,
      senderName: senderName || (role==="client"?"Client":"Team"),
      senderPhoto: senderPhoto || null,
      createdAt: serverTimestamp(),
    });
    // Denormalized onto the client doc (not a per-channel/per-message read)
    // so Admin's unread badges — sidebar, Home's Clients tile, the Team
    // member→properties view — can compute straight from the `clients`
    // collection they already have subscribed, no extra listeners per
    // client. Only client/team senders mark a client unread for Admin;
    // Admin's own messages shouldn't flag themselves as unread-by-Admin.
    if (role !== "admin") {
      updateDoc(doc(db,"clients",clientId), { lastMessageAt: serverTimestamp(), lastMessageByRole: role }).catch(()=>{});
    }
    setText(""); setImgFile(null); setImgPreview(null);
    if (fileRef.current) fileRef.current.value = "";
    setUploading(false);
  };

  const isTeamSide = role === "team" || role === "admin";

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100%" }}>
      {channelId === "emergency" && (
        <div style={S.emergencyBanner}>
          <span style={{ fontSize:"20px" }}>🚨</span>
          <span>{t(lang,"emergencyBanner")}</span>
        </div>
      )}
      {channelId === "special" && (
        <div style={{ ...S.emergencyBanner, background:"#FFF5EE", borderColor:C.orange+"44", color:C.orange }}>
          <span style={{ fontSize:"20px" }}>🧹</span>
          <span>{t(lang,"specialBanner")}</span>
        </div>
      )}

      <div style={{ display:"flex", alignItems:"center", justifyContent:"flex-end", gap:"8px", marginBottom:"8px" }}>
        <label style={{ fontSize:"12px", color:C.textMid }}>Since</label>
        <input type="date" style={{ ...S.input, marginBottom:0, padding:"5px 8px", width:"auto", fontSize:"12px" }} value={sinceDate} onChange={e=>setSinceDate(e.target.value)} />
        {sinceDate && <button style={S.btnSm(C.offWhite,C.textMid)} onClick={()=>setSinceDate("")}>Show all</button>}
      </div>
      <div style={S.msgList}>
        {messages.length === 0 && (
          <div style={{ textAlign:"center", color:C.textMid, fontSize:"13px", padding:"40px 0" }}>
            {sinceDate
              ? `No messages since ${new Date(`${sinceDate}T00:00:00`).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}.`
              : t(lang,"noMessagesIn",{channel: role==="team" ? t(lang,`channel_${channelId}`) : ch.label})}
          </div>
        )}
        {messages.map(m => {
          const mine = isTeamSide ? (m.role==="team"||m.role==="admin") : m.role==="client";
          return (
            <div key={m.id} style={S.msgRow(mine)}>
              <Avatar src={mine ? senderPhoto : m.senderPhoto} name={m.senderName||"?"} size={30} />
              <div>
                <div style={S.msgBubble(mine, ch.color)}>
                  {m.text && <div style={{ fontSize:"14px", lineHeight:"1.5" }}>{m.text}</div>}
                  {m.imgUrl && <img src={m.imgUrl} alt="attachment" style={S.msgImg} />}
                  <div style={S.msgMeta(mine)}>{m.senderName} · {fmtDate(m.createdAt)}</div>
                </div>
              </div>
            </div>
          );
        })}
        {imgPreview && (
          <div style={{ display:"flex", justifyContent:"flex-end" }}>
            <div style={{ position:"relative" }}>
              <img src={imgPreview} alt="preview" style={{ maxWidth:"120px", borderRadius:"8px", border:`2px solid ${ch.color}` }} />
              <button onClick={()=>{setImgPreview(null);setImgFile(null);}}
                style={{ position:"absolute", top:"-8px", right:"-8px", background:C.red, color:C.white, border:"none", borderRadius:"99px", width:"22px", height:"22px", cursor:"pointer", fontSize:"13px" }}>×</button>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <div style={S.msgCompose(ch.color)}>
        <input type="file" ref={fileRef} accept="image/*" style={{ display:"none" }} onChange={handleFile} />
        <button style={S.attachBtn} onClick={()=>fileRef.current?.click()}>📷</button>
        <textarea style={S.msgInput} placeholder={role==="team" ? t(lang,"messagePlaceholder",{channel:t(lang,`channel_${channelId}`)}) : `Message ${ch.label}…`} value={text}
          onChange={e=>setText(e.target.value)}
          onKeyDown={e=>{ if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();send();}}} rows={1} />
        {uploading
          ? <span style={{ fontSize:"12px", color:C.textMid, padding:"10px" }}>{t(lang,"sending")}</span>
          : <button style={S.sendBtn(ch.color)} onClick={send}>{t(lang,"send")}</button>}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// CHECKLIST TAB
// ─────────────────────────────────────────────────────────────────────────
function ChecklistTab({ clientId, role, member, myOpenEntry, clockOut, clockUploading, clockErr, lang="en" }) {
  const [items,   setItems]   = useState([]);
  const [newItem, setNewItem] = useState("");

  useEffect(() => {
    if (!clientId) return;
    return onSnapshot(collection(db,"clients",clientId,"checklist"), snap => {
      const docs = snap.docs.map(d=>({id:d.id,...d.data()}));
      docs.sort((a,b)=>(a.order||0)-(b.order||0));
      setItems(docs);
    });
  }, [clientId]);

  const toggle = async (item) => {
    const r = doc(db,"clients",clientId,"checklist",item.id);
    const byName = role==="admin"?"Admin":role==="team"?(member?.name||"Team"):"Client";
    item.done
      ? await updateDoc(r,{done:false,doneAt:null,doneBy:null})
      : await updateDoc(r,{done:true,doneAt:new Date().toISOString(),doneBy:byName});
  };

  const addItem = async () => {
    if (!newItem.trim()) return;
    await addDoc(collection(db,"clients",clientId,"checklist"),{ text:newItem.trim(),done:false,doneAt:null,doneBy:null,order:items.length });
    setNewItem("");
  };

  const deleteItem = async (id) => await deleteDoc(doc(db,"clients",clientId,"checklist",id));

  const done = items.filter(i=>i.done).length;
  const pct  = items.length ? Math.round((done/items.length)*100) : 0;

  return (
    <div>
      {role==="team" && myOpenEntry && (
        <div style={{ background:"#EEF3FB", border:`1.5px solid ${C.accent}`, borderRadius:"12px", padding:"14px 16px", marginBottom:"16px" }}>
          <div style={{ fontWeight:"700", fontSize:"14px", color:C.navy, marginBottom:"2px" }}>{t(lang,"readyCheckOut")}</div>
          <div style={{ fontSize:"12px", color:C.textMid, marginBottom:"10px" }}>{t(lang,"completeChecklistThenCheckOut")}</div>
          {clockErr && <div style={S.err}>{clockErr}</div>}
          <button onClick={clockOut} disabled={clockUploading}
            style={{ width:"100%", padding:"14px 0", background:clockUploading?C.textMid:C.red, color:C.white, border:"none", borderRadius:"10px", fontSize:"15px", fontWeight:"700", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:"10px" }}>
            <span style={{ fontSize:"20px" }}>📷</span>{clockUploading?t(lang,"uploading"):t(lang,"clockOutBtn")}
          </button>
        </div>
      )}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"10px" }}>
        <div style={{ fontSize:"13px", color:C.textMid, fontWeight:"600" }}>{role==="team" ? t(lang,"itemsComplete",{done,total:items.length}) : `${done}/${items.length} complete`}</div>
        <div style={{ fontSize:"13px", fontWeight:"700", color:pct===100?C.green:C.accent }}>{pct}%</div>
      </div>
      <div style={S.progressWrap}><div style={S.progressBar(pct)} /></div>
      <div style={S.checkCard}>
        {items.map((item,idx) => (
          <div key={item.id} style={{ ...S.checkItem(item.done), borderBottom:idx===items.length-1?"none":`1px solid ${C.border}` }}>
            <div style={S.checkBox(item.done)} onClick={()=>toggle(item)}>
              {item.done && <span style={{ color:C.white,fontSize:"13px",fontWeight:"800" }}>✓</span>}
            </div>
            <div style={{ flex:1 }}>
              <div style={S.checkText(item.done)}>{item.text}</div>
              {item.done && <div style={S.checkMeta}>{role==="team" ? t(lang,"markedBy",{name:item.doneBy,date:fmtDate(item.doneAt)}) : `Marked by ${item.doneBy} · ${fmtDate(item.doneAt)}`}</div>}
            </div>
            {(role==="team"||role==="admin") && (
              <button onClick={()=>deleteItem(item.id)} style={{ background:"transparent",border:"none",color:C.textMid,cursor:"pointer",fontSize:"16px",padding:"0 4px" }}>×</button>
            )}
          </div>
        ))}
        {(role==="team"||role==="admin") && (
          <div style={S.addRow}>
            <input style={S.addInput} placeholder={role==="team"?t(lang,"addItemPlaceholder"):"Add item…"} value={newItem} onChange={e=>setNewItem(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addItem()} />
            <button style={S.btnSm()} onClick={addItem}>{role==="team"?t(lang,"addBtn"):"+ Add"}</button>
          </div>
        )}
        {items.length===0 && role==="client" && (
          <div style={{ padding:"20px",textAlign:"center",color:C.textMid,fontSize:"13px" }}>No checklist items yet.</div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// SETTINGS TAB (client only)
// ─────────────────────────────────────────────────────────────────────────
function SettingsTab({ clientId, clientData, onLogoUpdate }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const handleLogo = async (e) => {
    const f = e.target.files[0]; if (!f) return;
    setUploading(true);
    const url = await uploadFile(`clients/${clientId}/logo_${Date.now()}`, f);
    await updateDoc(doc(db,"clients",clientId),{ logoUrl:url });
    onLogoUpdate(url);
    setUploading(false);
  };

  // Opt-in text alert when a new message is waiting for a reply — self-
  // service: the client enters their own number and flips this on, no
  // admin involvement. Dispatched by onNewMessage in functions/index.js.
  const [notifyPhone, setNotifyPhone] = useState(clientData?.notifyPhone || "");
  const [notifySmsOptIn, setNotifySmsOptIn] = useState(!!clientData?.notifySmsOptIn);
  const [savingNotify, setSavingNotify] = useState(false);
  const [notifySaved, setNotifySaved] = useState(false);
  const saveNotifyPrefs = async () => {
    setSavingNotify(true); setNotifySaved(false);
    await updateDoc(doc(db,"clients",clientId), { notifyPhone: notifyPhone.trim(), notifySmsOptIn });
    setSavingNotify(false); setNotifySaved(true);
  };

  return (
    <div>
      <div style={S.sectionTitle}>Company Settings</div>
      <div style={{ background:C.white, borderRadius:"12px", border:`1px solid ${C.border}`, overflow:"hidden", marginBottom:"20px" }}>
        <div style={S.settingRow}>
          <div>
            <div style={{ fontWeight:"600", fontSize:"14px" }}>Company Logo</div>
            <div style={{ fontSize:"12px", color:C.textMid, marginTop:"2px" }}>Shown in your portal header</div>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:"12px" }}>
            {clientData?.logoUrl && <img src={clientData.logoUrl} alt="logo" style={{ height:"36px", objectFit:"contain", borderRadius:"6px" }} />}
            <input type="file" accept="image/*" ref={fileRef} style={{ display:"none" }} onChange={handleLogo} />
            <button style={S.btnSm(uploading?C.textMid:C.accent)} onClick={()=>fileRef.current?.click()} disabled={uploading}>
              {uploading?"Uploading…":clientData?.logoUrl?"Replace":"Upload"}
            </button>
          </div>
        </div>
        <div style={{ ...S.settingRow, borderBottom:"none" }}>
          <div>
            <div style={{ fontWeight:"600", fontSize:"14px" }}>Company Name</div>
            <div style={{ fontSize:"12px", color:C.textMid, marginTop:"2px" }}>{clientData?.name}</div>
          </div>
        </div>
      </div>

      <div style={S.sectionTitle}>Message Notifications</div>
      <div style={{ background:C.white, borderRadius:"12px", border:`1px solid ${C.border}`, padding:"16px" }}>
        <div style={{ fontSize:"12px", color:C.textMid, marginBottom:"12px" }}>
          Get a text when there's a new message waiting for your reply.
        </div>
        <input style={S.input} type="tel" placeholder="Phone number" value={notifyPhone} onChange={e=>setNotifyPhone(e.target.value)} />
        <label style={{ display:"flex", alignItems:"center", gap:"8px", fontSize:"13px", marginBottom:"12px", cursor:"pointer" }}>
          <input type="checkbox" checked={notifySmsOptIn} onChange={e=>setNotifySmsOptIn(e.target.checked)} />
          Text me when there's a new message
        </label>
        <button style={S.btnSm(savingNotify?C.textMid:C.accent)} onClick={saveNotifyPrefs} disabled={savingNotify}>
          {savingNotify?"Saving…":"Save"}
        </button>
        {notifySaved && <span style={{ fontSize:"12px", color:C.green, marginLeft:"10px" }}>✓ Saved</span>}
      </div>
    </div>
  );
}

// Shared by AdminPortal (as its own top-level nav) and AdminSetup (as a
// floating bottom nav so Setup is never a dead end — see AdminSetup's
// onDone/onGoTo). "setup" always points at the current AdminSetup screen
// itself; AdminPortal's `goNav` treats it as a navigate-away action
// (never shows "active" there), while AdminSetup shows it permanently
// active since that's where you already are. "estimator"/"team" aren't
// slots here — reached only from their Home tiles, same treatment as
// Contractor's "contract" (VENDOR_NAV_TABS below). "messages" isn't a
// real `activeView` either — AdminPortal's goNav routes it to "clients"
// (same destination its Home tile uses) and treats it as active whenever
// activeView is "clients", since that's where Messages actually lives.
const ADMIN_NAV_TABS = [
  { id:"home",     icon:"🏠", short:"Home",     label:"🏠 Home" },
  { id:"messages", icon:"💬", short:"Messages", label:"💬 Messages" },
  { id:"schedule", icon:"📅", short:"Schedule", label:"📅 Schedule" },
  { id:"setup",    icon:"⚙️", short:"Setup",    label:"⚙️ Setup" },
];

// ─────────────────────────────────────────────────────────────────────────
// ADMIN SETUP SCREEN
// ─────────────────────────────────────────────────────────────────────────
function AdminSetup({ onDone, onGoTo, initialTab, logoUrl, adminSetupPin, estimatorPin, adminEmail, businessInfo }) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);
  const [members,     setMembers]     = useState([]);
  const [clients,     setClients]     = useState([]);
  const [newName,     setNewName]     = useState("");
  const [newPin,      setNewPin]      = useState("");
  const [newClient,   setNewClient]   = useState("");
  const [newClientType, setNewClientType] = useState("commercial"); // "commercial" | "residential"
  const [clientListFilter, setClientListFilter] = useState("active"); // "active" | "inactive"
  const [tab,         setTab]         = useState(initialTab || "members");
  const [saving,      setSaving]      = useState(false);
  const [qrClient,       setQrClient]       = useState(null);
  const [qrClientFilter, setQrClientFilter] = useState("active"); // "active" | "inactive"
  const [newArea,        setNewArea]        = useState("");
  const [scheduleRules,  setScheduleRules]  = useState([]);
  const [scheduleClient, setScheduleClient] = useState(null);
  const [shiftForm,      setShiftForm]      = useState(null); // { memberId, mode, days:Set, monthDay, rangeStart }
  const [shiftErr,       setShiftErr]       = useState("");
  const [savingShift,    setSavingShift]    = useState(false);
  const [vendors,          setVendors]          = useState([]);
  const [vendorContracts,  setVendorContracts]  = useState([]);
  const [newVendorName,    setNewVendorName]    = useState("");
  const [newVendorClientId,setNewVendorClientId]= useState("");
  const [savingVendor,     setSavingVendor]     = useState(false);
  const [vendorErr,        setVendorErr]        = useState("");
  const [lastInviteCode,   setLastInviteCode]   = useState(null); // { companyName, code }
  const [workOrders,       setWorkOrders]       = useState([]);
  const [newWOAssignType,  setNewWOAssignType]  = useState("vendor"); // "vendor" | "staff"
  const [newWOVendorId,    setNewWOVendorId]    = useState("");
  const [newWOClientId,    setNewWOClientId]    = useState(""); // staff path only — vendor path derives clientId from the vendor
  const [newWOStaffIds,    setNewWOStaffIds]    = useState(() => new Set());
  const [newWOTitle,       setNewWOTitle]       = useState("");
  const [newWODescription, setNewWODescription] = useState("");
  const [newWODueDate,     setNewWODueDate]     = useState("");
  const [newWOFile,        setNewWOFile]        = useState(null);
  const [savingWO,         setSavingWO]         = useState(false);
  const [woErr,            setWoErr]            = useState("");
  const suppliesStatus  = useLicenseStatus("supplies");  // loading | active | inactive
  const estimatorStatus = useLicenseStatus("estimator"); // loading | active | inactive
  const [suppliesBusy,   setSuppliesBusy]   = useState(false);
  const [bundleBusy,     setBundleBusy]     = useState(false);
  const [bundleErr,      setBundleErr]      = useState("");
  const [suppliesClient, setSuppliesClient] = useState(null);
  const [newSupply,      setNewSupply]      = useState("");
  const [editingSupplyEmails, setEditingSupplyEmails] = useState(null); // { id, emails:[e1,e2] }
  const [reportsClient,       setReportsClient]       = useState(null); // client whose report panel is open
  const [editingReportEmails, setEditingReportEmails] = useState(null); // { id, emails:[e1,e2] }
  const [testSendMsg,         setTestSendMsg]         = useState(""); // last "Send test ___ now" result, per-panel
  const [sendingTest,         setSendingTest]         = useState(false);
  const [reportsCfg,          setReportsCfg]          = useState(null); // settings/reports doc
  const [savingReportsCfg,    setSavingReportsCfg]    = useState(false);
  const [notifCfg,            setNotifCfg]            = useState(null); // settings/notifications doc
  const [savingNotifCfg,      setSavingNotifCfg]      = useState(false);
  const [testTextMsg,         setTestTextMsg]         = useState("");
  const [sendingTestText,     setSendingTestText]     = useState(false);
  useEffect(() => {
    return onSnapshot(doc(db,"settings","reports"), s => {
      setReportsCfg(s.exists() ? s.data() : { opsDigestEnabled:false, opsCadence:"monthly", opsRecipients:[] });
    });
  }, []);
  useEffect(() => {
    return onSnapshot(doc(db,"settings","notifications"), s => {
      setNotifCfg(s.exists() ? s.data() : { smsEnabled:false, phoneNumbers:[], clockInOutEnabled:false, missedVisitEnabled:false, workOrderMissedEnabled:false, workOrderCompletedEnabled:false, timeZone:"America/New_York" });
    });
  }, []);
  const sendTestClientRecap = async (clientId) => {
    setSendingTest(true); setTestSendMsg("");
    try { const r = await callOwnFunction("sendTestReport", { clientId }); setTestSendMsg(`✓ Sent to ${r.to.join(", ")}`); }
    catch (e) { setTestSendMsg(`✗ ${e.message}`); }
    setSendingTest(false);
  };
  const sendTestOpsDigest = async () => {
    setSendingTest(true); setTestSendMsg("");
    try { const r = await callOwnFunction("sendTestReport", { ops:true }); setTestSendMsg(`✓ Sent to ${r.to.join(", ")}`); }
    catch (e) { setTestSendMsg(`✗ ${e.message}`); }
    setSendingTest(false);
  };
  const sendTestText = async () => {
    setSendingTestText(true); setTestTextMsg("");
    try { const r = await callOwnFunction("sendTestSms", {}); setTestTextMsg(`✓ Sent to ${r.to.join(", ")}`); }
    catch (e) { setTestTextMsg(`✗ ${e.message}`); }
    setSendingTestText(false);
  };
  const subscribeSupplies = async () => {
    setSuppliesBusy(true);
    const redirected = await redirectToLicenseEndpoint("createCheckoutSession", {
      product: "supplies", successUrl: window.location.href, cancelUrl: window.location.href,
    });
    if (!redirected) setSuppliesBusy(false);
  };
  const subscribeBundle = async () => {
    setBundleBusy(true);
    const redirected = await redirectToLicenseEndpoint("createCheckoutSession", {
      product: "bundle", successUrl: window.location.href, cancelUrl: window.location.href,
    });
    if (!redirected) setBundleBusy(false);
  };
  // Adds Supply Tracker to an already-active Estimator subscription at no
  // extra cost — swaps that same subscription onto the $25/mo bundle price
  // instead of starting a second $5/mo one (see upgradeToBundle).
  const addSuppliesViaBundle = async () => {
    setBundleBusy(true); setBundleErr("");
    try { await upgradeToBundle(); }
    catch (e) { setBundleErr(e.message || "Failed to upgrade — try again."); }
    setBundleBusy(false);
  };
  const [editingMemberPin,  setEditingMemberPin]  = useState(null); // { id, value }
  const [editingClientPin,  setEditingClientPin]  = useState(null); // { id, value }
  const [editingClientName, setEditingClientName] = useState(null); // { id, value }
  const [uploadingLogo,  setUploadingLogo]  = useState(false);
  const [editingAdminSetupPin, setEditingAdminSetupPin] = useState(null);
  const [editingEstimatorPin,  setEditingEstimatorPin]  = useState(null);
  const [editingAdminAccount,  setEditingAdminAccount]  = useState(null); // { email, pin } while editing, else null
  const [savingAdminAccount,   setSavingAdminAccount]   = useState(false);
  const [adminAccountErr,      setAdminAccountErr]      = useState("");
  const [bizForm,   setBizForm]   = useState(null);
  const [savingBiz, setSavingBiz] = useState(false);
  const [bizSaved,  setBizSaved]  = useState(false);
  const fileRefs = useRef({});
  const clientFileRefs = useRef({});
  const logoFileRef = useRef();

  const handleCompanyLogo = async (file) => {
    setUploadingLogo(true);
    const url = await uploadFile(`branding/logo_${Date.now()}`, file);
    await setDoc(doc(db,"settings","branding"), { logoUrl:url }, { merge:true });
    setUploadingLogo(false);
  };

  const saveAdminPin = async (field, value) => {
    if (!value.trim()) return;
    await setDoc(doc(db,"settings","auth"), { [field]: value.trim() }, { merge:true });
    setEditingAdminSetupPin(null);
    setEditingEstimatorPin(null);
  };

  const saveAdminAccount = async () => {
    const email = editingAdminAccount.email.trim();
    const pin = editingAdminAccount.pin;
    if (!email) { setAdminAccountErr("Enter an email."); return; }
    if (pin.length < 6) { setAdminAccountErr("PIN must be at least 6 characters."); return; }
    setSavingAdminAccount(true); setAdminAccountErr("");
    try {
      await saveAdminAccountRemote(email, pin);
      setEditingAdminAccount(null);
    } catch (e) {
      setAdminAccountErr(e.message || "Failed to save admin account.");
    }
    setSavingAdminAccount(false);
  };

  const [exporting, setExporting] = useState(false);
  const exportData = async () => {
    setExporting(true);
    try {
      const scheduleSnap = await getDocs(collection(db,"schedule"));
      const schedule = scheduleSnap.docs.map(d=>({id:d.id,...d.data()}));
      const clientsWithChecklists = await Promise.all(clients.map(async c => {
        const checklistSnap = await getDocs(collection(db,"clients",c.id,"checklist"));
        return { ...c, checklist: checklistSnap.docs.map(d=>({id:d.id,...d.data()})) };
      }));
      const payload = { exportedAt: new Date().toISOString(), teamMembers: members, clients: clientsWithChecklists, schedule };
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${BRAND.name.replace(/\s+/g,"-").toLowerCase()}-export-${Date.now()}.json`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  // Seed the form from whatever's saved in Firestore, falling back to the
  // generic template defaults in brand-config.js — so an unconfigured buyer
  // sees today's effective values (not a blank form) and can edit any subset.
  useEffect(() => {
    setBizForm({
      companyName: businessInfo?.companyName ?? BRAND.name,
      contact: {
        email:       businessInfo?.contact?.email       ?? BRAND.contact.email,
        phone:       businessInfo?.contact?.phone        ?? BRAND.contact.phone,
        website:     businessInfo?.contact?.website      ?? BRAND.contact.website,
        serviceArea: businessInfo?.contact?.serviceArea  ?? BRAND.contact.serviceArea,
      },
      pricing: {
        payrollTax:     businessInfo?.pricing?.payrollTax     ?? BRAND.pricing.payrollTax,
        overhead:       businessInfo?.pricing?.overhead       ?? BRAND.pricing.overhead,
        margin:         businessInfo?.pricing?.margin         ?? BRAND.pricing.margin,
        tileMarkup:     businessInfo?.pricing?.tileMarkup     ?? BRAND.pricing.tileMarkup,
        carpetMarkup:   businessInfo?.pricing?.carpetMarkup   ?? BRAND.pricing.carpetMarkup,
        tileMin:        businessInfo?.pricing?.tileMin        ?? BRAND.pricing.tileMin,
        carpetMin:      businessInfo?.pricing?.carpetMin      ?? BRAND.pricing.carpetMin,
        minProfit:      businessInfo?.pricing?.minProfit      ?? BRAND.pricing.minProfit,
        pubRestroomFee: businessInfo?.pricing?.pubRestroomFee ?? BRAND.pricing.pubRestroomFee,
        pubRates: {
          biweekly: businessInfo?.pricing?.pubRates?.biweekly ?? BRAND.pricing.pubRates.biweekly,
          weekly:   businessInfo?.pricing?.pubRates?.weekly   ?? BRAND.pricing.pubRates.weekly,
          "3x":     businessInfo?.pricing?.pubRates?.["3x"]   ?? BRAND.pricing.pubRates["3x"],
          daily:    businessInfo?.pricing?.pubRates?.daily    ?? BRAND.pricing.pubRates.daily,
        },
        pubTypeMult: {
          office:    businessInfo?.pricing?.pubTypeMult?.office    ?? BRAND.pricing.pubTypeMult.office,
          retail:    businessInfo?.pricing?.pubTypeMult?.retail    ?? BRAND.pricing.pubTypeMult.retail,
          govt:      businessInfo?.pricing?.pubTypeMult?.govt      ?? BRAND.pricing.pubTypeMult.govt,
          medical:   businessInfo?.pricing?.pubTypeMult?.medical   ?? BRAND.pricing.pubTypeMult.medical,
          warehouse: businessInfo?.pricing?.pubTypeMult?.warehouse ?? BRAND.pricing.pubTypeMult.warehouse,
        },
      },
    });
  }, [businessInfo]);

  const saveBusinessInfo = async () => {
    setSavingBiz(true);
    await setDoc(doc(db,"settings","business"), bizForm, { merge:true });
    setSavingBiz(false);
    setBizSaved(true);
    setTimeout(()=>setBizSaved(false), 1500);
  };

  useEffect(() => {
    const u1 = onSnapshot(collection(db,"teamMembers"), s=>setMembers(s.docs.map(d=>({id:d.id,...d.data()}))));
    const u2 = onSnapshot(collection(db,"clients"),     s=>setClients(s.docs.map(d=>({id:d.id,...d.data()}))));
    const u3 = onSnapshot(collection(db,"scheduleRules"), s=>setScheduleRules(s.docs.map(d=>({id:d.id,...d.data()}))));
    const u4 = onSnapshot(collection(db,"vendors"),         s=>setVendors(s.docs.map(d=>({id:d.id,...d.data()}))));
    const u5 = onSnapshot(collection(db,"vendorContracts"), s=>setVendorContracts(s.docs.map(d=>({id:d.id,...d.data()}))));
    const u6 = onSnapshot(collection(db,"workOrders"),      s=>setWorkOrders(s.docs.map(d=>({id:d.id,...d.data()}))));
    return ()=>{ u1(); u2(); u3(); u4(); u5(); u6(); };
  }, []);

  // A Work Order is either a short in-app description or an uploaded PDF
  // (or both). Two assignment paths:
  //  - Contractor: created unassigned for one vendor — the contractor
  //    picks which of their own crew it goes to (VendorWorkOrdersTab
  //    "Assign to crew"), Admin never picks crew directly here.
  //  - Staff: Admin assigns directly to one or more in-house team members
  //    (no vendor layer between Admin and staff, so there's no one else
  //    to do the assigning) — assignedCrew is set immediately.
  // Either way, each assigned person signs it themselves on-site from
  // their own Team Member login (TeamWorkOrdersTab). `assignedCrewIds` is
  // a flat id array kept alongside `assignedCrew` purely so Firestore can
  // query "work orders assigned to me" with array-contains — the
  // {id,name} object array can't be queried that way.
  const addWorkOrder = async () => {
    if (newWOAssignType==="vendor" && !newWOVendorId) { setWoErr("Pick which contractor this is for."); return; }
    if (newWOAssignType==="staff" && !newWOClientId) { setWoErr("Pick which client this work order is for."); return; }
    if (newWOAssignType==="staff" && newWOStaffIds.size===0) { setWoErr("Pick at least one staff member."); return; }
    if (!newWOTitle.trim()) { setWoErr("Enter a title."); return; }
    setWoErr(""); setSavingWO(true);
    try {
      const vendor = newWOAssignType==="vendor" ? vendors.find(v => v.id === newWOVendorId) : null;
      const assignedCrew = newWOAssignType==="staff"
        ? members.filter(m => newWOStaffIds.has(m.id)).map(m => ({ id:m.id, name:m.name }))
        : [];
      let pdfUrl = null;
      const uploadKey = newWOAssignType==="vendor" ? newWOVendorId : "staff";
      if (newWOFile) pdfUrl = await uploadFile(`workOrders/${uploadKey}/${Date.now()}_${newWOFile.name}`, newWOFile);
      await addDoc(collection(db,"workOrders"), {
        vendorId: newWOAssignType==="vendor" ? newWOVendorId : null,
        clientId: newWOAssignType==="vendor" ? (vendor?.clientId || null) : newWOClientId,
        title: newWOTitle.trim(), description: newWODescription.trim() || null, pdfUrl,
        dueDate: newWODueDate || null,
        assignedCrew, assignedCrewIds: assignedCrew.map(c=>c.id),
        signatures: [], createdAt: serverTimestamp(),
      });
      setNewWOVendorId(""); setNewWOClientId(""); setNewWOStaffIds(new Set());
      setNewWOTitle(""); setNewWODescription(""); setNewWODueDate(""); setNewWOFile(null);
    } catch (e) {
      setWoErr("Failed to save: " + e.message);
    }
    setSavingWO(false);
  };
  const deleteWorkOrder = (wo) => {
    if (window.confirm(`Delete the work order "${wo.title}"? This can't be undone.`)) deleteDoc(doc(db,"workOrders",wo.id));
  };

  const addVendor = async () => {
    if (!newVendorName.trim()) { setVendorErr("Enter a company name."); return; }
    if (!newVendorClientId) { setVendorErr("Pick which client they'll service."); return; }
    setVendorErr(""); setSavingVendor(true);
    const client = clients.find(c=>c.id===newVendorClientId);
    const vendorRef = doc(collection(db,"vendors"));
    const contractRef = doc(collection(db,"vendorContracts"));
    const code = genInviteCode();
    const batch = writeBatch(db);
    batch.set(vendorRef, {
      clientId: newVendorClientId, companyName: newVendorName.trim(), createdAt: serverTimestamp(),
    });
    batch.set(contractRef, {
      clientId: newVendorClientId, vendorId: vendorRef.id, vendorName: newVendorName.trim(),
      startDate: todayKey(), endDate: null, createdAt: serverTimestamp(),
    });
    batch.set(doc(db,"inviteCodes",code), {
      clientId: newVendorClientId, vendorId: vendorRef.id, used: false, createdAt: serverTimestamp(),
    });
    await batch.commit();
    setNewVendorName(""); setNewVendorClientId(""); setSavingVendor(false);
    setLastInviteCode({ companyName: newVendorName.trim(), clientName: client?.name||"", code });
  };

  const endVendorContract = (contract) => {
    if (window.confirm(`End ${contract.vendorName}'s contract? Their crew will keep any PINs they already have but won't be scheduled going forward.`))
      updateDoc(doc(db,"vendorContracts",contract.id), { endDate: todayKey() });
  };

  // Reinstating writes a fresh row rather than clearing the old contract's
  // endDate, so the ended period stays in history (same reasoning as
  // vendorName being snapshotted at creation) — "current contract" is
  // still just whichever row has endDate === null.
  const reinstateVendor = (vendor) => {
    addDoc(collection(db,"vendorContracts"), {
      clientId: vendor.clientId, vendorId: vendor.id, vendorName: vendor.companyName,
      startDate: todayKey(), endDate: null, createdAt: serverTimestamp(),
    });
  };

  // A shift can cover several crew members at once (some properties are
  // cleaned by more than one person on the same visit) — creates one
  // scheduleRule per selected member, all sharing the same cadence/times,
  // rather than changing the schedule/rule data model to a multi-member row.
  const openShiftForm = () => { setShiftForm({ memberIds: new Set(members[0]?.id?[members[0].id]:[]), mode:"weekly", days:new Set(), monthDay:new Date().getDate(), rangeStart: todayKey(), startTime:"", endTime:"" }); setShiftErr(""); };
  const toggleShiftDay = (i) => { const days = new Set(shiftForm.days); days.has(i)?days.delete(i):days.add(i); setShiftForm(f=>({...f,days})); };
  const toggleShiftMember = (id) => { const ids = new Set(shiftForm.memberIds); ids.has(id)?ids.delete(id):ids.add(id); setShiftForm(f=>({...f,memberIds:ids})); };
  const submitShiftForm = async () => {
    const selected = members.filter(m=>shiftForm.memberIds.has(m.id));
    if (selected.length===0) { setShiftErr("Pick at least one team member."); return; }
    if (!shiftForm.rangeStart) { setShiftErr("Pick a start date."); return; }
    if ((shiftForm.mode==="weekly"||shiftForm.mode==="biweekly") && shiftForm.days.size===0) { setShiftErr("Pick at least one day of the week."); return; }
    if (shiftForm.mode==="monthly") {
      const d = Number(shiftForm.monthDay);
      if (!d || d<1 || d>31) { setShiftErr("Pick a valid day of the month (1–31)."); return; }
    }
    setSavingShift(true); setShiftErr("");
    try {
      for (const member of selected) {
        await createScheduleRule({
          memberId: member.id, memberName: member.name,
          clientId: scheduleClient.id, clientName: scheduleClient.name,
          startTime: shiftForm.startTime||null, endTime: shiftForm.endTime||null, notes: "",
          mode: shiftForm.mode, rangeStart: shiftForm.rangeStart,
          days: shiftForm.mode==="monthly" ? null : Array.from(shiftForm.days),
          monthDay: shiftForm.mode==="monthly" ? Number(shiftForm.monthDay) : null,
        });
      }
      setShiftForm(null);
    } catch (e) {
      setShiftErr("Failed to save: " + e.message);
    }
    setSavingShift(false);
  };

  const addMember = async () => {
    if (!newName.trim()||!newPin.trim()) return;
    setSaving(true);
    await addDoc(collection(db,"teamMembers"),{ name:newName.trim(),pin:newPin.trim(),photoUrl:null,createdAt:Date.now() });
    setNewName(""); setNewPin(""); setSaving(false);
  };

  const uploadMemberPhoto = async (id, file) => {
    setSaving(true);
    const url = await uploadFile(`teamMembers/${id}/photo_${Date.now()}`, file);
    await updateDoc(doc(db,"teamMembers",id),{ photoUrl:url });
    setSaving(false);
  };

  const uploadClientPhoto = async (id, file) => {
    setSaving(true);
    const url = await uploadFile(`clients/${id}/logo_${Date.now()}`, file);
    await updateDoc(doc(db,"clients",id),{ logoUrl:url });
    setSaving(false);
  };

  const addClient = async () => {
    if (!newClient.trim()) return;
    setSaving(true);
    const r = doc(collection(db,"clients"));
    const seedAreas = (newClientType==="residential" ? DEFAULT_AREAS_RESIDENTIAL : DEFAULT_AREAS_COMMERCIAL).map(slugifyArea);
    await setDoc(r,{
      name:newClient.trim(), logoUrl:null, createdAt:Date.now(), type:newClientType,
      supplyList: DEFAULT_SUPPLIES, supplyEmails: adminEmail ? [adminEmail] : [], areas: seedAreas,
    });
    const seedChecklist = newClientType==="residential" ? DEFAULT_CHECKLIST_RESIDENTIAL : DEFAULT_CHECKLIST_COMMERCIAL;
    for (let i=0;i<seedChecklist.length;i++) {
      await addDoc(collection(db,"clients",r.id,"checklist"),{ text:seedChecklist[i],done:false,doneAt:null,doneBy:null,order:i });
    }
    setNewClient(""); setNewClientType("commercial"); setSaving(false);
  };

  return (
    <div style={S.center}>
      <div style={{ ...S.card, maxWidth:"500px", maxHeight:"88vh", overflowY:"auto", ...(isMobile ? { marginBottom:"64px" } : {}) }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"6px" }}>
          <div style={{ fontWeight:"700", fontSize:"18px" }}>⚙️ Admin Setup</div>
          <button style={S.logoutBtn} onClick={onDone}>Done</button>
        </div>
        <div style={{ marginBottom:"14px" }}>
          <a href="mailto:outreach@custodialview.com" style={{ fontSize:"12px", color:C.textMid, textDecoration:"none" }}>
            Need help? outreach@custodialview.com
          </a>
        </div>
        <div style={{ display:"flex", gap:"8px", marginBottom:"20px", flexWrap:"wrap" }}>
          {["members","vendors","workorders","clients","qrcodes","branding","business","reports"].map(t=>(
            <button key={t} onClick={()=>setTab(t)} style={{ padding:"8px 16px", borderRadius:"8px", border:`1.5px solid ${tab===t?C.accent:C.border}`, background:tab===t?"#EEF3FB":C.white, color:tab===t?C.accent:C.textMid, fontWeight:"600", fontSize:"13px", cursor:"pointer" }}>
              {t==="members"?"👤 Team":t==="vendors"?"🔧 Contractors":t==="workorders"?"📋 Work Orders":t==="clients"?"🏢 Clients":t==="qrcodes"?"📷 QR Codes":t==="branding"?"🎨 Branding":t==="business"?"💼 Business Info":"📧 Reports"}
            </button>
          ))}
        </div>

        {tab==="members" && (<>
          <div style={S.sectionTitle}>Team Members</div>
          <div style={{ fontSize:"12px", color:C.textMid, marginTop:"-8px", marginBottom:"10px" }}>
            A contractor's own crew is managed by the contractor — see the 🔧 Contractors tab.
          </div>
          {members.filter(m=>!m.vendorId).map(m=>(
            <div key={m.id} style={{ ...S.memberCard, flexWrap:"wrap", gap:"10px" }}>
              <div style={{ position:"relative", cursor:"pointer" }} onClick={()=>fileRefs.current[m.id]?.click()}>
                <Avatar src={m.photoUrl} name={m.name} size={44} />
                <div style={{ position:"absolute", bottom:-2, right:-2, background:C.accent, borderRadius:"99px", width:"16px", height:"16px", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"9px", color:C.white }}>✏️</div>
                <input type="file" accept="image/*" style={{ display:"none" }} ref={el=>fileRefs.current[m.id]=el}
                  onChange={e=>e.target.files[0]&&uploadMemberPhoto(m.id,e.target.files[0])} />
              </div>
              <div style={{ flex:1, minWidth:"120px" }}>
                <div style={{ fontWeight:"600", fontSize:"14px" }}>{m.name}</div>
                <div style={{ fontSize:"12px", color:C.textMid }}>PIN: {"•".repeat(m.pin?.length||4)}</div>
              </div>
              {/* PIN editor */}
              {editingMemberPin?.id === m.id ? (
                <div style={{ display:"flex", gap:"6px", alignItems:"center" }}>
                  <input
                    style={{ ...S.input, marginBottom:0, width:"90px", padding:"7px 10px" }}
                    type="password" placeholder="New PIN"
                    value={editingMemberPin.value}
                    onChange={e=>setEditingMemberPin(p=>({...p, value:e.target.value}))}
                    onKeyDown={async e=>{
                      if (e.key==="Enter" && editingMemberPin.value.trim()) {
                        await updateDoc(doc(db,"teamMembers",m.id),{ pin: editingMemberPin.value.trim() });
                        setEditingMemberPin(null);
                      }
                    }}
                    maxLength={8} autoFocus
                  />
                  <button style={S.btnSm(C.green)} onClick={async ()=>{
                    if (!editingMemberPin.value.trim()) return;
                    await updateDoc(doc(db,"teamMembers",m.id),{ pin: editingMemberPin.value.trim() });
                    setEditingMemberPin(null);
                  }}>Save</button>
                  <button style={S.btnSm(C.offWhite, C.textMid)} onClick={()=>setEditingMemberPin(null)}>✕</button>
                </div>
              ) : (
                <button
                  style={S.btnSm("#EEF3FB", C.accent)}
                  onClick={()=>setEditingMemberPin({ id:m.id, value:m.pin||"" })}>
                  Change PIN
                </button>
              )}
              <button onClick={()=>deleteDoc(doc(db,"teamMembers",m.id))} style={{ background:"transparent",border:"none",color:C.red,cursor:"pointer",fontSize:"18px" }}>×</button>
            </div>
          ))}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"8px" }}>
            <input style={{ ...S.input, marginBottom:0 }} placeholder="Full name" value={newName} onChange={e=>setNewName(e.target.value)} />
            <input style={{ ...S.input, marginBottom:0 }} type="password" placeholder="PIN" value={newPin} onChange={e=>setNewPin(e.target.value)} maxLength={8} />
          </div>
          <button style={{ ...S.btn, marginTop:"10px" }} onClick={addMember} disabled={saving}>{saving?"Saving…":"+ Add Team Member"}</button>
        </>)}

        {tab==="vendors" && (<>
          <div style={S.sectionTitle}>Contractors</div>
          <div style={{ fontSize:"12px", color:C.textMid, marginTop:"-8px", marginBottom:"10px" }}>
            Each contractor signs in with their own email/password, manages their own crew and schedule, and sees a scorecard and their contract here.
          </div>
          <div style={{ fontSize:"12px", color:C.textMid, background:C.offWhite, border:`1px solid ${C.border}`, borderRadius:"8px", padding:"8px 12px", marginBottom:"14px" }}>
            💳 A contractor's crew bills to your subscription automatically, at half your normal per-seat rate, as soon as they add their first crew member.
          </div>

          {lastInviteCode && (
            <div style={{ background:"#EEF3FB", border:`1.5px solid ${C.accent}`, borderRadius:"10px", padding:"14px", marginBottom:"16px" }}>
              <div style={{ fontWeight:"700", fontSize:"14px", marginBottom:"4px" }}>{lastInviteCode.companyName} added</div>
              <div style={{ fontSize:"12px", color:C.textMid, marginBottom:"8px" }}>Send this invite code to their admin — it's single-use, for {lastInviteCode.clientName}.</div>
              <div style={{ display:"flex", alignItems:"center", gap:"10px", flexWrap:"wrap" }}>
                <div style={{ fontFamily:"monospace", fontSize:"20px", fontWeight:"800", letterSpacing:"2px", background:C.white, padding:"6px 14px", borderRadius:"8px", border:`1px solid ${C.border}` }}>{lastInviteCode.code}</div>
                <button style={S.btnSm(C.offWhite,C.textMid)} onClick={()=>setLastInviteCode(null)}>Dismiss</button>
              </div>
            </div>
          )}

          {vendors.length===0 && <div style={{ color:C.textMid,fontSize:"13px",marginBottom:"14px" }}>No contractors added yet.</div>}
          {vendors.map(v=>{
            const client = clients.find(c=>c.id===v.clientId);
            const contract = vendorContracts.find(c=>c.vendorId===v.id && !c.endDate);
            const crew = members.filter(m=>m.vendorId===v.id);
            return (
              <div key={v.id} style={{ ...S.memberCard, flexDirection:"column", alignItems:"stretch", gap:"8px" }}>
                <div style={{ display:"flex", alignItems:"center", gap:"10px", flexWrap:"wrap" }}>
                  <div style={{ width:44,height:44,borderRadius:"8px",background:C.orange+"22",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"18px",fontWeight:"700",color:C.orange }}>{(v.companyName||'?')[0]}</div>
                  <div style={{ flex:1, minWidth:"120px" }}>
                    <div style={{ fontWeight:"600", fontSize:"14px" }}>{v.companyName}</div>
                    <div style={{ fontSize:"12px", color:C.textMid }}>{client?.name || "⚠️ Client not found"}</div>
                  </div>
                  {contract ? (
                    <>
                      <span style={S.badge(C.green)}>Active since {fmtDateKey(contract.startDate)}</span>
                      <button style={S.btnSm(C.offWhite,C.red)} onClick={()=>endVendorContract(contract)}>End Contract</button>
                    </>
                  ) : (
                    <>
                      <span style={S.badge(C.textMid)}>Contract ended</span>
                      <button style={S.btnSm(C.offWhite,C.accent)} onClick={()=>reinstateVendor(v)}>Reinstate</button>
                    </>
                  )}
                </div>
                <div style={{ fontSize:"12px", color:C.textMid, paddingLeft:"54px" }}>
                  {crew.length===0 ? "No crew added yet" : `Crew: ${crew.map(m=>m.name).join(", ")}`}
                </div>
              </div>
            );
          })}

          <div style={{ ...S.sectionTitle, marginTop:"18px" }}>+ Add Contractor</div>
          {vendorErr && <div style={S.err}>{vendorErr}</div>}
          <input style={{ ...S.input }} placeholder="Company name" value={newVendorName} onChange={e=>setNewVendorName(e.target.value)} />
          <select style={{ ...S.input, background:C.white }} value={newVendorClientId} onChange={e=>setNewVendorClientId(e.target.value)}>
            <option value="">Which client will they service?</option>
            {clients.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <button style={{ ...S.btn, marginTop:"10px" }} onClick={addVendor} disabled={savingVendor}>{savingVendor?"Saving…":"+ Add & Generate Invite Code"}</button>
        </>)}

        {tab==="workorders" && (<>
          <div style={S.sectionTitle}>Work Orders</div>
          <div style={{ fontSize:"12px", color:C.textMid, marginTop:"-8px", marginBottom:"10px" }}>
            Create a work order for a contractor (they assign it to their own crew) or directly for your own staff — either way, whoever's assigned signs it on location from their own Team Member login.
          </div>

          {workOrders.length===0 && <div style={{ color:C.textMid,fontSize:"13px",marginBottom:"14px" }}>No work orders yet.</div>}
          {workOrders.map(wo=>{
            const vendor = wo.vendorId ? vendors.find(v=>v.id===wo.vendorId) : null;
            const client = clients.find(c=>c.id===wo.clientId);
            const signedIds = new Set((wo.signatures||[]).map(s=>s.crewId));
            const crewCount = (wo.assignedCrew||[]).length;
            const fullySigned = crewCount>0 && (wo.assignedCrew||[]).every(c=>signedIds.has(c.id));
            const isOverdue = wo.dueDate && wo.dueDate < todayKey() && !fullySigned;
            const statusLabel = fullySigned ? "Fully signed"
              : crewCount===0 ? (wo.vendorId ? "Awaiting contractor assignment" : "Awaiting assignment")
              : `${signedIds.size}/${crewCount} signed`;
            const assigneeLabel = wo.vendorId
              ? (vendor?.companyName || "⚠️ Contractor not found")
              : crewCount>0 ? `Staff: ${wo.assignedCrew.map(c=>c.name).join(", ")}` : "Staff (unassigned)";
            return (
              <div key={wo.id} style={{ ...S.memberCard, flexDirection:"column", alignItems:"stretch", gap:"8px" }}>
                <div style={{ display:"flex", alignItems:"center", gap:"10px", flexWrap:"wrap" }}>
                  <div style={{ flex:1, minWidth:"120px" }}>
                    <div style={{ fontWeight:"600", fontSize:"14px" }}>{wo.title}</div>
                    <div style={{ fontSize:"12px", color:C.textMid }}>{assigneeLabel}{client && ` · ${client.name}`}</div>
                    {wo.dueDate && <div style={{ fontSize:"11px", color:isOverdue?C.red:C.textMid, fontWeight:isOverdue?"700":"400" }}>{isOverdue?"⚠️ Overdue — ":"Due "}{fmtDateKey(wo.dueDate)}</div>}
                  </div>
                  <span style={S.badge(fullySigned?C.green:isOverdue?C.red:crewCount===0?C.textMid:C.amber)}>{statusLabel}</span>
                  {wo.pdfUrl && <a href={wo.pdfUrl} target="_blank" rel="noreferrer" style={S.btnSm("#EEF3FB", C.accent)}>View PDF</a>}
                  <button onClick={()=>deleteWorkOrder(wo)} style={{ background:"transparent",border:"none",color:C.red,cursor:"pointer",fontSize:"18px" }}>×</button>
                </div>
                {wo.description && <div style={{ fontSize:"12px", color:C.textMid }}>{wo.description}</div>}
                {crewCount>0 && (
                  <div style={{ fontSize:"12px", color:C.textMid }}>
                    {(wo.assignedCrew||[]).map(c=>{
                      const sig = (wo.signatures||[]).find(s=>s.crewId===c.id);
                      return sig ? `✅ ${c.name} — signed by ${sig.clientName || "client"}` : `⏳ ${c.name}`;
                    }).join("  ·  ")}
                  </div>
                )}
                {wo.vendorId && <WorkOrderActions wo={wo} vendorName={vendor?.companyName} clientName={client?.name} prefillEmail={client?.reportEmails?.[0]} />}
              </div>
            );
          })}

          <div style={{ ...S.sectionTitle, marginTop:"18px" }}>+ New Work Order</div>
          {woErr && <div style={S.err}>{woErr}</div>}
          <div style={{ display:"flex", gap:"8px", marginBottom:"10px" }}>
            {["vendor","staff"].map(t=>(
              <button key={t} onClick={()=>setNewWOAssignType(t)}
                style={{ flex:1, padding:"8px", borderRadius:"8px", border:`1.5px solid ${newWOAssignType===t?C.accent:C.border}`, background:newWOAssignType===t?"#EEF3FB":C.white, color:newWOAssignType===t?C.accent:C.textMid, fontWeight:"600", fontSize:"13px", cursor:"pointer" }}>
                {t==="vendor"?"🔧 Contractor":"👤 Staff"}
              </button>
            ))}
          </div>
          {newWOAssignType==="vendor" ? (
            <select style={{ ...S.input, background:C.white }} value={newWOVendorId} onChange={e=>setNewWOVendorId(e.target.value)}>
              <option value="">Which contractor is this for?</option>
              {vendors.map(v=><option key={v.id} value={v.id}>{v.companyName}</option>)}
            </select>
          ) : (<>
            <select style={{ ...S.input, background:C.white }} value={newWOClientId} onChange={e=>setNewWOClientId(e.target.value)}>
              <option value="">Which client/property is this for?</option>
              {clients.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <div style={{ marginBottom:"10px" }}>
              <label style={{ fontSize:"12px", color:C.textMid, display:"block", marginBottom:"6px" }}>Assign to:</label>
              {members.filter(m=>!m.vendorId).length===0 && <div style={{ fontSize:"12px", color:C.textMid }}>No staff added yet — add them in the Team tab.</div>}
              {members.filter(m=>!m.vendorId).map(m=>(
                <label key={m.id} style={{ display:"flex", alignItems:"center", gap:"8px", fontSize:"13px", padding:"4px 0", cursor:"pointer" }}>
                  <input type="checkbox" checked={newWOStaffIds.has(m.id)}
                    onChange={()=>setNewWOStaffIds(prev=>{ const next=new Set(prev); next.has(m.id)?next.delete(m.id):next.add(m.id); return next; })} />
                  {m.name}
                </label>
              ))}
            </div>
          </>)}
          <input style={S.input}
            placeholder={clients.find(c=>c.id===(newWOAssignType==="vendor"?vendors.find(v=>v.id===newWOVendorId)?.clientId:newWOClientId))?.type==="residential" ? "Title (e.g. Deep clean before move-out)" : "Title (e.g. Replace lobby carpet tiles)"}
            value={newWOTitle} onChange={e=>setNewWOTitle(e.target.value)} />
          <textarea style={{ ...S.input, minHeight:"70px", fontFamily:"inherit" }} placeholder="Description (optional)" value={newWODescription} onChange={e=>setNewWODescription(e.target.value)} />
          <div style={{ marginBottom:"10px" }}>
            <label style={{ fontSize:"12px", color:C.textMid, display:"block", marginBottom:"6px" }}>Due date (optional)</label>
            <input type="date" style={{ ...S.input, marginBottom:0 }} value={newWODueDate} onChange={e=>setNewWODueDate(e.target.value)} />
          </div>
          <div style={{ marginBottom:"10px" }}>
            <label style={{ fontSize:"12px", color:C.textMid, display:"block", marginBottom:"6px" }}>Attach a signable PDF (optional)</label>
            <input type="file" accept="application/pdf" onChange={e=>setNewWOFile(e.target.files[0]||null)} />
          </div>
          {newWOAssignType==="vendor" && (
            <div style={{ fontSize:"12px", color:C.textMid, marginBottom:"10px" }}>
              The contractor picks which of their own crew this goes to — you don't assign crew directly.
            </div>
          )}
          <button style={{ ...S.btn, marginTop:"4px" }} onClick={addWorkOrder} disabled={savingWO}>{savingWO?"Saving…":"+ Create Work Order"}</button>
        </>)}

        {tab==="clients" && (<>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"10px" }}>
            <div style={{ ...S.sectionTitle, marginBottom:0 }}>Clients</div>
            <div style={{ display:"flex", gap:"4px" }}>
              {["active","inactive"].map(f=>(
                <button key={f} onClick={()=>setClientListFilter(f)}
                  style={{ padding:"4px 10px", borderRadius:"6px", border:`1px solid ${clientListFilter===f?C.accent:C.border}`, background:clientListFilter===f?"#EEF3FB":C.white, color:clientListFilter===f?C.accent:C.textMid, fontSize:"11px", fontWeight:"600", cursor:"pointer", textTransform:"capitalize" }}>
                  {f} ({clients.filter(c=>f==="active"?!c.inactive:c.inactive).length})
                </button>
              ))}
            </div>
          </div>
          {suppliesStatus === "active" ? (
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", background:"#EEF3FB", border:`1px solid ${C.border}`, borderRadius:"10px", padding:"10px 14px", marginBottom:"14px", fontSize:"12px", color:C.accent, fontWeight:"600" }}>
              📦 Supply Tracker is active
              <button style={S.btnSm(C.offWhite,C.textMid)} onClick={()=>redirectToLicenseEndpoint("createPortalSession",{ returnUrl:window.location.href })}>Manage billing</button>
            </div>
          ) : suppliesStatus === "inactive" && estimatorStatus === "active" ? (
            <div style={{ background:"#FFF9E6", border:`1px solid ${C.amber}55`, borderRadius:"10px", padding:"14px", marginBottom:"14px" }}>
              <div style={{ fontWeight:"700", fontSize:"13px", marginBottom:"4px" }}>📦 Add Supply Tracker — no extra cost</div>
              <div style={{ fontSize:"12px", color:C.textMid, marginBottom:"10px" }}>
                You're already on the $25/mo Estimator plan — the bundle price is the same $25/mo total and adds Supply Tracker too.
              </div>
              {bundleErr && <div style={S.err}>{bundleErr}</div>}
              <button style={S.btnSm()} onClick={addSuppliesViaBundle} disabled={bundleBusy}>{bundleBusy?"Upgrading…":"Add Supply Tracker — still $25/mo"}</button>
            </div>
          ) : suppliesStatus === "inactive" ? (
            <div style={{ background:"#FFF9E6", border:`1px solid ${C.amber}55`, borderRadius:"10px", padding:"14px", marginBottom:"14px" }}>
              <div style={{ fontWeight:"700", fontSize:"13px", marginBottom:"4px" }}>📦 Unlock Supply Tracker — $5/mo</div>
              <div style={{ fontSize:"12px", color:C.textMid, marginBottom:"10px" }}>
                Let your team request supplies with a photo of current stock, emailed straight to your restock contacts.
              </div>
              <button style={S.btnSm()} onClick={subscribeSupplies} disabled={suppliesBusy}>{suppliesBusy?"Redirecting…":"Subscribe to unlock"}</button>
              {estimatorStatus === "inactive" && (
                <div style={{ fontSize:"12px", color:C.textMid, marginTop:"10px" }}>
                  Also want the Job Estimator? <button style={{ ...S.btnSm("#EEF3FB",C.accent), marginLeft:"4px" }} onClick={subscribeBundle} disabled={bundleBusy}>{bundleBusy?"Redirecting…":"Get both for $25/mo"}</button>
                </div>
              )}
            </div>
          ) : null}
          {clients.filter(c=>clientListFilter==="active"?!c.inactive:c.inactive).map(c=>(
            <div key={c.id}>
            <div style={{ ...S.memberCard, flexWrap:"wrap", gap:"10px" }}>
              <div style={{ position:"relative", cursor:"pointer" }} onClick={()=>clientFileRefs.current[c.id]?.click()}>
                {c.logoUrl
                  ? <img src={c.logoUrl} alt={c.name} style={{ width:44,height:44,borderRadius:"8px",objectFit:"contain",background:C.offWhite }} />
                  : <div style={{ width:44,height:44,borderRadius:"8px",background:C.accent+"22",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"18px",fontWeight:"700",color:C.accent }}>{(c.name||'?')[0]}</div>
                }
                <div style={{ position:"absolute", bottom:-2, right:-2, background:C.accent, borderRadius:"99px", width:"16px", height:"16px", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"9px", color:C.white }}>✏️</div>
                <input type="file" accept="image/*" style={{ display:"none" }} ref={el=>clientFileRefs.current[c.id]=el}
                  onChange={e=>e.target.files[0]&&uploadClientPhoto(c.id,e.target.files[0])} />
              </div>
              <div style={{ flex:1, minWidth:"120px" }}>
                {editingClientName?.id === c.id ? (
                  <div style={{ display:"flex", gap:"6px", alignItems:"center", marginBottom:"4px" }}>
                    <input
                      style={{ ...S.input, marginBottom:0, flex:1, padding:"7px 10px" }}
                      type="text" placeholder="Company name"
                      value={editingClientName.value}
                      onChange={e=>setEditingClientName(p=>({...p, value:e.target.value}))}
                      onKeyDown={async e=>{
                        if (e.key==="Enter" && editingClientName.value.trim()) {
                          await updateDoc(doc(db,"clients",c.id),{ name: editingClientName.value.trim() });
                          setEditingClientName(null);
                        }
                      }}
                      autoFocus
                    />
                    <button style={S.btnSm(C.green)} onClick={async ()=>{
                      if (!editingClientName.value.trim()) return;
                      await updateDoc(doc(db,"clients",c.id),{ name: editingClientName.value.trim() });
                      setEditingClientName(null);
                    }}>Save</button>
                    <button style={S.btnSm(C.offWhite, C.textMid)} onClick={()=>setEditingClientName(null)}>✕</button>
                  </div>
                ) : (
                  <div style={{ display:"flex", alignItems:"center", gap:"6px", marginBottom:"2px" }}>
                    <div style={{ fontWeight:"600", fontSize:"14px", color:c.name?C.textDark:C.red }}>
                      {c.name || "⚠️ No name"}
                    </div>
                    <button style={{ background:"transparent", border:"none", color:C.accent, cursor:"pointer", fontSize:"13px", padding:"0 2px", lineHeight:1 }}
                      onClick={()=>setEditingClientName({ id:c.id, value:c.name||"" })}>✏️</button>
                  </div>
                )}
                <div style={{ fontSize:"12px", color:c.pin?C.textMid:C.red }}>
                  {c.pin ? `PIN: ${"•".repeat(c.pin.length)}` : "⚠️ No PIN set — client can't log in"}
                </div>
              </div>
              {/* PIN editor */}
              {editingClientPin?.id === c.id ? (
                <div style={{ display:"flex", gap:"6px", alignItems:"center" }}>
                  <input
                    style={{ ...S.input, marginBottom:0, width:"90px", padding:"7px 10px" }}
                    type="password" placeholder="New PIN"
                    value={editingClientPin.value}
                    onChange={e=>setEditingClientPin(p=>({...p, value:e.target.value}))}
                    onKeyDown={async e=>{
                      if (e.key==="Enter" && editingClientPin.value.trim()) {
                        await updateDoc(doc(db,"clients",c.id),{ pin: editingClientPin.value.trim() });
                        setEditingClientPin(null);
                      }
                    }}
                    maxLength={8} autoFocus
                  />
                  <button style={S.btnSm(C.green)} onClick={async ()=>{
                    if (!editingClientPin.value.trim()) return;
                    await updateDoc(doc(db,"clients",c.id),{ pin: editingClientPin.value.trim() });
                    setEditingClientPin(null);
                  }}>Save</button>
                  <button style={S.btnSm(C.offWhite, C.textMid)} onClick={()=>setEditingClientPin(null)}>✕</button>
                </div>
              ) : (
                <button
                  style={S.btnSm(c.pin?"#EEF3FB":C.accent+"22", c.pin?C.accent:C.accent)}
                  onClick={()=>setEditingClientPin({ id:c.id, value:c.pin||"" })}>
                  {c.pin ? "Change PIN" : "Set PIN"}
                </button>
              )}
              <button
                style={S.btnSm(scheduleClient?.id===c.id?C.accent:"#EEF3FB", scheduleClient?.id===c.id?C.white:C.accent)}
                onClick={()=>{ setScheduleClient(scheduleClient?.id===c.id?null:c); setShiftForm(null); }}>
                📅 Schedule
              </button>
              <button
                style={S.btnSm(reportsClient?.id===c.id?C.accent:"#EEF3FB", reportsClient?.id===c.id?C.white:C.accent)}
                onClick={()=>{ setReportsClient(reportsClient?.id===c.id?null:c); setEditingReportEmails(null); setTestSendMsg(""); }}>
                📧 Reports
              </button>
              {suppliesStatus === "active" && (
                <button
                  style={S.btnSm(suppliesClient?.id===c.id?C.accent:"#EEF3FB", suppliesClient?.id===c.id?C.white:C.accent)}
                  onClick={()=>{
                    const opening = suppliesClient?.id !== c.id;
                    setSuppliesClient(opening ? c : null); setNewSupply(""); setEditingSupplyEmails(null);
                    // Existing clients from before Supply Tracker existed won't
                    // have this yet — pre-load it the first time the panel opens
                    // instead of leaving it blank.
                    if (opening) {
                      const fields = {};
                      if (!(c.supplyList||[]).length) fields.supplyList = DEFAULT_SUPPLIES;
                      if (!(c.supplyEmails||[]).length && adminEmail) fields.supplyEmails = [adminEmail];
                      if (Object.keys(fields).length) updateDoc(doc(db,"clients",c.id), fields);
                    }
                  }}>
                  📦 Supplies
                </button>
              )}
              <button onClick={()=>updateDoc(doc(db,"clients",c.id),{ inactive: !c.inactive })} style={S.btnSm(C.offWhite, C.textMid)}>
                {c.inactive ? "Reactivate" : "Deactivate"}
              </button>
              <button onClick={()=>deleteDoc(doc(db,"clients",c.id))} style={{ background:"transparent",border:"none",color:C.red,cursor:"pointer",fontSize:"18px",padding:"0 4px" }}>×</button>
            </div>

            {reportsClient?.id === c.id && (
              <div style={{ background:C.offWhite, borderRadius:"12px", padding:"14px", marginTop:"-4px", marginBottom:"12px" }}>
                <div style={{ fontWeight:"700", fontSize:"13px", marginBottom:"10px", color:C.navy }}>{c.name} — Service Recap Emails</div>
                <div style={{ fontSize:"11px", color:C.textMid, marginBottom:"6px" }}>How often should this client get a recap?</div>
                <select style={{ ...S.input, marginBottom:"12px", width:"auto" }}
                  value={c.reportCadence || "off"}
                  onChange={e=>updateDoc(doc(db,"clients",c.id),{ reportCadence: e.target.value })}>
                  {["off","daily","weekly","biweekly","monthly","quarterly","annual"].map(v=>(
                    <option key={v} value={v}>{v==="off"?"Off":v[0].toUpperCase()+v.slice(1)}</option>
                  ))}
                </select>

                <div style={{ fontWeight:"700", fontSize:"12px", marginBottom:"6px", color:C.navy }}>Recap emails</div>
                <div style={{ fontSize:"11px", color:C.textMid, marginBottom:"8px" }}>Up to 2 addresses.</div>
                {editingReportEmails?.id === c.id ? (
                  <div>
                    {[0,1].map(i=>(
                      <input key={i} style={S.input} type="email" placeholder={`Email ${i+1}`}
                        value={editingReportEmails.emails[i]||""}
                        onChange={e=>setEditingReportEmails(f=>{ const emails=[...f.emails]; emails[i]=e.target.value; return {...f, emails}; })} />
                    ))}
                    <div style={{ display:"flex", gap:"8px" }}>
                      <button style={S.btnSm()} onClick={async ()=>{
                        const emails = editingReportEmails.emails.map(e=>(e||"").trim()).filter(Boolean).slice(0,2);
                        await updateDoc(doc(db,"clients",c.id),{reportEmails:emails});
                        setEditingReportEmails(null);
                      }}>Save</button>
                      <button style={S.btnSm(C.offWhite,C.textMid)} onClick={()=>setEditingReportEmails(null)}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ marginBottom:"12px" }}>
                    <div style={{ fontSize:"12px", color:(c.reportEmails||[]).length?C.textMid:C.red, marginBottom:"8px" }}>
                      {(c.reportEmails||[]).length ? (c.reportEmails||[]).join(", ") : "⚠️ No emails set — recap can't be sent yet"}
                    </div>
                    <button style={S.btnSm("#EEF3FB",C.accent)}
                      onClick={()=>setEditingReportEmails({ id:c.id, emails:(c.reportEmails||[]).length?[...c.reportEmails]:[adminEmail||"",""] })}>
                      {(c.reportEmails||[]).length ? "Change emails" : "Set emails"}
                    </button>
                  </div>
                )}
                <button style={S.btnSm(C.offWhite,C.textMid)} disabled={sendingTest} onClick={()=>sendTestClientRecap(c.id)}>
                  {sendingTest?"Sending…":"✉️ Send test recap now"}
                </button>
                {testSendMsg && <div style={{ fontSize:"12px", marginTop:"8px", color:testSendMsg.startsWith("✓")?C.green:C.red }}>{testSendMsg}</div>}
              </div>
            )}

            {suppliesClient?.id === c.id && (
              <div style={{ background:C.offWhite, borderRadius:"12px", padding:"14px", marginTop:"-4px", marginBottom:"12px" }}>
                <div style={{ fontWeight:"700", fontSize:"13px", marginBottom:"10px", color:C.navy }}>{c.name} — Tracked Supplies</div>
                {(c.supplyList||[]).length===0 && (
                  <div style={{ fontSize:"12px", color:C.textMid, marginBottom:"10px" }}>No supplies tracked yet. Add one below.</div>
                )}
                {(c.supplyList||[]).map(item=>(
                  <div key={item} style={{ display:"flex", alignItems:"center", gap:"8px", background:C.white, borderRadius:"8px", padding:"8px 12px", marginBottom:"6px", border:`1px solid ${C.border}` }}>
                    <div style={{ flex:1, fontSize:"13px" }}>{item}</div>
                    <button onClick={()=>updateDoc(doc(db,"clients",c.id),{supplyList:arrayRemove(item)})}
                      style={{ background:"transparent",border:"none",color:C.red,cursor:"pointer",fontSize:"16px",padding:"0 4px" }}>×</button>
                  </div>
                ))}
                <div style={{ display:"flex", gap:"8px", marginBottom:"14px" }}>
                  <input style={{ ...S.input, marginBottom:0, flex:1 }} placeholder="Supply name (e.g. Paper towels)"
                    value={newSupply} onChange={e=>setNewSupply(e.target.value)}
                    onKeyDown={e=>{ if(e.key==="Enter"&&newSupply.trim()){ updateDoc(doc(db,"clients",c.id),{supplyList:arrayUnion(newSupply.trim())}); setNewSupply(""); }}} />
                  <button style={S.btnSm()} onClick={()=>{ if(!newSupply.trim()) return; updateDoc(doc(db,"clients",c.id),{supplyList:arrayUnion(newSupply.trim())}); setNewSupply(""); }}>+ Add</button>
                </div>

                <div style={{ fontWeight:"700", fontSize:"12px", marginBottom:"6px", color:C.navy }}>Restock request emails</div>
                <div style={{ fontSize:"11px", color:C.textMid, marginBottom:"8px" }}>Up to 2 addresses — a team member's supply request goes to both.</div>
                {editingSupplyEmails?.id === c.id ? (
                  <div>
                    {[0,1].map(i=>(
                      <input key={i} style={S.input} type="email" placeholder={`Email ${i+1}`}
                        value={editingSupplyEmails.emails[i]||""}
                        onChange={e=>setEditingSupplyEmails(f=>{ const emails=[...f.emails]; emails[i]=e.target.value; return {...f, emails}; })} />
                    ))}
                    <div style={{ display:"flex", gap:"8px" }}>
                      <button style={S.btnSm()} onClick={async ()=>{
                        const emails = editingSupplyEmails.emails.map(e=>(e||"").trim()).filter(Boolean).slice(0,2);
                        await updateDoc(doc(db,"clients",c.id),{supplyEmails:emails});
                        setEditingSupplyEmails(null);
                      }}>Save</button>
                      <button style={S.btnSm(C.offWhite,C.textMid)} onClick={()=>setEditingSupplyEmails(null)}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div style={{ fontSize:"12px", color:(c.supplyEmails||[]).length?C.textMid:C.red, marginBottom:"8px" }}>
                      {(c.supplyEmails||[]).length ? (c.supplyEmails||[]).join(", ") : "⚠️ No emails set — requests can't be sent yet"}
                    </div>
                    <button style={S.btnSm("#EEF3FB",C.accent)}
                      onClick={()=>setEditingSupplyEmails({ id:c.id, emails:(c.supplyEmails||[]).length?[...c.supplyEmails]:[adminEmail||"",""] })}>
                      {(c.supplyEmails||[]).length ? "Change emails" : "Set emails"}
                    </button>
                  </div>
                )}
              </div>
            )}

            {scheduleClient?.id === c.id && (
              <div style={{ background:C.offWhite, borderRadius:"12px", padding:"14px", marginTop:"-4px", marginBottom:"12px" }}>
                <div style={{ fontWeight:"700", fontSize:"13px", marginBottom:"10px", color:C.navy }}>{c.name} — Recurring Shifts</div>
                {scheduleRules.filter(r=>r.clientId===c.id).length===0 && !shiftForm && (
                  <div style={{ fontSize:"12px", color:C.textMid, marginBottom:"10px" }}>No recurring shifts for this client yet.</div>
                )}
                {scheduleRules.filter(r=>r.clientId===c.id).map(r=>(
                  <div key={r.id} style={{ background:C.white, borderRadius:"10px", padding:"10px 12px", marginBottom:"8px", border:`1px solid ${C.border}`, display:"flex", alignItems:"center", gap:"10px" }}>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontWeight:"600", fontSize:"13px" }}>{r.memberName}</div>
                      <div style={{ fontSize:"11px", color:C.textMid }}>
                        {RULE_LABEL[r.mode]} · {ruleDayLabel(r)} · since {fmtDateKey(r.rangeStart)}
                        {(r.startTime || r.endTime) && ` · ${fmtTime12(r.startTime)}${r.endTime?` – ${fmtTime12(r.endTime)}`:""}`}
                      </div>
                    </div>
                    <span style={S.badge(r.active?C.green:C.textMid)}>{r.active?"Active":"Inactive"}</span>
                    <button
                      style={{ ...S.btnSm(r.active?C.offWhite:C.green, r.active?C.textMid:C.white), border:r.active?`1.5px solid ${C.border}`:"none" }}
                      onClick={()=>toggleRuleActive(r)}>
                      {r.active?"Deactivate":"Activate"}
                    </button>
                    <button style={{ background:"transparent", border:"none", color:C.red, cursor:"pointer", fontSize:"16px" }} onClick={()=>deleteRule(r.id)}>✕</button>
                  </div>
                ))}

                {shiftForm ? (
                  <div style={{ background:C.white, borderRadius:"10px", padding:"12px", border:`1px solid ${C.border}` }}>
                    {shiftErr && <div style={S.err}>{shiftErr}</div>}
                    <div style={{ fontSize:"11px", color:C.textMid, marginBottom:"3px" }}>Crew member(s) — pick more than one if this property is cleaned by several people at once</div>
                    <div style={{ display:"flex", gap:"6px", marginBottom:"10px", flexWrap:"wrap" }}>
                      {members.map(m=>(
                        <button key={m.id} onClick={()=>toggleShiftMember(m.id)}
                          style={{ padding:"6px 12px", borderRadius:"8px", border:`1.5px solid ${shiftForm.memberIds.has(m.id)?C.accent:C.border}`, background:shiftForm.memberIds.has(m.id)?C.accent:C.white, color:shiftForm.memberIds.has(m.id)?C.white:C.textMid, fontWeight:"600", fontSize:"12px", cursor:"pointer" }}>
                          {m.name}
                        </button>
                      ))}
                    </div>
                    <div style={{ display:"flex", gap:"6px", marginBottom:"10px", flexWrap:"wrap" }}>
                      {["weekly","biweekly","monthly"].map(m=>(
                        <button key={m} onClick={()=>setShiftForm(f=>({...f,mode:m}))}
                          style={{ padding:"6px 12px", borderRadius:"8px", border:`1.5px solid ${shiftForm.mode===m?C.accent:C.border}`, background:shiftForm.mode===m?"#EEF3FB":C.white, color:shiftForm.mode===m?C.accent:C.textMid, fontWeight:"600", fontSize:"12px", cursor:"pointer" }}>
                          {m==="weekly"?"Weekly":m==="biweekly"?"Every 2 weeks":"Monthly"}
                        </button>
                      ))}
                    </div>
                    {shiftForm.mode==="monthly" ? (
                      <div style={{ marginBottom:"10px" }}>
                        <div style={{ fontSize:"11px", color:C.textMid, marginBottom:"3px" }}>Day of month</div>
                        <input style={{ ...S.input, width:"90px" }} type="number" min="1" max="31" value={shiftForm.monthDay}
                          onChange={e=>setShiftForm(f=>({...f,monthDay:e.target.value}))} />
                      </div>
                    ) : (
                      <div style={{ display:"flex", gap:"6px", marginBottom:"10px", flexWrap:"wrap" }}>
                        {DOW.map((d,i)=>(
                          <button key={d} onClick={()=>toggleShiftDay(i)}
                            style={{ width:"38px", padding:"7px 0", borderRadius:"8px", border:`1.5px solid ${shiftForm.days.has(i)?C.accent:C.border}`, background:shiftForm.days.has(i)?C.accent:C.white, color:shiftForm.days.has(i)?C.white:C.textMid, fontWeight:"600", fontSize:"11px", cursor:"pointer" }}>
                            {d}
                          </button>
                        ))}
                      </div>
                    )}
                    <div style={{ marginBottom:"10px" }}>
                      <div style={{ fontSize:"11px", color:C.textMid, marginBottom:"3px" }}>Starts</div>
                      <input style={{ ...S.input, marginBottom:0, width:"160px" }} type="date" value={shiftForm.rangeStart} onChange={e=>setShiftForm(f=>({...f,rangeStart:e.target.value}))} />
                    </div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"10px", marginBottom:"10px" }}>
                      <div>
                        <div style={{ fontSize:"11px", color:C.textMid, marginBottom:"3px" }}>Start time (optional)</div>
                        <input style={{ ...S.input, marginBottom:0 }} type="time" value={shiftForm.startTime} onChange={e=>setShiftForm(f=>({...f,startTime:e.target.value}))} />
                      </div>
                      <div>
                        <div style={{ fontSize:"11px", color:C.textMid, marginBottom:"3px" }}>End time (optional)</div>
                        <input style={{ ...S.input, marginBottom:0 }} type="time" value={shiftForm.endTime} onChange={e=>setShiftForm(f=>({...f,endTime:e.target.value}))} />
                      </div>
                    </div>
                    <div style={{ display:"flex", gap:"8px" }}>
                      <button style={S.btnSm()} onClick={submitShiftForm} disabled={savingShift}>{savingShift?"Saving…":"Create Shift"}</button>
                      <button style={S.btnSm(C.offWhite,C.textMid)} onClick={()=>setShiftForm(null)}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <button style={S.btnSm()} onClick={openShiftForm} disabled={members.length===0}>+ Add Shift</button>
                )}
                {members.length===0 && <div style={{ fontSize:"11px", color:C.textMid, marginTop:"6px" }}>Add a team member first (Team tab).</div>}
              </div>
            )}
            </div>
          ))}
          <div style={{ display:"flex", gap:"8px", marginBottom:"8px" }}>
            {[["commercial","🏢 Commercial"],["residential","🏠 Residential"]].map(([val,label])=>(
              <button key={val} type="button" onClick={()=>setNewClientType(val)}
                style={{ ...S.btnSm(newClientType===val?C.accent:C.offWhite, newClientType===val?C.white:C.textMid), flex:1 }}>{label}</button>
            ))}
          </div>
          <div style={{ display:"flex", gap:"8px" }}>
            <input style={{ ...S.input, marginBottom:0, flex:1 }} placeholder="Client company name" value={newClient} onChange={e=>setNewClient(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addClient()} />
            <button style={S.btnSm()} onClick={addClient} disabled={saving}>+ Add</button>
          </div>
        </>)}

        {tab==="qrcodes" && (<>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"10px" }}>
            <div style={{ ...S.sectionTitle, marginBottom:0 }}>QR Code Manager</div>
            <div style={{ display:"flex", gap:"4px" }}>
              {["active","inactive"].map(f=>(
                <button key={f} onClick={()=>setQrClientFilter(f)}
                  style={{ padding:"4px 10px", borderRadius:"6px", border:`1px solid ${qrClientFilter===f?C.accent:C.border}`, background:qrClientFilter===f?"#EEF3FB":C.white, color:qrClientFilter===f?C.accent:C.textMid, fontSize:"11px", fontWeight:"600", cursor:"pointer", textTransform:"capitalize" }}>
                  {f} ({clients.filter(c=>f==="active"?!c.inactive:c.inactive).length})
                </button>
              ))}
            </div>
          </div>
          <div style={{ marginBottom:"14px" }}>
            <div style={{ fontSize:"12px", color:C.textMid, marginBottom:"6px" }}>Select client:</div>
            <div style={{ display:"flex", flexDirection:"column", gap:"6px" }}>
              {clients.filter(c=>qrClientFilter==="active"?!c.inactive:c.inactive).map(c=>(
                <div key={c.id} onClick={()=>setQrClient(qrClient?.id===c.id?null:c)}
                  style={{ padding:"10px 14px", borderRadius:"10px", border:`2px solid ${qrClient?.id===c.id?C.accent:C.border}`, background:qrClient?.id===c.id?"#EEF3FB":C.offWhite, cursor:"pointer", display:"flex", alignItems:"center", gap:"10px" }}>
                  <span style={{ flex:1, fontWeight:"600", fontSize:"14px" }}>{c.name}</span>
                  <span style={{ fontSize:"12px", color:C.textMid }}>{(c.areas||[]).length} areas</span>
                  {qrClient?.id===c.id && <span style={{ color:C.accent }}>▼</span>}
                </div>
              ))}
            </div>
          </div>
          {qrClient && (<>
            <div style={{ background:C.offWhite, borderRadius:"12px", padding:"14px", marginBottom:"14px" }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"10px" }}>
                <div style={{ fontWeight:"700", fontSize:"14px", color:C.navy }}>{qrClient.name} — Areas</div>
                {(clients.find(c=>c.id===qrClient.id)?.areas||[]).length > 0 && (
                  <button
                    onClick={()=>printAllQrCodes(
                      qrClient.name,
                      clients.find(c=>c.id===qrClient.id)?.areas||[],
                      area=>`${window.location.origin}/?cid=${qrClient.id}&area=${encodeURIComponent(area)}`
                    )}
                    style={{ fontSize:"11px", fontWeight:"600", color:C.accent, background:"#EEF3FB", border:"none", borderRadius:"8px", padding:"6px 10px", cursor:"pointer" }}>
                    🖨️ Print All
                  </button>
                )}
              </div>
              {(clients.find(c=>c.id===qrClient.id)?.areas||[]).length===0 && (
                <div style={{ fontSize:"13px", color:C.textMid, marginBottom:"10px" }}>No areas yet. Add one below.</div>
              )}
              {(clients.find(c=>c.id===qrClient.id)?.areas||[]).map(area=>{
                const url = `${window.location.origin}/?cid=${qrClient.id}&area=${encodeURIComponent(area)}`;
                return (
                  <div key={area} style={{ background:C.white, borderRadius:"10px", padding:"12px", marginBottom:"10px", border:`1px solid ${C.border}` }}>
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"10px" }}>
                      <div style={{ fontWeight:"600", fontSize:"13px", textTransform:"capitalize" }}>{area.replace(/-/g," ")}</div>
                      <button onClick={()=>updateDoc(doc(db,"clients",qrClient.id),{areas:arrayRemove(area)})}
                        style={{ background:"transparent",border:"none",color:C.red,cursor:"pointer",fontSize:"16px",padding:"0 4px" }}>×</button>
                    </div>
                    <div style={{ display:"flex", alignItems:"center", gap:"12px" }}>
                      <img src={`https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(url)}`}
                        alt={`QR for ${area}`} style={{ width:80, height:80, borderRadius:"6px", border:`1px solid ${C.border}` }} />
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:"10px", color:C.textMid, wordBreak:"break-all", marginBottom:"6px" }}>{url}</div>
                        <div style={{ display:"flex", gap:"14px" }}>
                          <a href={url} target="_blank" rel="noreferrer"
                            style={{ fontSize:"11px", color:C.accent, textDecoration:"none", fontWeight:"600" }}>Test link →</a>
                          <button onClick={()=>printQrCode(qrClient.name, area, url)}
                            style={{ fontSize:"11px", color:C.accent, textDecoration:"none", fontWeight:"600", background:"none", border:"none", padding:0, cursor:"pointer" }}>
                            🖨️ Print
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div style={{ display:"flex", gap:"8px", marginTop:"8px" }}>
                <input style={{ ...S.input, marginBottom:0, flex:1 }}
                  placeholder={clients.find(c=>c.id===qrClient.id)?.type==="residential" ? "Area name (e.g. Master Bedroom)" : "Area name (e.g. 3rd Floor Restroom)"}
                  value={newArea} onChange={e=>setNewArea(e.target.value)}
                  onKeyDown={e=>{ if(e.key==="Enter"&&newArea.trim()){ updateDoc(doc(db,"clients",qrClient.id),{areas:arrayUnion(slugifyArea(newArea))}); setNewArea(""); }}} />
                <button style={S.btnSm()} onClick={()=>{ if(!newArea.trim()) return; updateDoc(doc(db,"clients",qrClient.id),{areas:arrayUnion(slugifyArea(newArea))}); setNewArea(""); }}>+ Add Area</button>
              </div>
            </div>
            <div style={{ fontSize:"11px", color:C.textMid, background:"#FFF9E6", border:`1px solid ${C.amber}55`, borderRadius:"8px", padding:"10px" }}>
              💡 Use Print or Print All to get physical copies, then post each QR code in its corresponding area. Cleaners scan it with their phone to document before &amp; after photos.
            </div>
          </>)}
        </>)}

        {tab==="branding" && (<>
          <div style={S.sectionTitle}>Company Logo</div>
          <div style={{ background:C.offWhite, borderRadius:"12px", padding:"14px", marginBottom:"20px", display:"flex", alignItems:"center", gap:"14px" }}>
            {(logoUrl || DEFAULT_LOGO_URL) && <img src={logoUrl || DEFAULT_LOGO_URL} alt="Company logo" style={{ height:"44px", objectFit:"contain", background:C.white, borderRadius:"6px", padding:"4px 8px" }} />}
            <div style={{ flex:1 }}>
              <div style={{ fontSize:"13px", color:C.textMid }}>Shown across your portal's login screens and header.</div>
            </div>
            <input type="file" accept="image/*" ref={logoFileRef} style={{ display:"none" }} onChange={e=>e.target.files[0] && handleCompanyLogo(e.target.files[0])} />
            <button style={S.btnSm(uploadingLogo?C.textMid:C.accent)} onClick={()=>logoFileRef.current?.click()} disabled={uploadingLogo}>
              {uploadingLogo ? "Uploading…" : logoUrl ? "Replace" : "Upload"}
            </button>
          </div>

          <div style={S.sectionTitle}>Admin Account</div>
          <div style={{ background:C.white, borderRadius:"12px", border:`1px solid ${C.border}`, overflow:"hidden", marginBottom:"20px" }}>
            <div style={{ ...S.settingRow, borderBottom:"none", alignItems:editingAdminAccount?"flex-start":"center" }}>
              <div style={{ flex:1 }}>
                <div style={{ fontWeight:"600", fontSize:"14px" }}>{adminEmail ? adminEmail : "Real admin sign-in"}</div>
                <div style={{ fontSize:"12px", color:C.textMid, marginTop:"2px" }}>
                  {adminEmail
                    ? "Signs in to see all clients and channels — replaces the old shared PIN."
                    : "Set up a real account (email + PIN) instead of the shared PIN — keep it separate from the Admin Setup PIN."}
                </div>
                {editingAdminAccount && (
                  <div style={{ marginTop:"10px" }}>
                    {adminAccountErr && <div style={S.err}>{adminAccountErr}</div>}
                    <input style={S.input} type="email" placeholder="Email" value={editingAdminAccount.email}
                      onChange={e=>setEditingAdminAccount(f=>({...f, email:e.target.value}))} />
                    <input style={{ ...S.input, marginBottom:0 }} type="password" placeholder="PIN (6+ characters)" value={editingAdminAccount.pin}
                      onChange={e=>setEditingAdminAccount(f=>({...f, pin:e.target.value}))}
                      onKeyDown={e=>e.key==="Enter"&&saveAdminAccount()} />
                    <div style={{ display:"flex", gap:"8px", marginTop:"10px" }}>
                      <button style={S.btnSm()} onClick={saveAdminAccount} disabled={savingAdminAccount}>{savingAdminAccount?"Saving…":"Save"}</button>
                      <button style={S.btnSm(C.offWhite,C.textMid)} onClick={()=>{setEditingAdminAccount(null);setAdminAccountErr("");}}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
              {!editingAdminAccount && (
                <button style={S.btnSm("#EEF3FB",C.accent)} onClick={()=>{setEditingAdminAccount({email:adminEmail||"",pin:""});setAdminAccountErr("");}}>
                  {adminEmail ? "Change" : "Set up"}
                </button>
              )}
            </div>
          </div>

          <div style={S.sectionTitle}>PINs</div>
          <div style={{ background:C.white, borderRadius:"12px", border:`1px solid ${C.border}`, overflow:"hidden" }}>
            <div style={S.settingRow}>
              <div>
                <div style={{ fontWeight:"600", fontSize:"14px" }}>Admin Setup PIN</div>
                <div style={{ fontSize:"12px", color:C.textMid, marginTop:"2px" }}>Reaches this Setup screen — keep it separate from the Admin PIN</div>
              </div>
              {editingAdminSetupPin===null ? (
                <button style={S.btnSm("#EEF3FB",C.accent)} onClick={()=>setEditingAdminSetupPin(adminSetupPin||"")}>
                  {adminSetupPin ? "Change" : "Set PIN"}
                </button>
              ) : (
                <div style={{ display:"flex", gap:"8px" }}>
                  <input style={{ ...S.input, marginBottom:0, width:"90px" }} value={editingAdminSetupPin} maxLength={8}
                    onChange={e=>setEditingAdminSetupPin(e.target.value)}
                    onKeyDown={e=>e.key==="Enter"&&saveAdminPin("adminSetupPin",editingAdminSetupPin)} autoFocus />
                  <button style={S.btnSm()} onClick={()=>saveAdminPin("adminSetupPin",editingAdminSetupPin)}>Save</button>
                </div>
              )}
            </div>
            <div style={{ ...S.settingRow, borderBottom:"none" }}>
              <div>
                <div style={{ fontWeight:"600", fontSize:"14px" }}>Estimator PIN</div>
                <div style={{ fontSize:"12px", color:C.textMid, marginTop:"2px" }}>Unlocks the Job Estimator tool — exactly 4 digits (its keypad only takes 4)</div>
              </div>
              {editingEstimatorPin===null ? (
                <button style={S.btnSm("#EEF3FB",C.accent)} onClick={()=>setEditingEstimatorPin(estimatorPin||"")}>
                  {estimatorPin ? "Change" : "Set PIN"}
                </button>
              ) : (
                <div style={{ display:"flex", gap:"8px" }}>
                  <input style={{ ...S.input, marginBottom:0, width:"90px" }} value={editingEstimatorPin}
                    inputMode="numeric" maxLength={4}
                    onChange={e=>setEditingEstimatorPin(e.target.value.replace(/\D/g,"").slice(0,4))}
                    onKeyDown={e=>e.key==="Enter"&&editingEstimatorPin.length===4&&saveAdminPin("estimatorPin",editingEstimatorPin)} autoFocus />
                  <button style={S.btnSm()} onClick={()=>saveAdminPin("estimatorPin",editingEstimatorPin)} disabled={editingEstimatorPin.length!==4}>Save</button>
                </div>
              )}
            </div>
          </div>

          <div style={S.sectionTitle}>Data</div>
          <div style={{ background:C.white, borderRadius:"12px", border:`1px solid ${C.border}`, padding:"14px 16px", display:"flex", alignItems:"center", gap:"14px" }}>
            <div style={{ flex:1 }}>
              <div style={{ fontWeight:"600", fontSize:"14px" }}>Export My Data</div>
              <div style={{ fontSize:"12px", color:C.textMid, marginTop:"2px" }}>Downloads clients, team members, checklists, and schedule as one JSON file.</div>
            </div>
            <button style={S.btnSm(C.offWhite,C.textMid)} onClick={exportData} disabled={exporting}>
              {exporting ? "Exporting…" : "Export"}
            </button>
          </div>
        </>)}

        {tab==="business" && bizForm && (<>
          <div style={S.sectionTitle}>Company Info</div>
          <input style={S.input} placeholder="Company name" value={bizForm.companyName}
            onChange={e=>setBizForm(f=>({...f, companyName:e.target.value}))} />

          <div style={S.sectionTitle}>Contact Info</div>
          <div style={{ fontSize:"12px", color:C.textMid, marginBottom:"10px" }}>
            Shown on Job Estimator proposals sent to your clients.
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"8px", marginBottom:"20px" }}>
            <input style={{ ...S.input, marginBottom:0 }} placeholder="Contact email" value={bizForm.contact.email}
              onChange={e=>setBizForm(f=>({...f, contact:{...f.contact, email:e.target.value}}))} />
            <input style={{ ...S.input, marginBottom:0 }} placeholder="Contact phone" value={bizForm.contact.phone}
              onChange={e=>setBizForm(f=>({...f, contact:{...f.contact, phone:e.target.value}}))} />
            <input style={{ ...S.input, marginBottom:0 }} placeholder="Website" value={bizForm.contact.website}
              onChange={e=>setBizForm(f=>({...f, contact:{...f.contact, website:e.target.value}}))} />
            <input style={{ ...S.input, marginBottom:0 }} placeholder="Service area" value={bizForm.contact.serviceArea}
              onChange={e=>setBizForm(f=>({...f, contact:{...f.contact, serviceArea:e.target.value}}))} />
          </div>

          <div style={S.sectionTitle}>Job Estimator Pricing</div>
          <div style={{ fontSize:"12px", color:C.textMid, marginBottom:"10px" }}>
            Formula inputs used to calculate quotes. Rates/margins are decimals (0.17 = 17%).
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"8px", marginBottom:"14px" }}>
            {[
              ["payrollTax","Payroll tax rate"],
              ["overhead","Overhead rate"],
              ["margin","Target margin"],
              ["tileMarkup","Tile markup (×)"],
              ["carpetMarkup","Carpet markup (×)"],
              ["tileMin","Tile job minimum ($)"],
              ["carpetMin","Carpet job minimum ($)"],
              ["minProfit","Minimum profit ($)"],
              ["pubRestroomFee","Restroom fee ($/mo)"],
            ].map(([key,label])=>(
              <div key={key}>
                <div style={{ fontSize:"11px", color:C.textMid, marginBottom:"3px" }}>{label}</div>
                <input style={{ ...S.input, marginBottom:0 }} type="number" step="0.001"
                  value={bizForm.pricing[key]}
                  onChange={e=>setBizForm(f=>({...f, pricing:{...f.pricing, [key]: e.target.value===""?"":parseFloat(e.target.value)}}))} />
              </div>
            ))}
          </div>

          <div style={{ fontSize:"12px", fontWeight:"600", color:C.textDark, margin:"14px 0 6px" }}>Janitorial rate ($/sqft/mo) by frequency</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"8px", marginBottom:"14px" }}>
            {[["biweekly","Biweekly"],["weekly","Weekly"],["3x","3×/week"],["daily","Daily"]].map(([key,label])=>(
              <div key={key}>
                <div style={{ fontSize:"11px", color:C.textMid, marginBottom:"3px" }}>{label}</div>
                <input style={{ ...S.input, marginBottom:0 }} type="number" step="0.001"
                  value={bizForm.pricing.pubRates[key]}
                  onChange={e=>setBizForm(f=>({...f, pricing:{...f.pricing, pubRates:{...f.pricing.pubRates, [key]: e.target.value===""?"":parseFloat(e.target.value)}}}))} />
              </div>
            ))}
          </div>

          <div style={{ fontSize:"12px", fontWeight:"600", color:C.textDark, margin:"14px 0 6px" }}>Building type multiplier</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"8px", marginBottom:"20px" }}>
            {[["office","Office"],["retail","Retail"],["govt","Government"],["medical","Medical"],["warehouse","Warehouse"]].map(([key,label])=>(
              <div key={key}>
                <div style={{ fontSize:"11px", color:C.textMid, marginBottom:"3px" }}>{label}</div>
                <input style={{ ...S.input, marginBottom:0 }} type="number" step="0.01"
                  value={bizForm.pricing.pubTypeMult[key]}
                  onChange={e=>setBizForm(f=>({...f, pricing:{...f.pricing, pubTypeMult:{...f.pricing.pubTypeMult, [key]: e.target.value===""?"":parseFloat(e.target.value)}}}))} />
              </div>
            ))}
          </div>

          <button style={S.btn} onClick={saveBusinessInfo} disabled={savingBiz}>
            {savingBiz ? "Saving…" : bizSaved ? "✓ Saved!" : "Save Business Info"}
          </button>
        </>)}

        {tab==="reports" && reportsCfg && (<>
          <div style={S.sectionTitle}>Client Recap Emails</div>
          <div style={{ fontSize:"12px", color:C.textMid, marginBottom:"12px" }}>
            Set a cadence and recipient emails per client from the Clients tab
            (📧 Reports button) — nothing sends until a client is explicitly
            opted in.
          </div>

          <div style={S.sectionTitle}>Ops Digest</div>
          <div style={{ fontSize:"12px", color:C.textMid, marginBottom:"10px" }}>
            One internal summary — labor hours, visit counts, and coverage
            gaps across every client.
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:"10px", marginBottom:"12px" }}>
            <div style={{ width:"20px", height:"20px", borderRadius:"5px", border:`1.5px solid ${reportsCfg.opsDigestEnabled?C.accent:C.border}`, background:reportsCfg.opsDigestEnabled?C.accent:C.white, display:"flex", alignItems:"center", justifyContent:"center", color:C.white, fontSize:"13px", cursor:"pointer" }}
              onClick={()=>setReportsCfg(f=>({...f, opsDigestEnabled:!f.opsDigestEnabled}))}>
              {reportsCfg.opsDigestEnabled && "✓"}
            </div>
            <div style={{ fontSize:"13px", fontWeight:"600" }}>Enable Ops Digest</div>
          </div>
          <div style={{ fontSize:"11px", color:C.textMid, marginBottom:"6px" }}>Cadence</div>
          <select style={{ ...S.input, marginBottom:"14px", width:"auto" }}
            value={reportsCfg.opsCadence || "monthly"}
            onChange={e=>setReportsCfg(f=>({...f, opsCadence:e.target.value}))}>
            {["daily","weekly","biweekly","monthly","quarterly","annual"].map(v=>(
              <option key={v} value={v}>{v[0].toUpperCase()+v.slice(1)}</option>
            ))}
          </select>
          <div style={{ fontSize:"11px", color:C.textMid, marginBottom:"6px" }}>
            Recipients (defaults to your admin email if left blank)
          </div>
          {[0,1].map(i=>(
            <input key={i} style={S.input} type="email" placeholder={`Email ${i+1} (optional)`}
              value={(reportsCfg.opsRecipients||[])[i]||""}
              onChange={e=>setReportsCfg(f=>{ const opsRecipients=[...(f.opsRecipients||["",""])]; opsRecipients[i]=e.target.value; return {...f, opsRecipients}; })} />
          ))}
          <div style={{ display:"flex", gap:"8px", marginTop:"6px", flexWrap:"wrap" }}>
            <button style={S.btn} disabled={savingReportsCfg} onClick={async ()=>{
              setSavingReportsCfg(true);
              const opsRecipients = (reportsCfg.opsRecipients||[]).map(e=>(e||"").trim()).filter(Boolean).slice(0,2);
              await setDoc(doc(db,"settings","reports"), { ...reportsCfg, opsRecipients }, { merge:true });
              setSavingReportsCfg(false);
            }}>{savingReportsCfg?"Saving…":"Save Reports Settings"}</button>
            <button style={S.btnSm(C.offWhite,C.textMid)} disabled={sendingTest} onClick={sendTestOpsDigest}>
              {sendingTest?"Sending…":"✉️ Send test digest now"}
            </button>
          </div>
          {testSendMsg && <div style={{ fontSize:"12px", marginTop:"8px", color:testSendMsg.startsWith("✓")?C.green:C.red }}>{testSendMsg}</div>}

          {notifCfg && (<>
            <div style={{ ...S.sectionTitle, marginTop:"24px" }}>Text Alerts</div>
            <div style={{ fontSize:"12px", color:C.textMid, marginBottom:"10px" }}>
              Instant SMS to your phone — not a scheduled report.
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:"10px", marginBottom:"12px" }}>
              <div style={{ width:"20px", height:"20px", borderRadius:"5px", border:`1.5px solid ${notifCfg.smsEnabled?C.accent:C.border}`, background:notifCfg.smsEnabled?C.accent:C.white, display:"flex", alignItems:"center", justifyContent:"center", color:C.white, fontSize:"13px", cursor:"pointer" }}
                onClick={()=>setNotifCfg(f=>({...f, smsEnabled:!f.smsEnabled}))}>
                {notifCfg.smsEnabled && "✓"}
              </div>
              <div style={{ fontSize:"13px", fontWeight:"600" }}>Enable Text Alerts</div>
            </div>
            <div style={{ fontSize:"11px", color:C.textMid, lineHeight:"1.5", marginBottom:"14px", padding:"10px", background:C.offWhite, borderRadius:"6px" }}>
              By enabling text alerts and adding a phone number below, you agree to receive automated SMS messages from CustodialView about team clock-in/clock-out activity and missed scheduled visits. Message frequency varies based on your team's activity. Msg &amp; data rates may apply. Reply STOP to cancel, HELP for help. See our{" "}
              <a href="https://custodialview.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color:C.accent }}>Privacy Policy</a>
              {" "}and{" "}
              <a href="https://custodialview.com/terms" target="_blank" rel="noopener noreferrer" style={{ color:C.accent }}>Terms of Service</a>.
            </div>
            <div style={{ fontSize:"11px", color:C.textMid, marginBottom:"6px" }}>Business timezone (used to tell when a scheduled visit counts as missed)</div>
            <select style={{ ...S.input, background:C.white }} value={notifCfg.timeZone || "America/New_York"}
              onChange={e=>setNotifCfg(f=>({...f, timeZone:e.target.value}))}>
              <option value="America/New_York">Eastern (New York)</option>
              <option value="America/Chicago">Central (Chicago)</option>
              <option value="America/Denver">Mountain (Denver)</option>
              <option value="America/Phoenix">Mountain, no DST (Phoenix)</option>
              <option value="America/Los_Angeles">Pacific (Los Angeles)</option>
              <option value="America/Anchorage">Alaska</option>
              <option value="Pacific/Honolulu">Hawaii</option>
            </select>
            <div style={{ fontSize:"11px", color:C.textMid, marginBottom:"6px" }}>Phone numbers (E.164 format, e.g. +14105551234)</div>
            {[0,1].map(i=>(
              <input key={i} style={S.input} type="tel" placeholder={`Phone ${i+1} (optional)`}
                value={(notifCfg.phoneNumbers||[])[i] ?? "+1"}
                onChange={e=>setNotifCfg(f=>{ const phoneNumbers=[...(f.phoneNumbers||["",""])]; phoneNumbers[i]=e.target.value; return {...f, phoneNumbers}; })} />
            ))}
            <div style={{ display:"flex", flexDirection:"column", gap:"8px", margin:"10px 0 14px" }}>
              {[["clockInOutEnabled","Team clock-in / clock-out"], ["missedVisitEnabled","Missed scheduled visits"], ["workOrderMissedEnabled","Overdue work orders"], ["workOrderCompletedEnabled","Completed work orders"]].map(([key,label])=>(
                <div key={key} style={{ display:"flex", alignItems:"center", gap:"10px" }}>
                  <div style={{ width:"18px", height:"18px", borderRadius:"4px", border:`1.5px solid ${notifCfg[key]?C.accent:C.border}`, background:notifCfg[key]?C.accent:C.white, display:"flex", alignItems:"center", justifyContent:"center", color:C.white, fontSize:"11px", cursor:"pointer" }}
                    onClick={()=>setNotifCfg(f=>({...f, [key]:!f[key]}))}>
                    {notifCfg[key] && "✓"}
                  </div>
                  <div style={{ fontSize:"13px" }}>{label}</div>
                </div>
              ))}
            </div>
            <div style={{ display:"flex", gap:"8px", flexWrap:"wrap" }}>
              <button style={S.btn} disabled={savingNotifCfg} onClick={async ()=>{
                setSavingNotifCfg(true);
                const phoneNumbers = (notifCfg.phoneNumbers||[]).map(p=>(p||"").trim()).filter(p=>p && p.replace(/^\+1\s*/,"").length>0).slice(0,2);
                await setDoc(doc(db,"settings","notifications"), { ...notifCfg, phoneNumbers }, { merge:true });
                setSavingNotifCfg(false);
              }}>{savingNotifCfg?"Saving…":"Save Text Alert Settings"}</button>
              <button style={S.btnSm(C.offWhite,C.textMid)} disabled={sendingTestText} onClick={sendTestText}>
                {sendingTestText?"Sending…":"📱 Send test text"}
              </button>
            </div>
            {testTextMsg && <div style={{ fontSize:"12px", marginTop:"8px", color:testTextMsg.startsWith("✓")?C.green:C.red }}>{testTextMsg}</div>}
          </>)}
        </>)}
      </div>
      <BrandFooterOverlay/>

      {isMobile && (
        <div style={S.bottomNav}>
          {ADMIN_NAV_TABS.map(t=>(
            <div key={t.id} style={S.bottomNavItem(t.id==="setup")}
              onClick={()=>{ if (t.id!=="setup") onGoTo(t.id==="messages" ? "clients" : t.id); }}>
              <div style={{ fontSize:"20px" }}>{t.icon}</div>
              <div style={{ fontSize:"10px", fontWeight:t.id==="setup"?"700":"500" }}>{t.short}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// LOGIN SCREEN
// ─────────────────────────────────────────────────────────────────────────
// PIN-only — no roster of other team members is ever fetched or shown here.
// The PIN is verified server-side (teamMemberLogin Cloud Function), which
// returns only the matching member's own {id,name,photoUrl,vendorId} — this
// is the only way the app learns who signed in, replacing the old pattern
// of downloading every team member's name/photo/PIN to the browser up
// front just so this screen could render a pick-your-name list.
function TeamMemberLogin({ onSuccess, lang="en" }) {
  const [pin,setPin]=useState("");
  const [err,setErr]=useState("");
  const [busy,setBusy]=useState(false);
  const attempt=async ()=>{
    if (!pin || busy) return;
    setBusy(true); setErr("");
    try {
      const member = await teamMemberLogin(pin);
      onSuccess(member);
    } catch {
      setErr(t(lang,"wrongPin")); setPin("");
    }
    setBusy(false);
  };
  return (
    <div>
      <input style={S.input} type="password" placeholder={t(lang,"yourPin")} value={pin}
        onChange={e=>setPin(e.target.value)} onKeyDown={e=>e.key==="Enter"&&attempt()} autoFocus maxLength={8} />
      {err && <div style={S.err}>{err}</div>}
      <button style={S.btn} disabled={busy} onClick={attempt}>{busy?"…":t(lang,"signIn")}</button>
    </div>
  );
}

function LoginScreen({ clients, logoUrl, adminPin, adminSetupPin, adminEmail, onLoginAdmin, onLoginTeam, onLoginClient, onAdminSetup, lang, toggleLang }) {
  const [mode,setMode]=useState("choose");
  const [pin,setPin]=useState("");
  const [email,setEmail]=useState("");
  const [err,setErr]=useState("");
  const [busy,setBusy]=useState(false);
  // Contractor (Vendor Admin) sign-in/join — a real email+password account,
  // not a PIN, so these get their own fields rather than overloading `pin`.
  const [password,setPassword]=useState("");
  const [name,setName]=useState("");
  const [inviteCode,setInviteCode]=useState("");
  const [resetSent,setResetSent]=useState(false);
  const back=()=>{setMode("choose");setPin("");setEmail("");setPassword("");setName("");setInviteCode("");setErr("");setResetSent(false);};

  // Shared by both real-account logins (Admin-via-email and Contractor) —
  // Firebase Auth handles the email itself (its default template, or a
  // custom one if configured in the Console), so there's nothing to send
  // via our own backend. Always shows the same confirmation regardless of
  // whether the address matched an account, so this can't be used to probe
  // which emails have accounts.
  const doResetPassword = async () => {
    if (!email.trim()) { setErr("Enter your email first."); return; }
    setBusy(true); setErr("");
    try { await sendPasswordResetEmail(auth, email.trim()); } catch {}
    setBusy(false); setResetSent(true);
  };

  const tryClient=()=>{
    const matched = (clients||[]).find(c=>c.pin && c.pin===pin);
    if (matched) { onLoginClient(matched); }
    else { setErr("Incorrect PIN."); setPin(""); }
  };
  // Once a buyer has set up a real admin account (AdminSetup -> Branding),
  // adminEmail is set and the shared plaintext adminPin stops being offered
  // entirely — see the "admin" mode render below.
  const tryAdmin = async () => {
    if (adminEmail) {
      if (!email.trim()) { setErr("Enter your email."); return; }
      setBusy(true);
      try {
        await signInWithEmailAndPassword(auth, email.trim(), pin);
        onLoginAdmin();
      } catch {
        setErr("Incorrect email or PIN.");
        setPin("");
      }
      setBusy(false);
      return;
    }
    if (pin===adminPin) onLoginAdmin();
    else if (pin===adminSetupPin) onAdminSetup();
    else { setErr("Incorrect PIN."); setPin(""); }
  };

  // A Contractor (Vendor Admin) is a real Firebase Auth account, not a PIN
  // against this shared anonymous session — App.jsx's top-level
  // onAuthStateChanged + memberships/{uid} lookup takes it from here once
  // sign-in succeeds, so there's no onLoginX callback to call.
  const doVendorSignIn = async () => {
    if (!email.trim() || !password) { setErr("Enter your email and password."); return; }
    setBusy(true); setErr("");
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
    } catch {
      setErr("Incorrect email or password.");
    }
    setBusy(false);
  };

  const doJoinVendor = async () => {
    if (!inviteCode.trim() || !name.trim() || !email.trim() || password.length < 6) {
      setErr("Fill in every field — password needs at least 6 characters.");
      return;
    }
    setBusy(true); setErr("");
    try {
      const code = inviteCode.trim().toUpperCase();
      const inviteSnap = await getDoc(doc(db,"inviteCodes",code));
      if (!inviteSnap.exists() || inviteSnap.data().used) {
        setErr("That invite code is invalid or has already been used.");
        setBusy(false);
        return;
      }
      const { clientId, vendorId } = inviteSnap.data();
      const cred = await createUserWithEmailAndPassword(auth, email.trim(), password);
      const uid = cred.user.uid;
      const batch = writeBatch(db);
      batch.set(doc(db,"memberships",uid), {
        role: "vendor_admin", vendorId, clientId, inviteCode: code,
        name: name.trim(), email: email.trim(), createdAt: serverTimestamp(),
      });
      batch.update(doc(db,"inviteCodes",code), { used: true, usedByUid: uid, usedAt: serverTimestamp() });
      await batch.commit();
    } catch (e) {
      setErr(e.code === "auth/email-already-in-use" ? "That email is already in use." : "Something went wrong — check the invite code and try again.");
    }
    setBusy(false);
  };

  if (mode==="choose") return (
    <div style={S.center}>
      <div style={S.card}>
        <div style={{ display:"flex", justifyContent:"center", marginBottom:"22px" }}>
          <img src={logoUrl || DEFAULT_LOGO_URL} alt={BRAND.name} style={{ height:"70px", objectFit:"contain" }} />
        </div>
        <div style={{ ...S.title, fontFamily:"'Adamina',Georgia,serif", fontWeight:"400", fontSize:"24px", textAlign:"center" }}>Client Portal</div>
        <div style={{ ...S.sub, textAlign:"center" }}>Who are you signing in as?</div>
        <div style={{ display:"flex", flexDirection:"column", gap:"10px" }}>
          <button style={{ ...S.btn, background:C.navy }} onClick={()=>{setMode("admin");setErr("");setPin("");}}>🔑 Admin</button>
          <button style={S.btn} onClick={()=>{setMode("team");setErr("");setPin("");}}>🧹 Team Member</button>
          <button style={{ ...S.btn, background:C.green }} onClick={()=>{setMode("client");setErr("");setPin("");}}>🏢 Client</button>
          <button style={{ ...S.btn, background:C.orange }} onClick={()=>{setMode("vendor");setErr("");}}>🔧 Contractor</button>
          <button style={{ ...S.ghostBtn, marginTop:"0" }} onClick={()=>{
            // First run: no settings/auth doc exists yet, so there's no PIN
            // to check against — go straight to Setup so the first admin can
            // create one (see AdminSetup's Branding tab).
            if (!adminSetupPin) onAdminSetup();
            else { setMode("setup"); setErr(""); setPin(""); }
          }}>⚙️ Admin Setup</button>
        </div>
      </div>
      <BrandFooterOverlay/>
    </div>
  );

  if (mode==="admin") return (
    <div style={S.center}><div style={S.card}>
      <div style={S.title}>Admin Sign In</div>
      <div style={S.sub}>{adminEmail ? "Sign in with your admin email and PIN." : "Enter admin PIN to access all client conversations."}</div>
      {err&&<div style={S.err}>{err}</div>}
      {resetSent && <div style={S.sub}>If that email has an account, a reset link is on its way.</div>}
      {adminEmail && (
        <input style={S.input} type="email" placeholder="Email" value={email} onChange={e=>setEmail(e.target.value)} onKeyDown={e=>e.key==="Enter"&&tryAdmin()} autoFocus />
      )}
      <input style={S.input} type="password" placeholder="PIN" value={pin} onChange={e=>setPin(e.target.value)} onKeyDown={e=>e.key==="Enter"&&tryAdmin()} maxLength={adminEmail?20:8} />
      <button style={{ ...S.btn, background:C.navy }} onClick={tryAdmin} disabled={busy}>{busy?"Signing in…":"Sign In"}</button>
      {adminEmail && (
        <button style={{ ...S.ghostBtn, marginTop:"0" }} onClick={doResetPassword} disabled={busy}>Forgot password?</button>
      )}
      <button style={S.ghostBtn} onClick={back}>← Back</button>
    </div><BrandFooterOverlay/></div>
  );

  if (mode==="setup") return (
    <div style={S.center}><div style={S.card}>
      <div style={S.title}>Admin Setup</div>
      <div style={S.sub}>Enter admin setup PIN to manage team members and clients.</div>
      {err&&<div style={S.err}>{err}</div>}
      <input style={S.input} type="password" placeholder="Setup PIN" value={pin} onChange={e=>setPin(e.target.value)} onKeyDown={e=>e.key==="Enter"&&(pin===adminSetupPin?onAdminSetup():(setErr("Incorrect PIN."),setPin("")))} maxLength={8} />
      <button style={S.btn} onClick={()=>pin===adminSetupPin?onAdminSetup():(setErr("Incorrect PIN."),setPin(""))}>Continue</button>
      <button style={S.ghostBtn} onClick={back}>← Back</button>
    </div><BrandFooterOverlay/></div>
  );

  if (mode==="client") return (
    <div style={S.center}><div style={S.card}>
      <div style={{ display:"flex", justifyContent:"center", marginBottom:"20px" }}>
        <img src={logoUrl || DEFAULT_LOGO_URL} alt={BRAND.name} style={{ height:"60px", objectFit:"contain" }} />
      </div>
      <div style={S.title}>Client Sign In</div>
      <div style={S.sub}>Enter your portal PIN.</div>
      {err&&<div style={S.err}>{err}</div>}
      <input style={S.input} type="password" placeholder="Client PIN" value={pin} onChange={e=>setPin(e.target.value)} onKeyDown={e=>e.key==="Enter"&&tryClient()} maxLength={8} />
      <button style={{ ...S.btn, background:C.green }} onClick={tryClient}>Sign In</button>
      <button style={S.ghostBtn} onClick={back}>← Back</button>
    </div><BrandFooterOverlay/></div>
  );

  if (mode==="vendor") return (
    <div style={S.center}><div style={S.card}>
      <div style={S.title}>Contractor</div>
      <div style={S.sub}>Sign in to your company's account, or join with an invite code.</div>
      <div style={{ display:"flex", flexDirection:"column", gap:"10px" }}>
        <button style={{ ...S.btn, background:C.orange }} onClick={()=>{setMode("vendorSignIn");setErr("");}}>Sign In</button>
        <button style={{ ...S.btn, background:"transparent", border:`1.5px solid ${C.orange}`, color:C.orange }} onClick={()=>{setMode("vendorJoin");setErr("");}}>Join as a Contractor</button>
      </div>
      <button style={{ ...S.ghostBtn, marginTop:"10px" }} onClick={back}>← Back</button>
    </div><BrandFooterOverlay/></div>
  );

  if (mode==="vendorSignIn") return (
    <div style={S.center}><div style={S.card}>
      <div style={S.title}>Contractor Sign In</div>
      <div style={S.sub}>Sign in with your company's email and password.</div>
      {err&&<div style={S.err}>{err}</div>}
      {resetSent && <div style={S.sub}>If that email has an account, a reset link is on its way.</div>}
      <input style={S.input} type="email" placeholder="Email" value={email} onChange={e=>setEmail(e.target.value)} autoFocus />
      <input style={S.input} type="password" placeholder="Password" value={password} onChange={e=>setPassword(e.target.value)} onKeyDown={e=>e.key==="Enter"&&doVendorSignIn()} />
      <button style={{ ...S.btn, background:C.orange, marginBottom:"10px" }} disabled={busy} onClick={doVendorSignIn}>{busy?"Signing in…":"Sign In"}</button>
      <button style={{ ...S.ghostBtn, marginTop:"0" }} onClick={doResetPassword} disabled={busy}>Forgot password?</button>
      <button style={S.ghostBtn} onClick={()=>{setMode("vendor");setErr("");}}>← Back</button>
    </div><BrandFooterOverlay/></div>
  );

  if (mode==="vendorJoin") return (
    <div style={S.center}><div style={S.card}>
      <div style={S.title}>Join as a Contractor</div>
      <div style={S.sub}>You'll need the invite code your contact sent you.</div>
      {err&&<div style={S.err}>{err}</div>}
      <input style={{ ...S.input, textTransform:"uppercase" }} placeholder="Invite code" value={inviteCode} onChange={e=>setInviteCode(e.target.value)} autoFocus />
      <input style={S.input} placeholder="Your name" value={name} onChange={e=>setName(e.target.value)} />
      <input style={S.input} type="email" placeholder="Your email" value={email} onChange={e=>setEmail(e.target.value)} />
      <input style={S.input} type="password" placeholder="Password (min 6 characters)" value={password} onChange={e=>setPassword(e.target.value)} onKeyDown={e=>e.key==="Enter"&&doJoinVendor()} />
      <button style={{ ...S.btn, background:C.orange, marginBottom:"10px" }} disabled={busy} onClick={doJoinVendor}>{busy?"Joining…":"Join"}</button>
      <button style={S.ghostBtn} onClick={()=>{setMode("vendor");setErr("");}}>← Back</button>
    </div><BrandFooterOverlay/></div>
  );

  if (mode==="team") return (
    <div style={S.center}><div style={{ ...S.card, maxHeight:"80vh", overflowY:"auto" }}>
      <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:"-6px" }}>
        <LangToggle lang={lang} onToggle={toggleLang} style={{ border:`1.5px solid ${C.border}`, color:C.textMid }} />
      </div>
      <div style={S.title}>{t(lang,"teamSignInTitle")}</div>
      <div style={S.sub}>{t(lang,"teamSignInSub")}</div>
      <TeamMemberLogin onSuccess={m=>onLoginTeam(m)} lang={lang} />
      <button style={{ ...S.ghostBtn, marginTop:"10px" }} onClick={back}>{t(lang,"back")}</button>
    </div><BrandFooterOverlay/></div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// CLIENT SELECTOR (team members pick a client)
// ─────────────────────────────────────────────────────────────────────────
function ClientSelector({ member, clients, clientsLoaded, onSelect, onSchedule, onSignOut, lang="en", toggleLang }) {
  return (
    <div style={S.center}>
      <div style={S.card}>
        <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:"-6px" }}>
          <LangToggle lang={lang} onToggle={toggleLang} style={{ border:`1.5px solid ${C.border}`, color:C.textMid }} />
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:"12px", marginBottom:"20px" }}>
          <Avatar src={member.photoUrl} name={member.name} size={44} />
          <div>
            <div style={{ fontWeight:"700", fontSize:"16px" }}>{t(lang,"greeting",{name:member.name.split(" ")[0]})}</div>
            <div style={{ fontSize:"12px", color:C.textMid }}>{t(lang,"whichClient")}</div>
          </div>
        </div>
        <div style={{ padding:"12px 14px", borderRadius:"10px", border:`1.5px solid ${C.accent}`, background:"#EEF3FB", cursor:"pointer", marginBottom:"14px", display:"flex", alignItems:"center", gap:"12px" }}
          onClick={onSchedule}>
          <span style={{ fontSize:"20px" }}>📅</span>
          <span style={{ flex:1, fontWeight:"600", color:C.accent }}>{t(lang,"mySchedule")}</span>
          <span style={{ color:C.accent }}>→</span>
        </div>
        {clientsLoaded && clients.length===0 && (
          <div style={{ padding:"14px", borderRadius:"10px", background:C.offWhite, fontSize:"13px", color:C.textMid, textAlign:"center", marginBottom:"14px" }}>
            {t(lang,"noClientsAssigned")}
          </div>
        )}
        {clients.map(c=>(
          <div key={c.id} style={{ padding:"12px 14px", borderRadius:"10px", border:`1.5px solid ${C.border}`, background:C.offWhite, cursor:"pointer", marginBottom:"8px", display:"flex", alignItems:"center", gap:"12px" }}
            onClick={()=>onSelect(c)}>
            {c.logoUrl
              ? <img src={c.logoUrl} alt={c.name} style={{ width:36,height:36,borderRadius:"6px",objectFit:"contain",background:C.white }} />
              : <div style={{ width:36,height:36,borderRadius:"6px",background:C.accent+"22",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:"700",color:C.accent }}>{(c.name||'?')[0]}</div>
            }
            <span style={{ flex:1, fontWeight:"500" }}>{c.name}</span>
            <span style={{ color:C.accent }}>→</span>
          </div>
        ))}
        <button style={S.ghostBtn} onClick={onSignOut}>{t(lang,"signOut")}</button>
      </div>
      <BrandFooterOverlay/>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// ADMIN HOME — dashboard brief: real counts and today's schedule, nothing
// invented. Quick-action tiles route into the rest of AdminPortal (Clients
// opens the existing sidebar/roster, Schedule/Estimator are the existing
// sections, Team routes to Setup since there's no standalone Team view).
// ─────────────────────────────────────────────────────────────────────────
function AdminHomeTab({ clients, teamMembers, onSetup, onGoTo }) {
  const [todaySchedule, setTodaySchedule] = useState([]);
  const [workOrders,    setWorkOrders]    = useState([]);

  useEffect(() => {
    const q = query(collection(db,"schedule"), where("date","==",todayKey()));
    return onSnapshot(q, snap => setTodaySchedule(snap.docs.map(d=>({id:d.id,...d.data()}))));
  }, []);

  useEffect(() => onSnapshot(collection(db,"workOrders"), snap=>setWorkOrders(snap.docs.map(d=>({id:d.id,...d.data()})))), []);

  // Same onboarding card AdminPortal already showed for a brand-new admin
  // with zero clients — now lives here since Home is the default landing
  // section, not duplicated into every other empty state.
  if (clients.length === 0) {
    return (
      <div style={{ display:"flex", alignItems:"center", justifyContent:"center", flex:1, padding:"24px" }}>
        <div style={{ ...S.card, maxWidth:"420px" }}>
          <div style={{ ...S.title, textAlign:"center" }}>👋 Let's get set up</div>
          <div style={{ ...S.sub, textAlign:"center" }}>A few quick steps before your team can start using this.</div>
          <div style={{ display:"flex", flexDirection:"column", gap:"10px" }}>
            {[
              ["👤", "Add your team members", "Setup → Team"],
              ["🏢", "Add your first client", "Setup → Clients"],
              ["🔑", "Set your Admin PIN", "Setup → Branding"],
            ].map(([icon, label, where]) => (
              <div key={label} onClick={()=>onSetup()} style={{ display:"flex", alignItems:"center", gap:"12px", padding:"12px 14px", borderRadius:"10px", border:`1.5px solid ${C.border}`, background:C.offWhite, cursor:"pointer" }}>
                <span style={{ fontSize:"20px" }}>{icon}</span>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:"600", fontSize:"14px" }}>{label}</div>
                  <div style={{ fontSize:"12px", color:C.textMid }}>{where}</div>
                </div>
                <span style={{ color:C.accent }}>→</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const activeClients = clients.filter(c=>!c.inactive);
  const checkedIn = todaySchedule.filter(s=>s.checkedInAt).length;
  const unreadCount = clients.filter(isClientUnread).length;
  const today = todayKey();
  const overdueWO = workOrders.filter(wo => {
    if (!wo.dueDate || wo.dueDate >= today) return false;
    const signedIds = new Set((wo.signatures||[]).map(s=>s.crewId));
    const crewCount = (wo.assignedCrew||[]).length;
    return !(crewCount>0 && wo.assignedCrew.every(c=>signedIds.has(c.id)));
  }).length;

  return (
    <div style={{ padding:"16px" }}>
      {/* AdminPortal's content area is dark navy by convention (see
          AdminScheduleTab's own C.white/#A8C0E8 text), unlike the light
          S.app background Client/Team use — match that here. */}
      <div style={{ fontSize:"20px", fontWeight:"700", color:C.white, marginBottom:"18px" }}>Today</div>

      <div style={S.homeTileGrid}>
        {/* Routes to the same "clients" section as the Clients tile below
            (AdminPortal's per-client view defaults its own activeTab to
            "messages" — see selectClient/activeTab — so this lands right
            on a conversation, not a client-picker). A separate, clearly-
            labeled tile since "it's inside Clients" isn't obvious. */}
        <div style={S.homeTile} onClick={()=>onGoTo("clients")}>
          {unreadCount>0 && <div style={S.homeTileBadge}><div style={S.badge(C.red)}>{unreadCount}</div></div>}
          <div style={{ fontSize:"20px" }}>💬</div>
          <div style={S.homeTileLabel}>Messages</div>
          <div style={S.homeTileSub}>{unreadCount>0 ? `${unreadCount} unread` : "All caught up"}</div>
        </div>
        <div style={S.homeTile} onClick={()=>onGoTo("clients")}>
          {unreadCount>0 && <div style={S.homeTileBadge}><div style={S.badge(C.red)}>{unreadCount}</div></div>}
          <div style={{ fontSize:"20px" }}>🏢</div>
          <div style={S.homeTileLabel}>Clients</div>
          <div style={S.homeTileSub}>{activeClients.length} active</div>
        </div>
        <div style={S.homeTile} onClick={()=>onGoTo("schedule")}>
          <div style={{ fontSize:"20px" }}>📅</div>
          <div style={S.homeTileLabel}>Today's visits</div>
          <div style={S.homeTileSub}>{todaySchedule.length ? `${checkedIn}/${todaySchedule.length} checked in` : "None scheduled"}</div>
        </div>
        <div style={S.homeTile} onClick={()=>onGoTo("team")}>
          {unreadCount>0 && <div style={S.homeTileBadge}><div style={S.badge(C.red)}>{unreadCount}</div></div>}
          <div style={{ fontSize:"20px" }}>👤</div>
          <div style={S.homeTileLabel}>Team</div>
          <div style={S.homeTileSub}>{teamMembers.length} member{teamMembers.length===1?"":"s"}</div>
        </div>
        <div style={S.homeTile} onClick={()=>onGoTo("estimator")}>
          <div style={{ fontSize:"20px" }}>🧮</div>
          <div style={S.homeTileLabel}>Estimator</div>
        </div>
        <div style={S.homeTile} onClick={()=>onGoTo("workorders")}>
          {overdueWO>0 && <div style={S.homeTileBadge}><div style={S.badge(C.red)}>{overdueWO}</div></div>}
          <div style={{ fontSize:"20px" }}>📋</div>
          <div style={S.homeTileLabel}>Work Orders</div>
          <div style={S.homeTileSub}>{overdueWO>0 ? `${overdueWO} overdue` : workOrders.length ? `${workOrders.length} total` : "None yet"}</div>
        </div>
      </div>

      {todaySchedule.length>0 && (<>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", margin:"4px 0 10px" }}>
          <div style={{ ...S.sectionTitle, color:"#A8C0E8" }}>Today's schedule</div>
          <div style={{ fontSize:"12px", color:"#7FB1FF", fontWeight:"600", cursor:"pointer" }} onClick={()=>onGoTo("schedule")}>View all</div>
        </div>
        {todaySchedule.slice(0,6).map(s=>(
          <div key={s.id} style={S.visitCard}>
            <Avatar name={s.memberName} size={32} />
            <div style={{ flex:1 }}>
              <div style={{ fontWeight:"600", fontSize:"13px" }}>{s.memberName} → {s.clientName}</div>
              <div style={{ fontSize:"12px", color:C.textMid }}>{s.startTime?`${fmtTime12(s.startTime)} – ${fmtTime12(s.endTime)}`:"All day"}</div>
            </div>
            <div style={S.statusPill(s.checkedInAt?"done":"scheduled")}>{s.checkedInAt?"Checked in":"Upcoming"}</div>
          </div>
        ))}
      </>)}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// ADMIN TEAM — pick a team member, see which properties (clients) they're
// scheduled at (derived from the `schedule` collection, same data
// AdminScheduleTab/VendorScorecardTab already read), and jump straight
// into that property's Messages tab to reach them about it — messaging in
// this app is always per-client-channel, not a 1:1 DM, so "message them
// about the exact property" means landing in that property's channel,
// same as everywhere else Admin reaches Messages from.
// ─────────────────────────────────────────────────────────────────────────
function AdminTeamTab({ teamMembers, clients, onSelectClient }) {
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberSchedule, setMemberSchedule] = useState([]);

  useEffect(() => {
    if (!selectedMember) { setMemberSchedule([]); return; }
    const q = query(collection(db,"schedule"), where("memberId","==",selectedMember.id));
    return onSnapshot(q, snap => setMemberSchedule(snap.docs.map(d=>d.data())));
  }, [selectedMember]);

  if (selectedMember) {
    const clientIds = [...new Set(memberSchedule.map(s=>s.clientId))];
    const properties = clientIds.map(id=>clients.find(c=>c.id===id)).filter(Boolean);
    return (
      <div>
        <div style={S.backRow} onClick={()=>setSelectedMember(null)}>‹ Team</div>
        <div style={{ display:"flex", alignItems:"center", gap:"12px", marginBottom:"20px" }}>
          <Avatar src={selectedMember.photoUrl} name={selectedMember.name} size={44} />
          <div style={{ fontWeight:"700", fontSize:"16px", color:C.white }}>{selectedMember.name}</div>
        </div>
        <div style={{ ...S.sectionTitle, color:"#A8C0E8" }}>Properties</div>
        {properties.length===0 && <div style={{ color:"#A8C0E8", fontSize:"13px" }}>No scheduled properties yet.</div>}
        {properties.map(c=>(
          <div key={c.id} style={S.visitCard} onClick={()=>onSelectClient(c.id)}>
            {c.logoUrl
              ? <img src={c.logoUrl} alt={c.name} style={{ width:32,height:32,borderRadius:"6px",objectFit:"contain",background:C.offWhite,flexShrink:0 }} />
              : <Avatar name={c.name} size={32} />
            }
            <div style={{ flex:1, fontWeight:"600", fontSize:"14px" }}>{c.name}</div>
            {isClientUnread(c) && <div style={{ width:"8px", height:"8px", borderRadius:"99px", background:C.red, flexShrink:0 }} />}
            <div style={{ color:C.textMid }}>💬</div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div>
      <div style={{ fontSize:"20px", fontWeight:"700", color:C.white, marginBottom:"18px" }}>Team</div>
      {teamMembers.length===0 && <div style={{ color:"#A8C0E8", fontSize:"13px" }}>No team members yet — add them in Setup.</div>}
      {teamMembers.map(m=>(
        <div key={m.id} style={S.visitCard} onClick={()=>setSelectedMember(m)}>
          <Avatar src={m.photoUrl} name={m.name} size={36} />
          <div style={{ flex:1, fontWeight:"600", fontSize:"14px" }}>{m.name}</div>
          <div style={{ color:C.textMid }}>›</div>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// ADMIN WORK ORDERS — overdue/open/completed overview across every
// contractor and staff work order. Status is computed the same way
// AdminSetup's own list already does (every assignedCrew member has a
// matching signature = fully signed); "Overdue" = a past dueDate on one
// that isn't. "+ New" routes into AdminSetup's existing creation form
// (landing on its Work Orders tab directly) rather than duplicating it.
// ─────────────────────────────────────────────────────────────────────────
function AdminWorkOrdersTab({ clients, onSetup }) {
  const [workOrders, setWorkOrders] = useState([]);
  const [vendors,    setVendors]    = useState([]);

  useEffect(() => onSnapshot(collection(db,"workOrders"), snap=>setWorkOrders(snap.docs.map(d=>({id:d.id,...d.data()})))), []);
  useEffect(() => onSnapshot(collection(db,"vendors"),    snap=>setVendors(snap.docs.map(d=>({id:d.id,...d.data()})))), []);

  const today = todayKey();
  const withStatus = workOrders.map(wo => {
    const signedIds = new Set((wo.signatures||[]).map(s=>s.crewId));
    const crewCount = (wo.assignedCrew||[]).length;
    const fullySigned = crewCount>0 && wo.assignedCrew.every(c=>signedIds.has(c.id));
    const overdue = !!(wo.dueDate && wo.dueDate < today && !fullySigned);
    return { ...wo, fullySigned, overdue };
  }).sort((a,b)=>(a.dueDate||"9999").localeCompare(b.dueDate||"9999"));

  const missed    = withStatus.filter(wo=>wo.overdue);
  const upcoming  = withStatus.filter(wo=>!wo.overdue && !wo.fullySigned);
  const completed = withStatus.filter(wo=>wo.fullySigned);

  const Row = ({ wo }) => {
    const client = clients.find(c=>c.id===wo.clientId);
    const vendor = wo.vendorId ? vendors.find(v=>v.id===wo.vendorId) : null;
    const assignee = wo.vendorId
      ? (vendor?.companyName || "Contractor")
      : ((wo.assignedCrew||[]).length ? wo.assignedCrew.map(c=>c.name).join(", ") : "Unassigned");
    return (
      <div style={S.visitCard}>
        <div style={{ flex:1 }}>
          <div style={{ fontWeight:"600", fontSize:"13px" }}>{wo.title}</div>
          <div style={{ fontSize:"12px", color:C.textMid }}>{assignee}{client ? ` · ${client.name}` : ""}</div>
          {wo.dueDate && <div style={{ fontSize:"11px", color:wo.overdue?C.red:C.textMid }}>{wo.overdue?"Due ":"Due "}{fmtDateKey(wo.dueDate)}</div>}
        </div>
        <div style={S.statusPill(wo.fullySigned?"done":wo.overdue?"overdue":"scheduled")}>{wo.fullySigned?"Completed":wo.overdue?"Overdue":"Open"}</div>
      </div>
    );
  };

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"18px" }}>
        <div style={{ fontSize:"20px", fontWeight:"700", color:C.white }}>Work Orders</div>
        <button style={S.btnSm(NAV_ACCENT)} onClick={()=>onSetup("workorders")}>+ New</button>
      </div>

      {missed.length>0 && (<>
        <div style={{ ...S.sectionTitle, color:"#FF9B9B" }}>Overdue</div>
        {missed.map(wo=><Row key={wo.id} wo={wo} />)}
      </>)}

      <div style={{ ...S.sectionTitle, color:"#A8C0E8", marginTop:missed.length>0?"18px":0 }}>Open</div>
      {upcoming.length===0 && <div style={{ color:C.textMid, fontSize:"13px" }}>Nothing outstanding.</div>}
      {upcoming.map(wo=><Row key={wo.id} wo={wo} />)}

      {completed.length>0 && (<>
        <div style={{ ...S.sectionTitle, color:"#A8C0E8", marginTop:"18px" }}>Completed ({completed.length})</div>
        {completed.slice(0,10).map(wo=><Row key={wo.id} wo={wo} />)}
      </>)}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// ADMIN PORTAL — sidebar with all clients, all channels
// ─────────────────────────────────────────────────────────────────────────
function AdminPortal({ clients, teamMembers, onSignOut, onSetup, logoUrl, initialView }) {
  const [activeClientId, setActiveClientId] = useState(clients[0]?.id||null);
  const [activeChannel,  setActiveChannel]  = useState("general");
  const [activeTab,      setActiveTab]      = useState("messages"); // messages | checklist
  const [activeView,     setActiveView]     = useState(initialView || "home"); // home | clients | schedule | estimator
  const [isMobile,       setIsMobile]       = useState(window.innerWidth < 768);
  const [sidebarOpen,    setSidebarOpen]    = useState(window.innerWidth >= 768);
  const [sidebarFilter,  setSidebarFilter]  = useState("active"); // "active" | "inactive"

  useEffect(() => {
    const check = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setSidebarOpen(true);
    };
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const activeClient = clients.find(c=>c.id===activeClientId);

  // Marks this client "read" for Admin's unread badges (sidebar, Home's
  // Clients tile, the Team member→properties view) whenever Admin is
  // actually looking at its Messages tab — not on every client switch,
  // since activeTab can also be "checklist"/"timecard".
  useEffect(() => {
    if (activeView==="clients" && activeTab==="messages" && activeClientId) {
      updateDoc(doc(db,"clients",activeClientId), { adminLastReadAt: serverTimestamp() }).catch(()=>{});
    }
  }, [activeView, activeTab, activeClientId]);

  const selectClient = (id) => {
    setActiveClientId(id);
    setActiveChannel("general");
    setActiveTab("messages");
    setActiveView("clients");
    if (isMobile) setSidebarOpen(false);
  };

  // Top-level sections — same nav-bar pattern (bottom nav on mobile, top
  // S.tabs bar on desktop) the Client/Team portal uses; shared with
  // AdminSetup's own bottom nav (ADMIN_NAV_TABS, module-level). "Setup"
  // navigates straight to the separate AdminSetup screen rather than
  // being a real `activeView`, so it never shows as active here. The
  // client roster lives behind the "Home" dashboard's Clients tile
  // (and the sidebar/hamburger, once inside it) rather than its own
  // nav slot — a bottom-nav icon doesn't fit a roster of many clients.
  const adminNavTabs = ADMIN_NAV_TABS;
  const goNav = (id) => {
    if (id==="setup") { onSetup(); return; }
    setActiveView(id==="messages" ? "clients" : id);
    if (isMobile) setSidebarOpen(false);
  };
  const navIsActive = (id) => id==="setup" ? false : id==="messages" ? activeView==="clients" : activeView===id;
  const unreadCount = clients.filter(isClientUnread).length;

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100vh" }}>
      {/* Header */}
      <div style={S.header}>
        <div style={S.headerLeft}>
          {isMobile && activeView==="clients" && (
            <button
              onClick={() => setSidebarOpen(o => !o)}
              style={{ background:"transparent", border:"none", color:"#A8C0E8", fontSize:"20px", cursor:"pointer", padding:"4px 6px", lineHeight:1 }}
            >{sidebarOpen ? "✕" : "☰"}</button>
          )}
          <img src={logoUrl || DEFAULT_LOGO_URL} alt={BRAND.name} style={{ height:"32px", objectFit:"contain", ...(logoUrl ? {} : { filter:"brightness(0) invert(1)" }) }} />
          {!isMobile && <span style={{ fontSize:"13px", color:"#A8C0E8", fontWeight:"600" }}>Admin View</span>}
        </div>
        <div style={S.headerRight}>
          <button style={S.logoutBtn} onClick={onSignOut}>{isMobile ? "Out" : "Sign Out"}</button>
        </div>
      </div>

      {!isMobile && (
        <div style={S.tabs}>
          {adminNavTabs.map(t=>(
            <div key={t.id} style={S.tab(navIsActive(t.id), NAV_ACCENT)} onClick={()=>goNav(t.id)}>
              {t.label}
              {t.id==="messages" && unreadCount>0 && <span style={{ ...S.badge(C.red), marginLeft:"6px" }}>{unreadCount}</span>}
            </div>
          ))}
        </div>
      )}

      <div style={{ ...S.adminShell, height:"auto", flex:1, position:"relative" }}>
        {/* Mobile backdrop */}
        {isMobile && sidebarOpen && (
          <div
            onClick={() => setSidebarOpen(false)}
            style={{ position:"absolute", inset:0, background:"rgba(0,0,0,0.5)", zIndex:40 }}
          />
        )}

        {/* Sidebar */}
        {(!isMobile || sidebarOpen) && (
        <div style={{
          ...S.sidebar,
          ...(isMobile ? { position:"absolute", top:0, left:0, bottom:0, zIndex:50 } : {})
        }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"12px 12px 4px" }}>
            <div style={{ ...S.sideSection, padding:0 }}>Clients</div>
            <div style={{ display:"flex", gap:"4px" }}>
              {["active","inactive"].map(f=>(
                <button key={f} onClick={()=>setSidebarFilter(f)}
                  style={{ padding:"2px 7px", borderRadius:"5px", border:`1px solid ${sidebarFilter===f?C.accent:"#3A5275"}`, background:sidebarFilter===f?"rgba(46,94,168,0.35)":"transparent", color:sidebarFilter===f?"#FFFFFF":"#A8C0E8", fontSize:"10px", fontWeight:"600", cursor:"pointer", textTransform:"capitalize" }}>
                  {f}
                </button>
              ))}
            </div>
          </div>
          {clients.length===0 && <div style={{ padding:"12px 14px", fontSize:"13px", color:"#4A6A90" }}>No clients yet. Add in Setup.</div>}
          {clients.filter(c=>sidebarFilter==="active"?!c.inactive:c.inactive).map(c=>(
            <div key={c.id} style={S.sideItem(c.id===activeClientId && activeView==="clients")} onClick={()=>selectClient(c.id)}>
              {c.logoUrl
                ? <img src={c.logoUrl} alt={c.name} style={{ width:24,height:24,borderRadius:"4px",objectFit:"contain",background:"rgba(255,255,255,0.1)" }} />
                : <div style={{ width:24,height:24,borderRadius:"4px",background:C.accent+"55",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"11px",fontWeight:"700",color:C.white }}>{(c.name||'?')[0]}</div>
              }
              <span style={{ flex:1, fontSize:"13px", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{c.name}</span>
              {isClientUnread(c) && <div style={{ width:"8px", height:"8px", borderRadius:"99px", background:C.red, flexShrink:0 }} />}
            </div>
          ))}
        </div>
        )}

        {/* Main content */}
        <div style={{ ...S.adminMain, ...(isMobile ? { paddingBottom:"60px" } : {}) }}>
          {activeView === "home" ? (
            <AdminHomeTab clients={clients} teamMembers={teamMembers} onSetup={onSetup} onGoTo={goNav} />
          ) : activeView === "team" ? (
            <AdminTeamTab teamMembers={teamMembers} clients={clients} onSelectClient={selectClient} />
          ) : activeView === "workorders" ? (
            <AdminWorkOrdersTab clients={clients} onSetup={onSetup} />
          ) : activeView === "schedule" ? (
            <AdminScheduleTab clients={clients} teamMembers={teamMembers} />
          ) : activeView === "estimator" ? (
            <EstimatorGate S={S} C={C} onBack={()=>setActiveView("home")} />
          ) : !activeClient && clients.length === 0 ? (
            <div style={{ display:"flex", alignItems:"center", justifyContent:"center", flex:1, padding:"24px" }}>
              <div style={{ ...S.card, maxWidth:"420px" }}>
                <div style={{ ...S.title, textAlign:"center" }}>👋 Let's get set up</div>
                <div style={{ ...S.sub, textAlign:"center" }}>A few quick steps before your team can start using this.</div>
                <div style={{ display:"flex", flexDirection:"column", gap:"10px" }}>
                  {[
                    ["👤", "Add your team members", "Setup → Team"],
                    ["🏢", "Add your first client", "Setup → Clients"],
                    ["🔑", "Set your Admin PIN", "Setup → Branding"],
                  ].map(([icon, label, where]) => (
                    <div key={label} onClick={()=>onSetup()} style={{ display:"flex", alignItems:"center", gap:"12px", padding:"12px 14px", borderRadius:"10px", border:`1.5px solid ${C.border}`, background:C.offWhite, cursor:"pointer" }}>
                      <span style={{ fontSize:"20px" }}>{icon}</span>
                      <div style={{ flex:1 }}>
                        <div style={{ fontWeight:"600", fontSize:"14px" }}>{label}</div>
                        <div style={{ fontSize:"12px", color:C.textMid }}>{where}</div>
                      </div>
                      <span style={{ color:C.accent }}>→</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : !activeClient ? (
            <div style={{ display:"flex", alignItems:"center", justifyContent:"center", flex:1, color:C.textMid, fontSize:"14px" }}>
              Select a client from the sidebar
            </div>
          ) : (<>
            {/* Client name bar — same truncation treatment as the app
                header (see S.headerLeft): the name container needs
                minWidth:0 to actually shrink, and the text itself needs
                nowrap+ellipsis, or a long name wraps across several lines
                and blows out this row's height instead of clipping. */}
            <div style={{ background:C.white, padding:"10px 16px", borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", gap:"10px" }}>
              <div style={{ display:"flex", alignItems:"center", gap:"10px", minWidth:0, overflow:"hidden", flex:1 }}>
                {activeClient.logoUrl && <img src={activeClient.logoUrl} alt={activeClient.name} style={{ height:"28px", objectFit:"contain", flexShrink:0 }} />}
                <div style={{ fontWeight:activeClient.logoUrl?"600":"700", fontSize:activeClient.logoUrl?"14px":"15px", color:C.textDark, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{activeClient.name}</div>
              </div>
              <div style={{ display:"flex", gap:"4px", flexShrink:0 }}>
                {["messages","checklist","timecard"].map(t=>(
                  <button key={t} onClick={()=>setActiveTab(t)} style={{ padding:"6px 12px", borderRadius:"6px", border:`1px solid ${activeTab===t?C.accent:C.border}`, background:activeTab===t?"#EEF3FB":C.white, color:activeTab===t?C.accent:C.textMid, fontSize:"12px", fontWeight:"600", cursor:"pointer", whiteSpace:"nowrap" }}>
                    {t==="messages"?"💬 Messages":t==="checklist"?"✅ Checklist":"🕐 Time Card"}
                  </button>
                ))}
              </div>
            </div>

            {activeTab==="checklist" ? (
              <div style={{ padding:"16px", overflowY:"auto", flex:1 }}>
                <ChecklistTab clientId={activeClientId} role="admin" member={null} />
              </div>
            ) : activeTab==="timecard" ? (
              <div style={{ padding:"16px", overflowY:"auto", flex:1 }}>
                <TimeCardTab clientId={activeClientId} role="admin" member={null} />
              </div>
            ) : (<>
              <div style={S.channelBar}>
                {CHANNELS.map(ch=>(
                  <div key={ch.id} style={S.channelTab(activeChannel===ch.id, ch.color)} onClick={()=>setActiveChannel(ch.id)}>
                    {ch.icon} {ch.label}
                  </div>
                ))}
              </div>
              <div style={{ flex:1, overflowY:"auto", padding:"16px", display:"flex", flexDirection:"column" }}>
                <MessagesPanel
                  key={`${activeClientId}-${activeChannel}`}
                  clientId={activeClientId}
                  channelId={activeChannel}
                  role="admin"
                  member={null}
                  senderName="Admin"
                  senderPhoto={null}
                />
              </div>
            </>)}
          </>)}
        </div>
      </div>

      {isMobile && (
        <div style={S.bottomNav}>
          {adminNavTabs.map(t=>(
            <div key={t.id} style={{ ...S.bottomNavItem(navIsActive(t.id)), position:"relative" }} onClick={()=>goNav(t.id)}>
              {t.id==="messages" && unreadCount>0 && (
                <div style={{ position:"absolute", top:"2px", right:"calc(50% - 20px)" }}><div style={S.badge(C.red)}>{unreadCount}</div></div>
              )}
              <div style={{ fontSize:"20px" }}>{t.icon}</div>
              <div style={{ fontSize:"10px", fontWeight:navIsActive(t.id)?"700":"500" }}>{t.short}</div>
            </div>
          ))}
        </div>
      )}

      {!isMobile && <BrandFooterBar/>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// TEAM / CLIENT PORTAL
// ─────────────────────────────────────────────────────────────────────────
// A contractor's crew member's own Work Orders — only ones assigned to them
// specifically show up here, and this is the one and only place a work
// order actually gets signed (the Contractor Portal's own Work Orders tab
// is read-only status once a work order has been assigned to crew).
function TeamWorkOrdersTab({ member }) {
  const [workOrders, setWorkOrders] = useState([]);
  const [signingId, setSigningId] = useState(null);
  const [clientName, setClientName] = useState("");
  const [saving, setSaving] = useState(false);

  // array-contains on the flat assignedCrewIds works identically for
  // contractor crew and in-house staff — no role branching needed here.
  useEffect(() => {
    const q = query(collection(db,"workOrders"), where("assignedCrewIds","array-contains",member.id));
    return onSnapshot(q, snap => setWorkOrders(snap.docs.map(d=>({id:d.id,...d.data()}))));
  }, [member.id]);

  const startSigning = (woId) => { setSigningId(woId); setClientName(""); };

  // The signature itself is the client's, not the crew member's — they're
  // just the one on-site handing over the device. crewId/crewName still
  // record who was assigned (accountability for getting it signed at all);
  // clientName is who actually signed.
  const saveSignature = async (blob) => {
    setSaving(true);
    const url = await uploadFile(`workOrders/${signingId}/signatures/${member.id}_${Date.now()}.png`, blob);
    await updateDoc(doc(db,"workOrders",signingId), {
      signatures: arrayUnion({ crewId: member.id, crewName: member.name, clientName: clientName.trim(), signatureUrl: url, signedAt: todayKey() }),
    });
    setSaving(false); setSigningId(null); setClientName("");
  };

  if (workOrders.length===0) return <div style={{ color:C.textMid, fontSize:"13px" }}>No work orders assigned to you yet.</div>;

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:"12px" }}>
      {workOrders.map(wo=>{
        const sig = (wo.signatures||[]).find(s=>s.crewId===member.id);
        const isSigning = signingId===wo.id;
        return (
          <div key={wo.id} style={{ ...S.memberCard, flexDirection:"column", alignItems:"stretch", gap:"8px" }}>
            <div style={{ fontWeight:"600", fontSize:"14px" }}>{wo.title}</div>
            {wo.description && <div style={{ fontSize:"13px", color:C.textMid }}>{wo.description}</div>}
            {wo.pdfUrl && <a href={wo.pdfUrl} target="_blank" rel="noreferrer" style={{ fontSize:"13px", color:C.accent }}>📄 View work order PDF</a>}
            {sig ? (
              <div style={{ color:C.green, fontWeight:"600", fontSize:"13px" }}>✅ Signed by {sig.clientName || "client"} · {fmtDateKey(sig.signedAt)}</div>
            ) : isSigning ? (
              <div>
                <div style={{ fontSize:"12px", color:C.textMid, marginBottom:"6px" }}>Hand the device to the client to sign.</div>
                <input
                  style={S.input}
                  placeholder="Client's name"
                  value={clientName}
                  onChange={e=>setClientName(e.target.value)}
                  autoFocus
                />
                {clientName.trim() ? (
                  <SignaturePad saving={saving} onSave={saveSignature} onCancel={()=>setSigningId(null)} />
                ) : (
                  <button style={S.btnSm(C.offWhite,C.textMid)} onClick={()=>setSigningId(null)}>Cancel</button>
                )}
              </div>
            ) : (
              <button style={{ ...S.btnSm(C.orange), alignSelf:"flex-start" }} onClick={()=>startSigning(wo.id)}>Sign now</button>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// CLIENT HOME (dashboard) — client role only. Pulls together checklist
// completion, the soonest upcoming visit, recent completed visits, and
// open requests — all real data already used elsewhere in the app, no
// invented metrics (deliberately no "inspection score": nothing in this
// app's data model represents a real audited quality rating).
// ─────────────────────────────────────────────────────────────────────────
function ClientHomeTab({ clientId, activeClient, onGoTo }) {
  const [checklist,   setChecklist]   = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [schedule,    setSchedule]    = useState([]);
  const [requests,    setRequests]    = useState([]);

  useEffect(() => {
    if (!clientId) return;
    const unsubs = [
      onSnapshot(collection(db,"clients",clientId,"checklist"), snap=>setChecklist(snap.docs.map(d=>d.data()))),
      onSnapshot(collection(db,"clients",clientId,"timeEntries"), snap=>{
        const docs = snap.docs.map(d=>({id:d.id,...d.data()}));
        docs.sort((a,b)=>(b.clockInAt?.seconds||0)-(a.clockInAt?.seconds||0));
        setTimeEntries(docs);
      }),
      onSnapshot(query(collection(db,"schedule"), where("clientId","==",clientId)), snap=>setSchedule(snap.docs.map(d=>d.data()))),
      onSnapshot(collection(db,"clients",clientId,"requests"), snap=>{
        const docs = snap.docs.map(d=>({id:d.id,...d.data()}));
        docs.sort((a,b)=>(b.createdAt?.seconds||0)-(a.createdAt?.seconds||0));
        setRequests(docs);
      }),
    ];
    return () => unsubs.forEach(u=>u());
  }, [clientId]);

  const doneCount = checklist.filter(i=>i.done).length;
  const pct = checklist.length ? Math.round((doneCount/checklist.length)*100) : 0;

  const today = todayKey();
  const upcomingDates = [...new Set(schedule.map(s=>s.date))].filter(d=>d>=today).sort();
  const nextDate = upcomingDates[0];
  const nextCrew = nextDate ? [...new Set(schedule.filter(s=>s.date===nextDate).map(s=>s.memberName).filter(Boolean))] : [];

  const recentVisits  = timeEntries.filter(e=>e.status==="closed").slice(0,3);
  const openRequests  = requests.filter(r=>r.status!=="done").slice(0,2);

  const greeting = (() => { const h=new Date().getHours(); return h<12?"Good morning":h<18?"Good afternoon":"Good evening"; })();

  return (
    <div>
      <div style={{ fontSize:"20px", fontWeight:"700", color:C.textDark, marginBottom:"2px" }}>{greeting}</div>
      <div style={{ fontSize:"13px", color:C.textMid, marginBottom:"18px" }}>{activeClient?.name}</div>

      <div style={S.homeTileGrid}>
        <div style={S.homeTile} onClick={()=>onGoTo("messages")}>
          <div style={{ fontSize:"20px" }}>💬</div>
          <div style={S.homeTileLabel}>Message the team</div>
        </div>
        <div style={S.homeTile} onClick={()=>onGoTo("requests")}>
          <div style={{ fontSize:"20px" }}>🛠️</div>
          <div style={S.homeTileLabel}>Report an issue</div>
          {openRequests.length>0 && <div style={S.homeTileSub}>{openRequests.length} open</div>}
        </div>
        <div style={S.homeTile} onClick={()=>onGoTo("checklist")}>
          <div style={{ fontSize:"20px" }}>✅</div>
          <div style={S.homeTileLabel}>Checklist</div>
          <div style={S.homeTileSub}>{checklist.length ? `${pct}% today` : "No items yet"}</div>
        </div>
        <div style={S.homeTile} onClick={()=>onGoTo("photos")}>
          <div style={{ fontSize:"20px" }}>📸</div>
          <div style={S.homeTileLabel}>Photos</div>
        </div>
      </div>

      {nextDate && (
        <div style={S.homeCard} onClick={()=>onGoTo("visits")}>
          <div style={{ fontSize:"11px", opacity:0.75, textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:"4px" }}>Next visit</div>
          <div style={{ fontSize:"18px", fontWeight:"700" }}>{nextDate===today?"Today":fmtDateKey(nextDate)}</div>
          {nextCrew.length>0 && <div style={{ fontSize:"13px", opacity:0.85, marginTop:"4px" }}>{nextCrew.join(", ")}</div>}
        </div>
      )}

      {recentVisits.length>0 && (<>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", margin:"18px 0 10px" }}>
          <div style={S.sectionTitle}>Recent visits</div>
          <div style={{ fontSize:"12px", color:NAV_ACCENT, fontWeight:"600", cursor:"pointer" }} onClick={()=>onGoTo("visits")}>View all</div>
        </div>
        {recentVisits.map(v=>(
          <div key={v.id} style={S.visitCard}>
            <Avatar name={v.memberName} size={32} />
            <div style={{ flex:1 }}>
              <div style={{ fontWeight:"600", fontSize:"13px" }}>{v.memberName}</div>
              <div style={{ fontSize:"12px", color:C.textMid }}>{fmtDate(v.clockInAt)}</div>
            </div>
          </div>
        ))}
      </>)}

      {openRequests.length>0 && (<>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", margin:"18px 0 10px" }}>
          <div style={S.sectionTitle}>Open requests</div>
          <div style={{ fontSize:"12px", color:NAV_ACCENT, fontWeight:"600", cursor:"pointer" }} onClick={()=>onGoTo("requests")}>View all</div>
        </div>
        {openRequests.map(r=>(
          <div key={r.id} style={S.requestCard}>
            <div style={{ flex:1, fontSize:"13px" }}>{r.text}</div>
            <div style={S.statusPill(r.status)}>{r.status==="in_progress"?"In progress":"Open"}</div>
          </div>
        ))}
      </>)}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// CLIENT VISITS — merges planned `schedule` docs (not yet checked into)
// with completed `timeEntries`, grouped by calendar day, newest first.
// Both collections and their query shapes already exist elsewhere in the
// app (VendorScheduleSection / TimeCardTab) — nothing new is stored here.
// ─────────────────────────────────────────────────────────────────────────
function ClientVisitsTab({ clientId }) {
  const [timeEntries, setTimeEntries] = useState([]);
  const [schedule,    setSchedule]    = useState([]);

  useEffect(() => {
    if (!clientId) return;
    const u1 = onSnapshot(collection(db,"clients",clientId,"timeEntries"), snap=>{
      const docs = snap.docs.map(d=>({id:d.id,...d.data()}));
      docs.sort((a,b)=>(b.clockInAt?.seconds||0)-(a.clockInAt?.seconds||0));
      setTimeEntries(docs);
    });
    const u2 = onSnapshot(query(collection(db,"schedule"), where("clientId","==",clientId)), snap=>
      setSchedule(snap.docs.map(d=>({id:d.id,...d.data()}))));
    return () => { u1(); u2(); };
  }, [clientId]);

  const today = todayKey();
  const completedByDay = {};
  timeEntries.filter(e=>e.status==="closed" && e.clockInAt?.toDate).forEach(e=>{
    const key = dateKey(e.clockInAt.toDate());
    (completedByDay[key] ||= []).push(e);
  });
  const upcomingByDay = {};
  schedule.filter(s=>s.date>=today && !s.checkedInAt).forEach(s=>{
    (upcomingByDay[s.date] ||= []).push(s);
  });

  const days = [...new Set([...Object.keys(completedByDay), ...Object.keys(upcomingByDay)])].sort().reverse();

  if (days.length===0) return <div style={{ textAlign:"center", color:C.textMid, fontSize:"13px", padding:"40px 0" }}>No visits yet.</div>;

  return (
    <div>
      {days.map(day => (
        <div key={day} style={{ marginBottom:"18px" }}>
          <div style={S.sectionTitle}>{day===today?"Today":fmtDateKey(day)}</div>
          {(upcomingByDay[day]||[]).map(s=>(
            <div key={s.id} style={S.visitCard}>
              <Avatar name={s.memberName} size={32} />
              <div style={{ flex:1 }}>
                <div style={{ fontWeight:"600", fontSize:"13px" }}>{s.memberName}</div>
                <div style={{ fontSize:"12px", color:C.textMid }}>{s.startTime?`${fmtTime12(s.startTime)} – ${fmtTime12(s.endTime)}`:"Scheduled"}</div>
              </div>
              <div style={S.statusPill("scheduled")}>Upcoming</div>
            </div>
          ))}
          {(completedByDay[day]||[]).map(e=>(
            <div key={e.id} style={S.visitCard}>
              <Avatar name={e.memberName} size={32} />
              <div style={{ flex:1 }}>
                <div style={{ fontWeight:"600", fontSize:"13px" }}>{e.memberName}</div>
                <div style={{ fontSize:"12px", color:C.textMid }}>{fmtDate(e.clockInAt)}{e.clockOutAt?` · ${fmtDuration(e.clockInAt,e.clockOutAt)}`:""}</div>
              </div>
              <div style={S.statusPill("done")}>Completed</div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// REQUESTS — client-submitted "report an issue" tickets. Shared by Client
// (create + view own) and Team (view + resolve) roles via the `role` prop;
// new subcollection clients/{clientId}/requests, no firestore.rules change
// needed — it falls under the existing clients/{clientId}/{**} wildcard
// that checklist/timeEntries already use. No email notification wired yet
// (would need an endpoint on the shared senate-license-service project —
// a separate, bigger change — so this pass is in-app only).
// ─────────────────────────────────────────────────────────────────────────
function RequestsTab({ clientId, role, member, composeOpen, onComposeOpenChange, clientAreas }) {
  const [requests, setRequests] = useState([]);
  const [text,     setText]     = useState("");
  const [area,     setArea]     = useState("");
  const [saving,   setSaving]   = useState(false);

  useEffect(() => {
    if (!clientId) return;
    return onSnapshot(collection(db,"clients",clientId,"requests"), snap => {
      const docs = snap.docs.map(d=>({id:d.id,...d.data()}));
      docs.sort((a,b)=>(b.createdAt?.seconds||0)-(a.createdAt?.seconds||0));
      setRequests(docs);
    });
  }, [clientId]);

  const submit = async () => {
    if (!text.trim()) return;
    setSaving(true);
    await addDoc(collection(db,"clients",clientId,"requests"), {
      text: text.trim(), area: area||null, status: "open",
      createdAt: serverTimestamp(),
      createdBy: role==="client" ? "Client" : (member?.name || "Team"),
    });
    setText(""); setArea(""); setSaving(false);
    onComposeOpenChange(false);
  };

  const setStatus = (r, status) => updateDoc(doc(db,"clients",clientId,"requests",r.id), {
    status,
    resolvedAt: status==="done" ? serverTimestamp() : null,
    resolvedBy: status==="done" ? (member?.name || "Team") : null,
  });

  const statusLabel = { open:"Open", in_progress:"In progress", done:"Done" };

  return (
    <div>
      {role==="client" && (
        <button style={{ ...S.btn, marginBottom:"18px", background:NAV_ACCENT }} onClick={()=>onComposeOpenChange(!composeOpen)}>
          {composeOpen ? "Cancel" : "+ New request"}
        </button>
      )}
      {role==="client" && composeOpen && (
        <div style={{ background:C.white, border:`1px solid ${C.border}`, borderRadius:"12px", padding:"14px", marginBottom:"18px" }}>
          <textarea style={{ ...S.input, minHeight:"80px", resize:"vertical" }} placeholder="What's going on?" value={text} onChange={e=>setText(e.target.value)} />
          {clientAreas?.length>0 && (
            <select style={S.input} value={area} onChange={e=>setArea(e.target.value)}>
              <option value="">Area (optional)</option>
              {clientAreas.map(a=><option key={a} value={a}>{a.replace(/-/g," ")}</option>)}
            </select>
          )}
          <button style={{ ...S.btn, background:NAV_ACCENT }} disabled={saving || !text.trim()} onClick={submit}>
            {saving ? "Sending…" : "Submit request"}
          </button>
        </div>
      )}

      {requests.length===0 && <div style={{ textAlign:"center", color:C.textMid, fontSize:"13px", padding:"40px 0" }}>No requests yet.</div>}
      {requests.map(r=>(
        <div key={r.id} style={S.requestCard}>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:"13px", fontWeight:"600" }}>{r.text}</div>
            <div style={{ fontSize:"11px", color:C.textMid, marginTop:"2px" }}>
              {r.area ? `${r.area.replace(/-/g," ")} · ` : ""}{fmtDate(r.createdAt)}
            </div>
          </div>
          {role!=="client" ? (
            <select value={r.status} onChange={e=>setStatus(r, e.target.value)} style={{ ...S.statusPill(r.status), paddingRight:"4px", cursor:"pointer" }}>
              <option value="open">Open</option>
              <option value="in_progress">In progress</option>
              <option value="done">Done</option>
            </select>
          ) : (
            <div style={S.statusPill(r.status)}>{statusLabel[r.status]||"Open"}</div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// CLIENT "MORE" (Account hub) — houses the screens that don't fit in the
// 5-slot bottom nav: Checklist, Photos, Settings, plus Sign Out.
// ─────────────────────────────────────────────────────────────────────────
function ClientMoreTab({ activeClient, onGoTo, onSignOut }) {
  const rows = [
    { id:"checklist", label:"✅ Checklist" },
    { id:"photos",    label:"📸 Photos" },
    { id:"settings",  label:"⚙️ Settings" },
  ];
  return (
    <div>
      <div style={{ display:"flex", alignItems:"center", gap:"12px", marginBottom:"20px" }}>
        <Avatar name={activeClient?.name} size={44} />
        <div>
          <div style={{ fontWeight:"700", fontSize:"15px" }}>{activeClient?.name}</div>
          <div style={{ fontSize:"12px", color:C.textMid }}>Client portal</div>
        </div>
      </div>
      <div style={{ background:C.white, borderRadius:"12px", border:`1px solid ${C.border}`, overflow:"hidden", marginBottom:"18px" }}>
        {rows.map((row,idx)=>(
          <div key={row.id} style={{ ...S.settingRow, borderBottom: idx===rows.length-1?"none":`1px solid ${C.border}`, cursor:"pointer" }} onClick={()=>onGoTo(row.id)}>
            <div style={{ fontWeight:"600", fontSize:"14px" }}>{row.label}</div>
            <div style={{ color:C.textMid }}>›</div>
          </div>
        ))}
      </div>
      <button style={S.ghostBtn} onClick={onSignOut}>Sign Out</button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// TEAM HOME — urgent-info brief for whoever's working this client right
// now: any Emergency-channel message from the last 24h (surfaced as a red
// alert card, not buried in a tab), clock-in status, today's checklist
// progress, and open client-submitted Requests. All real data already
// queried elsewhere in this file — nothing invented.
// ─────────────────────────────────────────────────────────────────────────
function TeamHomeTab({ clientId, clientName, myOpenEntry, lang, onGoTo }) {
  const [checklist, setChecklist] = useState([]);
  const [emergencyMsgs, setEmergencyMsgs] = useState([]);
  const [requests, setRequests] = useState([]);

  useEffect(() => {
    if (!clientId) return;
    const unsubs = [
      onSnapshot(collection(db,"clients",clientId,"checklist"), snap=>setChecklist(snap.docs.map(d=>d.data()))),
      onSnapshot(collection(db,"clients",clientId,"channels","emergency","messages"), snap=>{
        const docs = snap.docs.map(d=>({id:d.id,...d.data()}));
        docs.sort((a,b)=>(b.createdAt?.seconds||0)-(a.createdAt?.seconds||0));
        setEmergencyMsgs(docs);
      }),
      onSnapshot(collection(db,"clients",clientId,"requests"), snap=>{
        const docs = snap.docs.map(d=>({id:d.id,...d.data()}));
        docs.sort((a,b)=>(b.createdAt?.seconds||0)-(a.createdAt?.seconds||0));
        setRequests(docs);
      }),
    ];
    return () => unsubs.forEach(u=>u());
  }, [clientId]);

  const doneCount = checklist.filter(i=>i.done).length;
  const pct = checklist.length ? Math.round((doneCount/checklist.length)*100) : 0;
  const openRequests = requests.filter(r=>r.status!=="done");
  const latestEmergency = emergencyMsgs.find(m=>{
    const t2 = m.createdAt?.toDate ? m.createdAt.toDate() : null;
    return t2 && (Date.now()-t2.getTime()) < 24*3600*1000;
  });

  return (
    <div>
      <div style={{ fontSize:"20px", fontWeight:"700", color:C.textDark, marginBottom:"2px" }}>{clientName}</div>
      <div style={{ fontSize:"13px", color:C.textMid, marginBottom:"18px" }}>
        {myOpenEntry ? `Clocked in since ${fmtDate(myOpenEntry.clockInAt)}` : "Not clocked in"}
      </div>

      {latestEmergency && (
        <div style={{ ...S.homeCard, background:`linear-gradient(135deg, ${C.red}, #8A2020)` }} onClick={()=>onGoTo("messages")}>
          <div style={{ fontSize:"11px", opacity:0.85, textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:"4px" }}>🚨 Emergency</div>
          <div style={{ fontSize:"14px", fontWeight:"600" }}>{latestEmergency.text || "New photo attached"}</div>
          <div style={{ fontSize:"12px", opacity:0.85, marginTop:"4px" }}>{latestEmergency.senderName} · {fmtDate(latestEmergency.createdAt)}</div>
        </div>
      )}

      <div style={S.homeTileGrid}>
        <div style={S.homeTile} onClick={()=>onGoTo("timecard")}>
          <div style={{ fontSize:"20px" }}>🕐</div>
          <div style={S.homeTileLabel}>{t(lang,"tabTimeCard").replace(/^\S+\s*/,"")}</div>
          <div style={S.homeTileSub}>{myOpenEntry ? "Clocked in" : "Not clocked in"}</div>
        </div>
        <div style={S.homeTile} onClick={()=>onGoTo("messages")}>
          <div style={{ fontSize:"20px" }}>💬</div>
          <div style={S.homeTileLabel}>{t(lang,"tabMessages").replace(/^\S+\s*/,"")}</div>
        </div>
        <div style={S.homeTile} onClick={()=>onGoTo("checklist")}>
          <div style={{ fontSize:"20px" }}>✅</div>
          <div style={S.homeTileLabel}>{t(lang,"tabChecklist").replace(/^\S+\s*/,"")}</div>
          <div style={S.homeTileSub}>{checklist.length ? `${pct}% today` : "No items yet"}</div>
        </div>
        <div style={S.homeTile} onClick={()=>onGoTo("requests")}>
          <div style={{ fontSize:"20px" }}>🛠️</div>
          <div style={S.homeTileLabel}>Requests</div>
          <div style={S.homeTileSub}>{openRequests.length>0 ? `${openRequests.length} open` : "None open"}</div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// TEAM "MORE" — houses the tabs that don't fit the 5-slot nav: Messages,
// Supplies (if licensed), Work Orders (contractor crew only), Requests.
// Photos moved out to a primary bottom-nav slot (it's the QR fail-safe for
// before/after photos, so it needed one-tap access) — Messages took its
// place here instead. Same hub pattern as ClientMoreTab.
// ─────────────────────────────────────────────────────────────────────────
function TeamMoreTab({ member, suppliesActive, lang, onGoTo }) {
  const rows = [
    { id:"messages", label:t(lang,"tabMessages") },
    ...(suppliesActive ? [{ id:"supplies", label:t(lang,"tabSupplies") }] : []),
    // Shown for every team member now, not just contractor crew — work
    // orders can be assigned directly to in-house staff too. An empty
    // list just shows TeamWorkOrdersTab's existing "no work orders" state.
    { id:"workorders", label:"📋 Work Orders" },
    { id:"requests", label:"🛠️ Requests" },
  ];
  return (
    <div>
      <div style={{ background:C.white, borderRadius:"12px", border:`1px solid ${C.border}`, overflow:"hidden" }}>
        {rows.map((row,idx)=>(
          <div key={row.id} style={{ ...S.settingRow, borderBottom: idx===rows.length-1?"none":`1px solid ${C.border}`, cursor:"pointer" }} onClick={()=>onGoTo(row.id)}>
            <div style={{ fontWeight:"600", fontSize:"14px" }}>{row.label}</div>
            <div style={{ color:C.textMid }}>›</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Portal({ role, member, activeClient, onSwitchClient, onSignOut, onLogoUpdate, logoUrl, initialTab, lang: langProp, toggleLang }) {
  // Clients never get a custom initialTab (only team flows ever set
  // portalInitialTab, to "timecard") — so a client always lands on the
  // new Home dashboard; team keeps its existing default.
  const [tab, setTab] = useState(role==="client" ? "home" : (initialTab || "general"));
  // Mobile vs desktop nav (bottom bar + FAB vs top bar), client role only —
  // same breakpoint/pattern as AdminPortal's isMobile (src/App.jsx ~2470).
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);
  // Which message channel is active inside the single "Messages" tab's
  // pill row — shared by Client and Team now that both use the same
  // consolidated nav (Team's channel labels stay translated via t(), see
  // the "messages" render branch below).
  const [msgChannel, setMsgChannel] = useState("general");
  // Controls the Requests tab's "new request" composer — opened directly
  // from the Home dashboard tile or the mobile FAB.
  const [requestComposeOpen, setRequestComposeOpen] = useState(false);
  // Spanish toggle only ever applies to the Team Member view — Admin and
  // Client always see English regardless of this device's stored setting.
  const lang = role==="team" ? (langProp||"en") : "en";
  // Set only when a team member manually picks an area to document from the
  // Photos tab (QR code missing/damaged) rather than via the auto-triggered
  // post-clock-in flow or a QR scan — see the "firstAreaPhoto" tab below.
  const [manualCaptureArea, setManualCaptureArea] = useState(null);

  const senderName  = role==="team" ? (member?.name||"Team") : (activeClient?.name||"Client");
  const senderPhoto = role==="team" ? (member?.photoUrl||null) : null;

  // Clock in/out lives here (not inside TimeCardTab) so the guided flow
  // that follows a successful clock-in — jump to the first photo area,
  // then route checkout through the Checklist tab — can react to it from
  // outside Time Card's own tab.
  const clientId = activeClient?.id;
  const [timeEntries, setTimeEntries] = useState([]);
  const [clockUploading, setClockUploading] = useState(false);
  const [clockErr, setClockErr] = useState("");

  useEffect(() => {
    if (!clientId || role!=="team") return;
    return onSnapshot(collection(db,"clients",clientId,"timeEntries"), snap => {
      const docs = snap.docs.map(d=>({id:d.id,...d.data()}));
      docs.sort((a,b)=>(b.clockInAt?.seconds||0)-(a.clockInAt?.seconds||0));
      setTimeEntries(docs);
    });
  }, [clientId, role]);

  const myOpenEntry = role==="team" ? timeEntries.find(e=>e.memberId===member?.id && e.status==="open") : null;

  const captureClockPhoto = (onFile) => {
    const input = document.createElement("input");
    input.type = "file"; input.accept = "image/*"; input.capture = "environment";
    input.onchange = async (e) => {
      const file = e.target.files[0]; if (!file) return;
      setClockUploading(true); setClockErr("");
      try { await onFile(file); }
      catch(e) { setClockErr("Upload failed — check connection and try again"); }
      setClockUploading(false);
    };
    input.click();
  };

  const clockIn = () => captureClockPhoto(async (file) => {
    // If no one else is currently clocked in at this client, this clock-in
    // starts a new visit — reset the shared checklist so a client cleaned
    // on a recurring cadence (residential weekly/biweekly, or a second same-
    // day commercial shift) doesn't show days/weeks-old checkmarks as if
    // today's visit were already done. A visit with several crew members
    // clocking in together only resets once, from whoever clocks in first.
    try {
      const openSnap = await getDocs(query(collection(db,"clients",clientId,"timeEntries"), where("status","==","open")));
      if (openSnap.empty) {
        const checklistSnap = await getDocs(collection(db,"clients",clientId,"checklist"));
        if (!checklistSnap.empty) {
          const resetBatch = writeBatch(db);
          checklistSnap.docs.forEach(d => resetBatch.update(d.ref, { done:false, doneAt:null, doneBy:null }));
          await resetBatch.commit();
        }
      }
    } catch { /* non-critical — never block clock-in on this */ }
    const url = await uploadFile(`clients/${clientId}/timecard/${member.id}_${Date.now()}_in`, file);
    await addDoc(collection(db,"clients",clientId,"timeEntries"), {
      memberId: member.id, memberName: member.name,
      clockInAt: serverTimestamp(), clockInPhotoUrl: url,
      clockOutAt: null, clockOutPhotoUrl: null,
      status: "open",
    });
    // Marks today's matching schedule occurrence(s) as checked into — the
    // signal missedVisitCheck (Cloud Function) uses to tell a completed
    // visit from a missed one. Best-effort: a team member can still clock
    // in even for a client with no matching schedule doc today.
    try {
      const todaySnap = await getDocs(query(collection(db,"schedule"),
        where("memberId","==",member.id), where("clientId","==",clientId), where("date","==",todayKey())));
      if (!todaySnap.empty) {
        const batch = writeBatch(db);
        todaySnap.docs.forEach(d => batch.update(d.ref, { checkedInAt: serverTimestamp() }));
        await batch.commit();
      }
    } catch { /* non-critical — never block the actual clock-in on this */ }
    // Guided flow: if this client has photo areas configured, jump
    // straight to the first one's before/after capture instead of
    // leaving the team member to find it themselves.
    if (activeClient?.areas?.length) setTab("firstAreaPhoto");
  });

  const clockOut = () => captureClockPhoto(async (file) => {
    const url = await uploadFile(`clients/${clientId}/timecard/${member.id}_${Date.now()}_out`, file);
    await updateDoc(doc(db,"clients",clientId,"timeEntries",myOpenEntry.id), {
      clockOutAt: serverTimestamp(), clockOutPhotoUrl: url, status: "closed",
    });
  });

  const suppliesStatus = useLicenseStatus("supplies");

  // Both roles now share the same 5-slot nav shape (bottom bar on mobile,
  // top bar on desktop — see the render guards below, both keyed off
  // `isMobile` alone). For Team, the primary slot that used to be Messages
  // is now Photos — its area-picker grid (tab==="photos" below) is the QR
  // fail-safe (scan missing/damaged) for logging before/after photos, and
  // that needed to be reachable in one tap rather than buried under More.
  // Messages moved into "more" instead (still just as reachable — see
  // TeamMoreTab and the Home dashboard tile, both unchanged) since it
  // doesn't need primary-nav real estate the way the QR fail-safe does.
  const portalTabs = role==="team" ? [
    { id:"home",      icon:"🏠", short:stripIcon(t(lang,"tabHome")),      label:t(lang,"tabHome"),      color:NAV_ACCENT },
    { id:"timecard",  icon:"🕐", short:stripIcon(t(lang,"tabTimeCard")),  label:t(lang,"tabTimeCard"),  color:NAV_ACCENT },
    { id:"photos",    icon:"📸", short:stripIcon(t(lang,"tabPhotos")),    label:t(lang,"tabPhotos"),    color:NAV_ACCENT },
    { id:"checklist", icon:"✅", short:stripIcon(t(lang,"tabChecklist")), label:t(lang,"tabChecklist"), color:NAV_ACCENT },
    { id:"more",      icon:"⋯",  short:stripIcon(t(lang,"tabMore")),     label:t(lang,"tabMore"),      color:NAV_ACCENT },
  ] : [
    { id:"home",     icon:"🏠", short:"Home",     label:"🏠 Home",     color:NAV_ACCENT },
    { id:"messages", icon:"💬", short:"Messages", label:"💬 Messages", color:NAV_ACCENT },
    { id:"visits",   icon:"🧹", short:"Visits",   label:"🧹 Visits",   color:NAV_ACCENT },
    { id:"requests", icon:"🛠️", short:"Requests", label:"🛠️ Requests", color:NAV_ACCENT },
    { id:"more",     icon:"⋯",  short:"More",     label:"⋯ More",     color:NAV_ACCENT },
  ];

  return (
    <div style={S.app}>
      <div style={S.header}>
        <div style={S.headerLeft}>
          {activeClient?.logoUrl
            ? <img src={activeClient.logoUrl} alt={activeClient.name} style={{ height:"32px", objectFit:"contain", borderRadius:"4px", background:"rgba(255,255,255,0.08)", padding:"2px 6px", flexShrink:0 }} />
            : <img src={logoUrl || DEFAULT_LOGO_URL} alt={BRAND.name} style={{ height:"32px", objectFit:"contain", flexShrink:0, ...(logoUrl ? {} : { filter:"brightness(0) invert(1)" }) }} />
          }
          {role==="team" && <span style={{ fontSize:"13px", color:"#A8C0E8", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", minWidth:0 }}>{activeClient?.name}</span>}
        </div>
        <div style={S.headerRight}>
          {role==="team" && member && <Avatar src={member.photoUrl} name={member.name} size={28} />}
          <div style={S.pill(role==="team"?C.amber+"33":C.green+"33", role==="team"?"#C8820A":C.green)}>
            {role==="team"?(member?.name?.split(" ")[0]||"Team"):(activeClient?.name||"Client")}
          </div>
          {role==="team" && <LangToggle lang={lang} onToggle={toggleLang} />}
          {role==="team" && <button style={S.logoutBtn} onClick={onSwitchClient}>{t(lang,"switchClient")}</button>}
          <button style={S.logoutBtn} onClick={onSignOut}>{role==="team" ? t(lang,"signOut") : "Sign Out"}</button>
        </div>
      </div>

      {tab!=="firstAreaPhoto" && !isMobile && (
        <div style={S.tabs}>
          {portalTabs.map(t=>(
            <div key={t.id} style={S.tab(tab===t.id, t.color)} onClick={()=>setTab(t.id)}>{t.label}</div>
          ))}
        </div>
      )}

      <div style={{ ...S.body, ...(isMobile ? { paddingBottom:"110px" } : {}) }}>
        {tab==="firstAreaPhoto" && (
          <FirstAreaCapture clientId={clientId} area={manualCaptureArea || activeClient.areas[0]} clientName={activeClient.name} member={member} lang={lang}
            onDone={()=>{ const wasManual = !!manualCaptureArea; setManualCaptureArea(null); setTab(wasManual ? "photos" : "timecard"); }} />
        )}
        {role==="client" && tab==="home" && (
          <ClientHomeTab clientId={clientId} activeClient={activeClient}
            onGoTo={(t)=>{ setTab(t); setRequestComposeOpen(t==="requests"); }} />
        )}
        {role==="team" && tab==="home" && (
          <TeamHomeTab clientId={clientId} clientName={activeClient?.name} myOpenEntry={myOpenEntry} lang={lang}
            onGoTo={(t)=>{ setTab(t); setRequestComposeOpen(false); }} />
        )}
        {tab==="messages" && (<>
          <div style={S.channelPillRow}>
            {CHANNELS.map(ch=>(
              <div key={ch.id} style={S.channelPill(msgChannel===ch.id, ch.color)} onClick={()=>setMsgChannel(ch.id)}>
                {ch.icon} {role==="team" ? t(lang,`channel_${ch.id}`) : ch.label}
              </div>
            ))}
          </div>
          <MessagesPanel clientId={clientId} channelId={msgChannel} role={role} member={member} lang={lang}
            senderName={senderName} senderPhoto={senderPhoto} />
        </>)}
        {role==="client" && tab==="visits" && (
          <ClientVisitsTab clientId={clientId} />
        )}
        {role==="client" && tab==="more" && (
          <ClientMoreTab activeClient={activeClient} onGoTo={setTab} onSignOut={onSignOut} />
        )}
        {role==="team" && tab==="more" && (
          <TeamMoreTab member={member} suppliesActive={suppliesStatus==="active"} lang={lang} onGoTo={setTab} />
        )}
        {tab!=="firstAreaPhoto" && !portalTabs.some(pt=>pt.id===tab) && (
          <div style={S.backRow} onClick={()=>setTab("more")}>‹ {role==="client" ? "Account" : t(lang,"tabMore")}</div>
        )}
        {tab==="requests" && (
          <RequestsTab clientId={clientId} role={role} member={member}
            composeOpen={requestComposeOpen} onComposeOpenChange={setRequestComposeOpen}
            clientAreas={activeClient?.areas} />
        )}
        {tab==="checklist" && (
          <ChecklistTab clientId={activeClient?.id} role={role} member={member} lang={lang}
            myOpenEntry={myOpenEntry} clockOut={clockOut} clockUploading={clockUploading} clockErr={clockErr} />
        )}
        {tab==="photos" && (<>
          {role==="team" && (activeClient?.areas||[]).length>0 && (
            <div style={{ background:C.offWhite, border:`1px solid ${C.border}`, borderRadius:"12px", padding:"14px", marginBottom:"16px" }}>
              <div style={{ fontSize:"12px", fontWeight:"700", color:C.navy, marginBottom:"2px" }}>{t(lang,"documentArea")}</div>
              <div style={{ fontSize:"12px", color:C.textMid, marginBottom:"10px" }}>{t(lang,"documentAreaSub")}</div>
              <div style={{ display:"flex", flexWrap:"wrap", gap:"8px" }}>
                {activeClient.areas.map(a=>(
                  <button key={a} onClick={()=>{ setManualCaptureArea(a); setTab("firstAreaPhoto"); }}
                    style={{ ...S.btnSm("#EEF3FB", C.accent), textTransform:"capitalize" }}>
                    {a.replace(/-/g," ")}
                  </button>
                ))}
              </div>
            </div>
          )}
          <PhotoGalleryTab clientId={activeClient?.id} lang={lang} />
        </>)}
        {tab==="timecard" && role==="team" && (
          <TimeCardTab clientId={activeClient?.id} role={role} member={member} lang={lang}
            myOpenEntry={myOpenEntry} clockIn={clockIn} clockOut={clockOut}
            clockUploading={clockUploading} clockErr={clockErr}
            onGoToCheckout={()=>setTab("checklist")} />
        )}
        {tab==="settings" && role==="client" && (
          <SettingsTab clientId={activeClient?.id} clientData={activeClient} onLogoUpdate={onLogoUpdate} />
        )}
        {tab==="supplies" && role==="team" && (
          <SupplyRequestTab clientId={activeClient?.id} clientName={activeClient?.name}
            supplyList={activeClient?.supplyList} supplyEmails={activeClient?.supplyEmails}
            member={member} lang={lang} />
        )}
        {tab==="workorders" && role==="team" && (
          <TeamWorkOrdersTab member={member} />
        )}
      </div>

      {isMobile && tab!=="firstAreaPhoto" && (<>
        {role==="client" && (
          <button style={S.fab} onClick={()=>{ setTab("requests"); setRequestComposeOpen(true); }}>+ Report Issue</button>
        )}
        <div style={S.bottomNav}>
          {portalTabs.map(t=>(
            <div key={t.id} style={S.bottomNavItem(tab===t.id)} onClick={()=>{ setTab(t.id); setRequestComposeOpen(false); }}>
              <div style={{ fontSize:"20px" }}>{t.icon}</div>
              <div style={{ fontSize:"10px", fontWeight:tab===t.id?"700":"500" }}>{t.short}</div>
            </div>
          ))}
        </div>
      </>)}

      {!isMobile && <BrandFooterBar/>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// SUPPLY TRACKER (paid add-on) — team member picks which tracked supplies
// are low, optionally attaches a photo of current stock, and submits. The
// request emails the client's configured restock contacts directly (see
// sendSupplyRequestEmail in senate-license-service) — there's no in-app
// inbox for these, matching the user's ask ("an email of two people that
// should receive the request").
// ─────────────────────────────────────────────────────────────────────────
function SupplyRequestTab({ clientId, clientName, supplyList, supplyEmails, member, lang="en" }) {
  const [checked, setChecked] = useState(new Set());
  const [notes,   setNotes]   = useState("");
  const [photo,   setPhoto]   = useState(null); // { file, previewUrl }
  const [sending, setSending] = useState(false);
  const [err,     setErr]     = useState("");
  const [sent,    setSent]    = useState(false);

  const toggle = (item) => {
    const next = new Set(checked);
    next.has(item) ? next.delete(item) : next.add(item);
    setChecked(next);
  };

  const capturePhoto = () => {
    const input = document.createElement("input");
    input.type = "file"; input.accept = "image/*"; input.capture = "environment";
    input.onchange = (e) => {
      const file = e.target.files[0]; if (!file) return;
      setPhoto({ file, previewUrl: URL.createObjectURL(file) });
    };
    input.click();
  };

  const submit = async () => {
    if (checked.size === 0) { setErr(t(lang,"selectAtLeastOneSupply")); return; }
    const emails = (supplyEmails||[]).filter(Boolean);
    if (emails.length === 0) { setErr(t(lang,"noRestockEmails")); return; }
    setSending(true); setErr("");
    try {
      let photoUrl = null;
      if (photo) photoUrl = await uploadFile(`clients/${clientId}/supplyRequests/${Date.now()}`, photo.file);
      await sendSupplyRequest({
        toEmails: emails, clientName, items: Array.from(checked), notes: notes.trim(),
        photoUrl, requestedBy: member?.name,
      });
      setSent(true);
    } catch (e) {
      setErr(e.message || t(lang,"sendRequestFailed"));
    }
    setSending(false);
  };

  if (sent) {
    return (
      <div style={{ padding:"16px", flex:1, overflowY:"auto" }}>
        <div style={{ background:C.white, borderRadius:"16px", border:`1px solid ${C.border}`, padding:"28px 24px", maxWidth:"400px", margin:"20px auto", textAlign:"center" }}>
          <div style={{ fontSize:"32px", marginBottom:"10px" }}>✅</div>
          <div style={{ fontSize:"18px", fontWeight:"700", color:C.navy, marginBottom:"6px" }}>{t(lang,"requestSent")}</div>
          <div style={{ fontSize:"13px", color:C.textMid, marginBottom:"18px" }}>{t(lang,"restockContactsEmailed")}</div>
          <button style={S.btn} onClick={()=>{ setSent(false); setChecked(new Set()); setNotes(""); setPhoto(null); }}>{t(lang,"sendAnotherRequest")}</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding:"16px", flex:1, overflowY:"auto" }}>
      <div style={{ ...S.sectionTitle }}>{t(lang,"requestSupplies")}</div>
      {(supplyList||[]).length === 0 ? (
        <div style={{ fontSize:"13px", color:C.textMid }}>{t(lang,"noSuppliesTracked")}</div>
      ) : (
        <div style={{ display:"flex", flexDirection:"column", gap:"8px", marginBottom:"16px" }}>
          {supplyList.map(item=>(
            <div key={item} onClick={()=>toggle(item)}
              style={{ display:"flex", alignItems:"center", gap:"10px", background:C.white, border:`1.5px solid ${checked.has(item)?C.accent:C.border}`, borderRadius:"10px", padding:"12px 14px", cursor:"pointer" }}>
              <div style={{ width:"20px", height:"20px", borderRadius:"5px", border:`1.5px solid ${checked.has(item)?C.accent:C.border}`, background:checked.has(item)?C.accent:C.white, display:"flex", alignItems:"center", justifyContent:"center", color:C.white, fontSize:"13px" }}>
                {checked.has(item) && "✓"}
              </div>
              <div style={{ fontSize:"14px", fontWeight:"600" }}>{item}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ fontSize:"11px", color:C.textMid, marginBottom:"4px" }}>{t(lang,"photoOfSupplies")}</div>
      {photo ? (
        <img src={photo.previewUrl} alt="current supplies" style={{ width:"100%", maxWidth:"300px", borderRadius:"10px", marginBottom:"10px", objectFit:"cover" }} />
      ) : null}
      <button style={{ ...S.btnSm(C.offWhite,C.textMid), marginBottom:"14px" }} onClick={capturePhoto}>📷 {photo?t(lang,"retakePhoto"):t(lang,"addPhoto")}</button>

      <input style={S.input} placeholder={t(lang,"notesOptional")} value={notes} onChange={e=>setNotes(e.target.value)} />

      {err && <div style={S.err}>{err}</div>}
      <button style={S.btn} onClick={submit} disabled={sending || (supplyList||[]).length===0}>
        {sending ? t(lang,"sending") : t(lang,"sendRequest")}
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// FIRST-AREA PHOTO CAPTURE — shown once, automatically, right after a team
// member clocks in on a client that has at least one photo area configured.
// Same before/after capture as the QR-triggered PhotoCheckin below, minus
// its "auth" step (the team member is already signed in via Time Card).
// ─────────────────────────────────────────────────────────────────────────
function PhotoThumbGrid({ urls }) {
  if (!urls.length) return null;
  return (
    <div style={{ display:"grid", gridTemplateColumns:"repeat(3, 1fr)", gap:"8px", marginBottom:"14px" }}>
      {urls.map((u,i)=>(
        <img key={i} src={u} alt="" style={{ width:"100%", aspectRatio:"1", objectFit:"cover", borderRadius:"8px", border:`1px solid ${C.border}` }} />
      ))}
    </div>
  );
}

function FirstAreaCapture({ clientId, area, clientName, member, onDone, lang="en" }) {
  const [step,      setStep]      = useState("before");
  const [sessionId, setSessionId] = useState(null);
  const [beforeUrls,setBeforeUrls]= useState([]);
  const [afterUrls, setAfterUrls] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [err,       setErr]       = useState("");

  const capture = (type) => {
    const input = document.createElement("input");
    input.type = "file"; input.accept = "image/*"; input.capture = "environment";
    input.onchange = async (e) => {
      const file = e.target.files[0]; if (!file) return;
      setUploading(true); setErr("");
      try {
        const url = await uploadFile(`photoSessions/${clientId}/${area}/${Date.now()}_${type}`, file);
        if (type === "before") {
          if (!sessionId) {
            const r = await addDoc(collection(db,"photoSessions"), {
              clientId, area, cleanerName: member.name,
              beforePhotoUrls: [url], afterPhotoUrls: [],
              beforeTimestamp: serverTimestamp(), afterTimestamp: null,
              status: "before_taken", createdAt: serverTimestamp(),
            });
            setSessionId(r.id);
          } else {
            await updateDoc(doc(db,"photoSessions",sessionId), { beforePhotoUrls: arrayUnion(url) });
          }
          setBeforeUrls(prev=>[...prev, url]);
        } else {
          await updateDoc(doc(db,"photoSessions",sessionId), { afterPhotoUrls: arrayUnion(url), afterTimestamp: serverTimestamp() });
          setAfterUrls(prev=>[...prev, url]);
        }
      } catch(e) { setErr(t(lang,"uploadFailed")); }
      setUploading(false);
    };
    input.click();
  };

  const finish = async () => {
    await updateDoc(doc(db,"photoSessions",sessionId), { status:"complete" });
    onDone();
  };

  const areaLabel = decodeURIComponent(area||"").replace(/-/g," ");

  return (
    <div style={{ padding:"16px", flex:1, overflowY:"auto" }}>
      <div style={{ background:C.white, borderRadius:"16px", border:`1px solid ${C.border}`, padding:"28px 24px", maxWidth:"400px", margin:"20px auto", textAlign:"center" }}>
        {step==="before" ? (<>
          <div style={{ fontSize:"11px", fontWeight:"700", color:C.accent, letterSpacing:".08em", textTransform:"uppercase", marginBottom:"6px" }}>{t(lang,"clockedInStep1")}</div>
          <div style={{ fontSize:"20px", fontWeight:"700", color:C.navy, marginBottom:"4px" }}>{t(lang,"takeBeforePhotos")}</div>
          <div style={{ fontSize:"13px", color:C.textMid, textTransform:"capitalize", marginBottom:"24px" }}>{clientName} · {areaLabel}</div>
          {err && <div style={S.err}>{err}</div>}
          <PhotoThumbGrid urls={beforeUrls} />
          <button onClick={()=>capture("before")} disabled={uploading}
            style={{ width:"100%", padding:"22px 0", background:uploading?C.textMid:C.accent, color:C.white, border:"none", borderRadius:"12px", fontSize:"17px", fontWeight:"700", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:"10px" }}>
            <span style={{ fontSize:"30px" }}>📷</span>{uploading?t(lang,"uploading"):beforeUrls.length?t(lang,"addAnotherPhoto"):t(lang,"tapBeforePhoto")}
          </button>
          {beforeUrls.length>0 && (
            <button onClick={()=>setStep("after")} style={{ ...S.btn, background:C.green, marginTop:"10px" }}>{t(lang,"continueToAfter")}</button>
          )}
        </>) : (<>
          <PhotoThumbGrid urls={beforeUrls} />
          <div style={{ fontSize:"11px", fontWeight:"700", color:C.green, letterSpacing:".08em", textTransform:"uppercase", marginBottom:"6px" }}>{t(lang,"step2of2")}</div>
          <div style={{ fontSize:"20px", fontWeight:"700", color:C.navy, marginBottom:"4px" }}>{t(lang,"takeAfterPhotos")}</div>
          <div style={{ fontSize:"13px", color:C.textMid, textTransform:"capitalize", marginBottom:"24px" }}>{clientName} · {areaLabel}</div>
          {err && <div style={S.err}>{err}</div>}
          <PhotoThumbGrid urls={afterUrls} />
          <button onClick={()=>capture("after")} disabled={uploading}
            style={{ width:"100%", padding:"22px 0", background:uploading?C.textMid:C.green, color:C.white, border:"none", borderRadius:"12px", fontSize:"17px", fontWeight:"700", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:"10px" }}>
            <span style={{ fontSize:"30px" }}>✅</span>{uploading?t(lang,"uploading"):afterUrls.length?t(lang,"addAnotherPhoto"):t(lang,"tapAfterPhoto")}
          </button>
          {afterUrls.length>0 && (
            <button onClick={finish} style={{ ...S.btn, background:C.green, marginTop:"10px" }}>{t(lang,"saveFinish")}</button>
          )}
        </>)}
        <button style={{ ...S.ghostBtn, marginTop:"14px" }} onClick={onDone}>{t(lang,"skipForNow")}</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// PHOTO CHECK-IN  (cleaner-facing, reached via QR code URL)
// ─────────────────────────────────────────────────────────────────────────
function PhotoCheckin({ clientId, area, logoUrl, lang="en", toggleLang }) {
  const [clientData, setClientData] = useState(null);
  const [step,       setStep]       = useState("auth"); // auth | before | after | done
  const [cleaner,    setCleaner]    = useState(null);
  const [pin,        setPin]        = useState("");
  const [err,        setErr]        = useState("");
  const [busy,       setBusy]       = useState(false);
  const [sessionId,  setSessionId]  = useState(null);
  const [beforeUrls, setBeforeUrls] = useState([]);
  const [afterUrls,  setAfterUrls]  = useState([]);
  const [uploading,  setUploading]  = useState(false);

  useEffect(() => {
    if (!clientId) return;
    return onSnapshot(doc(db,"clients",clientId), d => {
      if (d.exists()) setClientData({ id:d.id, ...d.data() });
    });
  }, [clientId]);

  // PIN is verified server-side (teamMemberLogin) — this screen never
  // downloads the company's roster of names/photos/PINs just so a cleaner
  // can pick themselves from a list before checking in. See TeamMemberLogin
  // above for the same primitive used by the Team sign-in screen.
  const verify = async () => {
    if (!pin || busy) return;
    setBusy(true); setErr("");
    try {
      const member = await teamMemberLogin(pin);
      setCleaner(member);
      setStep("before");
    } catch {
      setErr(t(lang,"wrongPin")); setPin("");
    }
    setBusy(false);
  };

  const capture = (type) => {
    const input = document.createElement("input");
    input.type = "file"; input.accept = "image/*"; input.capture = "environment";
    input.onchange = async (e) => {
      const file = e.target.files[0]; if (!file) return;
      setUploading(true); setErr("");
      try {
        const url = await uploadFile(`photoSessions/${clientId}/${area}/${Date.now()}_${type}`, file);
        if (type === "before") {
          if (!sessionId) {
            const r = await addDoc(collection(db,"photoSessions"), {
              clientId, area, cleanerName: cleaner.name,
              beforePhotoUrls: [url], afterPhotoUrls: [],
              beforeTimestamp: serverTimestamp(), afterTimestamp: null,
              status: "before_taken", createdAt: serverTimestamp(),
            });
            setSessionId(r.id);
          } else {
            await updateDoc(doc(db,"photoSessions",sessionId), { beforePhotoUrls: arrayUnion(url) });
          }
          setBeforeUrls(prev=>[...prev, url]);
        } else {
          await updateDoc(doc(db,"photoSessions",sessionId), { afterPhotoUrls: arrayUnion(url), afterTimestamp: serverTimestamp() });
          setAfterUrls(prev=>[...prev, url]);
        }
      } catch(e) { setErr(t(lang,"uploadFailed")); }
      setUploading(false);
    };
    input.click();
  };

  const finish = async () => {
    await updateDoc(doc(db,"photoSessions",sessionId), { status:"complete" });
    setStep("done");
  };

  const areaLabel = decodeURIComponent(area||"").replace(/-/g," ");

  const Shell = ({ children }) => (
    <div style={{ minHeight:"100vh", background:C.navy, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"24px", position:"relative" }}>
      <div style={{ position:"absolute", top:"16px", right:"16px" }}>
        <LangToggle lang={lang} onToggle={toggleLang} />
      </div>
      <img src={logoUrl || DEFAULT_LOGO_URL} alt={BRAND.name} style={{ height:"50px", objectFit:"contain", marginBottom:"20px", opacity:.85, ...(logoUrl ? {} : { filter:"brightness(0) invert(1)" }) }} />
      <div style={{ background:C.white, borderRadius:"16px", padding:"28px 24px", width:"100%", maxWidth:"380px", boxShadow:"0 24px 64px rgba(0,0,0,0.3)" }}>
        {children}
      </div>
    </div>
  );

  if (step === "auth") return (
    <Shell>
      <div style={{ fontSize:"11px", fontWeight:"700", color:C.accent, letterSpacing:".08em", textTransform:"uppercase", marginBottom:"4px" }}>{t(lang,"photoCheckIn")}</div>
      <div style={{ fontSize:"18px", fontWeight:"700", color:C.navy, marginBottom:"2px" }}>{clientData?.name||"…"}</div>
      <div style={{ fontSize:"13px", color:C.textMid, textTransform:"capitalize", marginBottom:"20px" }}>{areaLabel}</div>
      {err && <div style={S.err}>{err}</div>}
      <input style={S.input} type="password" placeholder={t(lang,"yourPin")} value={pin}
        onChange={e=>setPin(e.target.value)} onKeyDown={e=>e.key==="Enter"&&verify()} maxLength={8} autoFocus />
      <button style={S.btn} disabled={busy} onClick={verify}>{busy?"…":t(lang,"startCheckIn")}</button>
    </Shell>
  );

  if (step === "before") return (
    <Shell>
      <div style={{ textAlign:"center" }}>
        <div style={{ fontSize:"11px", fontWeight:"700", color:C.accent, letterSpacing:".08em", textTransform:"uppercase", marginBottom:"6px" }}>{t(lang,"step1of2")}</div>
        <div style={{ fontSize:"20px", fontWeight:"700", color:C.navy, marginBottom:"4px" }}>{t(lang,"takeBeforePhotos")}</div>
        <div style={{ fontSize:"13px", color:C.textMid, textTransform:"capitalize", marginBottom:"4px" }}>{clientData?.name} · {areaLabel}</div>
        <div style={{ fontSize:"12px", color:C.textMid, marginBottom:"20px" }}>{t(lang,"signedInAs",{name:cleaner?.name||""})}</div>
        {err && <div style={S.err}>{err}</div>}
        <PhotoThumbGrid urls={beforeUrls} />
        <button onClick={()=>capture("before")} disabled={uploading}
          style={{ width:"100%", padding:"22px 0", background:uploading?C.textMid:C.accent, color:C.white, border:"none", borderRadius:"12px", fontSize:"17px", fontWeight:"700", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:"10px" }}>
          <span style={{ fontSize:"30px" }}>📷</span>{uploading?t(lang,"uploading"):beforeUrls.length?t(lang,"addAnotherPhoto"):t(lang,"tapBeforePhoto")}
        </button>
        <div style={{ fontSize:"12px", color:C.textMid, marginTop:"10px" }}>{t(lang,"photoAreaBeforeCleaning")}</div>
        {beforeUrls.length>0 && (
          <button onClick={()=>setStep("after")} style={{ ...S.btn, background:C.green, marginTop:"14px" }}>{t(lang,"continueToAfter")}</button>
        )}
      </div>
    </Shell>
  );

  if (step === "after") return (
    <Shell>
      <div style={{ textAlign:"center" }}>
        <div style={{ fontSize:"11px", fontWeight:"700", color:C.green, letterSpacing:".08em", textTransform:"uppercase", marginBottom:"6px" }}>{t(lang,"step2of2")}</div>
        <div style={{ fontSize:"20px", fontWeight:"700", color:C.navy, marginBottom:"4px" }}>{t(lang,"takeAfterPhotos")}</div>
        <div style={{ fontSize:"13px", color:C.textMid, textTransform:"capitalize", marginBottom:"24px" }}>{clientData?.name} · {areaLabel}</div>
        {err && <div style={S.err}>{err}</div>}
        <PhotoThumbGrid urls={afterUrls} />
        <button onClick={()=>capture("after")} disabled={uploading}
          style={{ width:"100%", padding:"22px 0", background:uploading?C.textMid:C.green, color:C.white, border:"none", borderRadius:"12px", fontSize:"17px", fontWeight:"700", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:"10px" }}>
          <span style={{ fontSize:"30px" }}>✅</span>{uploading?t(lang,"uploading"):afterUrls.length?t(lang,"addAnotherPhoto"):t(lang,"tapAfterPhoto")}
        </button>
        <div style={{ fontSize:"12px", color:C.textMid, marginTop:"10px" }}>{t(lang,"photoAreaAfterCleaning")}</div>
        {afterUrls.length>0 && (
          <button onClick={finish} style={{ ...S.btn, background:C.green, marginTop:"14px" }}>{t(lang,"saveFinish")}</button>
        )}
      </div>
    </Shell>
  );

  return (
    <Shell>
      <div style={{ textAlign:"center" }}>
        <div style={{ fontSize:"48px", marginBottom:"12px" }}>✅</div>
        <div style={{ fontSize:"20px", fontWeight:"700", color:C.navy, marginBottom:"6px" }}>{t(lang,"checkInComplete")}</div>
        <div style={{ fontSize:"13px", color:C.textMid, textTransform:"capitalize", marginBottom:"20px" }}>
          {t(lang,"areaDocumentedFor",{area:areaLabel,client:clientData?.name})}
        </div>
        <div style={{ background:"#F0FBF4", border:`1px solid ${C.green}55`, borderRadius:"10px", padding:"12px", fontSize:"13px", color:C.green, fontWeight:"600", marginBottom:"16px" }}>
          {t(lang,"beforeAfterSaved")}
        </div>
        <button onClick={()=>{ setStep("before"); setBeforeUrls([]); setAfterUrls([]); setSessionId(null); setErr(""); }}
          style={S.ghostBtn}>{t(lang,"documentAnotherArea")}</button>
      </div>
    </Shell>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// PHOTO GALLERY TAB  (client / admin facing)
// ─────────────────────────────────────────────────────────────────────────
function PhotoGalleryTab({ clientId, lang="en" }) {
  const [sessions,    setSessions]    = useState([]);
  const [expanded,    setExpanded]    = useState(null);
  const [filterArea,  setFilterArea]  = useState("all");

  useEffect(() => {
    if (!clientId) return;
    const q = query(collection(db,"photoSessions"), where("clientId","==",clientId), where("status","==","complete"));
    return onSnapshot(q, snap => {
      const docs = snap.docs.map(d=>({id:d.id,...d.data()}));
      docs.sort((a,b)=> (b.createdAt?.seconds||0)-(a.createdAt?.seconds||0));
      setSessions(docs);
    });
  }, [clientId]);

  const areas   = ["all", ...new Set(sessions.map(s=>s.area))];
  const visible = filterArea==="all" ? sessions : sessions.filter(s=>s.area===filterArea);

  if (sessions.length===0) return (
    <div style={{ textAlign:"center", padding:"48px 0", color:C.textMid }}>
      <div style={{ fontSize:"36px", marginBottom:"10px" }}>📷</div>
      <div style={{ fontSize:"14px", fontWeight:"600", marginBottom:"4px" }}>{t(lang,"noPhotosYet")}</div>
      <div style={{ fontSize:"13px" }}>{t(lang,"photosEmptyHint")}</div>
    </div>
  );

  return (
    <div>
      <div style={{ display:"flex", gap:"8px", flexWrap:"wrap", marginBottom:"16px" }}>
        {areas.map(a=>(
          <button key={a} onClick={()=>setFilterArea(a)}
            style={{ padding:"6px 14px", borderRadius:"20px", border:`1.5px solid ${filterArea===a?C.accent:C.border}`, background:filterArea===a?"#EEF3FB":C.white, color:filterArea===a?C.accent:C.textMid, fontSize:"12px", fontWeight:"600", cursor:"pointer", textTransform:"capitalize" }}>
            {a==="all" ? t(lang,"allAreas") : a.replace(/-/g," ")}
          </button>
        ))}
      </div>
      <div style={{ display:"flex", flexDirection:"column", gap:"10px" }}>
        {visible.map(s=>(
          <div key={s.id} style={{ background:C.white, borderRadius:"12px", border:`1px solid ${C.border}`, overflow:"hidden" }}>
            <div onClick={()=>setExpanded(expanded===s.id?null:s.id)}
              style={{ padding:"12px 16px", display:"flex", alignItems:"center", justifyContent:"space-between", cursor:"pointer", borderBottom:expanded===s.id?`1px solid ${C.border}`:"none" }}>
              <div>
                <div style={{ fontWeight:"600", fontSize:"14px", textTransform:"capitalize" }}>{s.area?.replace(/-/g," ")}</div>
                <div style={{ fontSize:"12px", color:C.textMid }}>{fmtDate(s.beforeTimestamp)} · {s.cleanerName}</div>
              </div>
              <span style={{ color:C.green, fontSize:"12px", fontWeight:"700" }}>✓ {expanded===s.id?"▲":"▼"}</span>
            </div>
            {expanded===s.id && (()=>{
              const beforeUrls = s.beforePhotoUrls || (s.beforePhotoUrl ? [s.beforePhotoUrl] : []);
              const afterUrls  = s.afterPhotoUrls  || (s.afterPhotoUrl  ? [s.afterPhotoUrl]  : []);
              return (
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"10px", padding:"14px" }}>
                  <div>
                    <div style={{ fontSize:"10px", fontWeight:"700", color:C.textMid, marginBottom:"5px", textTransform:"uppercase", letterSpacing:".07em" }}>{t(lang,"before")}</div>
                    <div style={{ display:"flex", flexDirection:"column", gap:"6px" }}>
                      {beforeUrls.map((u,i)=><img key={i} src={u} alt="before" style={{ width:"100%", borderRadius:"8px", objectFit:"cover", aspectRatio:"4/3" }} />)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize:"10px", fontWeight:"700", color:C.green, marginBottom:"5px", textTransform:"uppercase", letterSpacing:".07em" }}>{t(lang,"after")}</div>
                    <div style={{ display:"flex", flexDirection:"column", gap:"6px" }}>
                      {afterUrls.map((u,i)=><img key={i} src={u} alt="after" style={{ width:"100%", borderRadius:"8px", objectFit:"cover", aspectRatio:"4/3" }} />)}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// TIME CARD TAB  (clock in/out with door photos + access codes)
// ─────────────────────────────────────────────────────────────────────────
function fmtDuration(startTs, endTs) {
  if (!startTs || !endTs) return "";
  const start = startTs?.toDate ? startTs.toDate() : new Date(startTs);
  const end   = endTs?.toDate   ? endTs.toDate()   : new Date(endTs);
  const mins  = Math.max(0, Math.round((end - start) / 60000));
  const h = Math.floor(mins/60), m = mins%60;
  return h>0 ? `${h}h ${m}m` : `${m}m`;
}

function TimeCardTab({ clientId, role, member, myOpenEntry, clockIn, clockOut, clockUploading, clockErr, onGoToCheckout, lang="en" }) {
  const [codes,       setCodes]       = useState([]);
  const [entries,     setEntries]     = useState([]);
  const [newLabel,    setNewLabel]    = useState("");
  const [newValue,    setNewValue]    = useState("");
  const [editingCode, setEditingCode] = useState(null); // { id, label, value }
  const [expanded,    setExpanded]    = useState(null);

  const isAdmin = role === "admin";
  const isTeam  = role === "team";

  useEffect(() => {
    if (!clientId) return;
    const u1 = onSnapshot(collection(db,"clients",clientId,"accessCodes"), snap => {
      const docs = snap.docs.map(d=>({id:d.id,...d.data()}));
      docs.sort((a,b)=>(a.order||0)-(b.order||0));
      setCodes(docs);
    });
    const u2 = onSnapshot(collection(db,"clients",clientId,"timeEntries"), snap => {
      const docs = snap.docs.map(d=>({id:d.id,...d.data()}));
      docs.sort((a,b)=>(b.clockInAt?.seconds||0)-(a.clockInAt?.seconds||0));
      setEntries(docs);
    });
    return ()=>{ u1(); u2(); };
  }, [clientId]);

  const visibleEntries = isAdmin ? entries : entries.filter(e=>e.memberId===member?.id);

  const addCode = async () => {
    if (!newLabel.trim() || !newValue.trim()) return;
    await addDoc(collection(db,"clients",clientId,"accessCodes"), { label:newLabel.trim(), value:newValue.trim(), order: codes.length });
    setNewLabel(""); setNewValue("");
  };
  const saveCodeEdit = async () => {
    if (!editingCode.label.trim() || !editingCode.value.trim()) return;
    await updateDoc(doc(db,"clients",clientId,"accessCodes",editingCode.id), { label: editingCode.label.trim(), value: editingCode.value.trim() });
    setEditingCode(null);
  };
  const deleteCode = (id) => deleteDoc(doc(db,"clients",clientId,"accessCodes",id));

  return (
    <div>
      {/* Clock In/Out — team only */}
      {isTeam && (
        <div style={{ background:C.white, borderRadius:"12px", border:`1px solid ${C.border}`, padding:"18px", textAlign:"center", marginBottom:"20px" }}>
          {clockErr && <div style={S.err}>{clockErr}</div>}
          {myOpenEntry ? (
            <>
              <div style={{ fontSize:"12px", color:C.textMid, marginBottom:"10px" }}>{t(lang,"clockedInAt",{date:fmtDate(myOpenEntry.clockInAt)})}</div>
              <button onClick={onGoToCheckout}
                style={{ width:"100%", padding:"18px 0", background:C.accent, color:C.white, border:"none", borderRadius:"12px", fontSize:"16px", fontWeight:"700", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:"10px" }}>
                {t(lang,"finishCheckOut")}
              </button>
              <div style={{ fontSize:"11px", color:C.textMid, marginTop:"8px" }}>{t(lang,"completeChecklistToCheckOut")}</div>
            </>
          ) : (
            <button onClick={clockIn} disabled={clockUploading}
              style={{ width:"100%", padding:"18px 0", background:clockUploading?C.textMid:C.green, color:C.white, border:"none", borderRadius:"12px", fontSize:"16px", fontWeight:"700", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:"10px" }}>
              <span style={{ fontSize:"24px" }}>📷</span>{clockUploading?t(lang,"uploading"):t(lang,"clockInBtn")}
            </button>
          )}
        </div>
      )}

      {/* Access Codes */}
      <div style={S.sectionTitle}>{isTeam ? t(lang,"accessCodes") : "🔑 Access Codes"}</div>
      <div style={{ background:C.white, borderRadius:"12px", border:`1px solid ${C.border}`, overflow:"hidden", marginBottom:"20px" }}>
        {codes.length===0 && (
          <div style={{ padding:"16px", fontSize:"13px", color:C.textMid, textAlign:"center" }}>
            {isAdmin ? "No codes saved yet. Add one below." : isTeam ? t(lang,"noAccessCodesTeam") : "No access codes saved for this client yet."}
          </div>
        )}
        {codes.map((c,idx)=>(
          <div key={c.id} style={{ padding:"12px 16px", display:"flex", alignItems:"center", gap:"10px", borderBottom:idx===codes.length-1?"none":`1px solid ${C.border}` }}>
            {editingCode?.id===c.id ? (
              <>
                <input style={{ ...S.input, marginBottom:0, flex:1 }} value={editingCode.label} onChange={e=>setEditingCode(p=>({...p,label:e.target.value}))} placeholder="Label" />
                <input style={{ ...S.input, marginBottom:0, flex:1 }} value={editingCode.value} onChange={e=>setEditingCode(p=>({...p,value:e.target.value}))} placeholder="Code" />
                <button style={S.btnSm(C.green)} onClick={saveCodeEdit}>Save</button>
                <button style={S.btnSm(C.offWhite,C.textMid)} onClick={()=>setEditingCode(null)}>✕</button>
              </>
            ) : (
              <>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:"11px", fontWeight:"700", color:C.textMid, textTransform:"uppercase", letterSpacing:".05em" }}>{c.label}</div>
                  <div style={{ fontSize:"20px", fontWeight:"800", color:C.navy, letterSpacing:"1px", fontFamily:"monospace" }}>{c.value}</div>
                </div>
                {isAdmin && (<>
                  <button style={{ background:"transparent", border:"none", color:C.accent, cursor:"pointer", fontSize:"14px" }} onClick={()=>setEditingCode({id:c.id, label:c.label, value:c.value})}>✏️</button>
                  <button style={{ background:"transparent", border:"none", color:C.red, cursor:"pointer", fontSize:"16px" }} onClick={()=>deleteCode(c.id)}>✕</button>
                </>)}
              </>
            )}
          </div>
        ))}
        {isAdmin && (
          <div style={S.addRow}>
            <input style={{ ...S.addInput, flex:1 }} placeholder="Label (e.g. Alarm Code)" value={newLabel} onChange={e=>setNewLabel(e.target.value)} />
            <input style={{ ...S.addInput, flex:1 }} placeholder="Code" value={newValue} onChange={e=>setNewValue(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addCode()} />
            <button style={S.btnSm()} onClick={addCode}>+ Add</button>
          </div>
        )}
      </div>

      {/* Time Log */}
      <div style={S.sectionTitle}>{isAdmin ? "🕐 Team Time Log" : isTeam ? t(lang,"myTimeLog") : "🕐 My Time Log"}</div>
      {visibleEntries.length===0 && (
        <div style={{ textAlign:"center", padding:"32px 0", color:C.textMid, fontSize:"13px" }}>{isTeam ? t(lang,"noTimeEntries") : "No time entries yet."}</div>
      )}
      <div style={{ display:"flex", flexDirection:"column", gap:"10px" }}>
        {visibleEntries.map(e=>(
          <div key={e.id} style={{ background:C.white, borderRadius:"12px", border:`1px solid ${C.border}`, overflow:"hidden" }}>
            <div onClick={()=>setExpanded(expanded===e.id?null:e.id)}
              style={{ padding:"12px 16px", display:"flex", alignItems:"center", justifyContent:"space-between", cursor:"pointer", borderBottom:expanded===e.id?`1px solid ${C.border}`:"none" }}>
              <div>
                {isAdmin && <div style={{ fontWeight:"600", fontSize:"14px" }}>{e.memberName}</div>}
                <div style={{ fontSize:"12px", color:C.textMid }}>
                  {fmtDate(e.clockInAt)}{e.clockOutAt ? ` – ${fmtDate(e.clockOutAt)}` : ""}
                </div>
              </div>
              <div style={{ display:"flex", alignItems:"center", gap:"8px" }}>
                {e.status==="open"
                  ? <span style={S.badge(C.amber)}>{isTeam ? t(lang,"active") : "ACTIVE"}</span>
                  : <span style={{ fontSize:"12px", color:C.textMid, fontWeight:"600" }}>{fmtDuration(e.clockInAt, e.clockOutAt)}</span>}
                <span style={{ color:C.textMid, fontSize:"12px" }}>{expanded===e.id?"▲":"▼"}</span>
              </div>
            </div>
            {expanded===e.id && (
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"10px", padding:"14px" }}>
                <div>
                  <div style={{ fontSize:"10px", fontWeight:"700", color:C.green, marginBottom:"5px", textTransform:"uppercase", letterSpacing:".07em" }}>{isTeam ? t(lang,"clockIn") : "Clock In"}</div>
                  <img src={e.clockInPhotoUrl} alt="clock in" style={{ width:"100%", borderRadius:"8px", objectFit:"cover", aspectRatio:"4/3" }} />
                </div>
                <div>
                  <div style={{ fontSize:"10px", fontWeight:"700", color:C.red, marginBottom:"5px", textTransform:"uppercase", letterSpacing:".07em" }}>{isTeam ? t(lang,"clockOut") : "Clock Out"}</div>
                  {e.clockOutPhotoUrl
                    ? <img src={e.clockOutPhotoUrl} alt="clock out" style={{ width:"100%", borderRadius:"8px", objectFit:"cover", aspectRatio:"4/3" }} />
                    : <div style={{ width:"100%", aspectRatio:"4/3", borderRadius:"8px", background:C.offWhite, display:"flex", alignItems:"center", justifyContent:"center", color:C.textMid, fontSize:"12px" }}>{isTeam ? t(lang,"notYet") : "Not yet"}</div>}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// SCHEDULE — admin plans shifts, team members see only their own.
// Every occurrence of a repeating shift is its own independent doc in
// `schedule` (so editing/cancelling one day never touches the rest), tagged
// with a ruleId linking it back to the `scheduleRules` doc that generated
// it. Recurring rules have no end date — instead they're Active or
// Inactive; an Active rule's occurrences get topped up out to a rolling
// SCHEDULE_HORIZON_DAYS window every time either schedule screen loads,
// and an Inactive rule just stops generating new ones (past/already-
// generated occurrences are untouched either way).
// ─────────────────────────────────────────────────────────────────────────
const SCHEDULE_HORIZON_DAYS = 90;

// Expands a recurring rule into individual date keys within [fromDate,
// toDate] (inclusive). Mirrors the one-time range logic this replaced, but
// anchored to the rule's own original rangeStart (not the top-up window)
// so biweekly parity never drifts across repeated top-ups.
function computeRuleDates(rule, fromDate, toDate) {
  const dates = [];
  if (fromDate > toDate) return dates;
  if (rule.mode === "weekly" || rule.mode === "biweekly") {
    const [oy,om,od] = rule.rangeStart.split("-").map(Number);
    const originWeekStart = startOfWeek(new Date(oy,om-1,od));
    for (let d = new Date(fromDate); d <= toDate; d = addDays(d,1)) {
      if (!rule.days.includes(d.getDay())) continue;
      if (rule.mode === "biweekly") {
        const weekIdx = Math.round((startOfWeek(d) - originWeekStart) / (7*86400000));
        if (weekIdx % 2 !== 0) continue;
      }
      dates.push(dateKey(d));
      if (dates.length >= 400) break;
    }
  } else if (rule.mode === "monthly") {
    let y = fromDate.getFullYear(), m = fromDate.getMonth()+1;
    const endY = toDate.getFullYear(), endM = toDate.getMonth()+1;
    while (y < endY || (y === endY && m <= endM)) {
      const day = Math.min(rule.monthDay, daysInMonth(y,m));
      const candidate = new Date(y, m-1, day);
      if (candidate >= fromDate && candidate <= toDate) dates.push(dateKey(candidate));
      m++; if (m > 12) { m = 1; y++; }
      if (dates.length >= 400) break;
    }
  }
  return dates;
}

// Ensures one active rule's occurrences exist through today+HORIZON. Safe
// to call repeatedly (e.g. every time a schedule screen mounts) — it only
// ever generates what's missing, starting after whatever was generated last.
async function topUpRule(rule) {
  const horizon = addDays(new Date(), SCHEDULE_HORIZON_DAYS);
  // Equality-only filter (ruleId) — Firestore's automatic single-field
  // indexes cover this without needing a composite index deployed to every
  // buyer's own project; finding the max date happens client-side, same
  // pattern MemberScheduleTab already uses for its own schedule query.
  const existingSnap = await getDocs(query(collection(db,"schedule"), where("ruleId","==",rule.id)));
  const existingDates = existingSnap.docs.map(d=>d.data().date);
  const today = new Date(); today.setHours(0,0,0,0);
  const [ry,rm,rd] = rule.rangeStart.split("-").map(Number);
  const ruleStart = new Date(ry,rm-1,rd);
  let genFrom;
  if (existingDates.length > 0) {
    const maxKey = existingDates.reduce((a,b)=>a>b?a:b);
    const [ly,lm,ld] = maxKey.split("-").map(Number);
    genFrom = addDays(new Date(ly,lm-1,ld), 1);
  } else {
    genFrom = ruleStart > today ? ruleStart : today;
  }
  const dates = computeRuleDates(rule, genFrom, horizon);
  if (dates.length === 0) return;
  const batch = writeBatch(db);
  // Deterministic doc ID (not an auto-ID) makes this whole function
  // idempotent: the admin's post-create call and the reactive effect that
  // fires from the rule's own onSnapshot land on the same doc rather than
  // racing into duplicates, which is exactly what happened before this —
  // Firestore's onSnapshot fires once for the local optimistic write and
  // again on server confirmation, so both call sites were never actually
  // guaranteed to run only once.
  dates.forEach(dk => batch.set(doc(db,"schedule",`${rule.id}_${dk}`), {
    memberId: rule.memberId, memberName: rule.memberName,
    clientId: rule.clientId, clientName: rule.clientName,
    startTime: rule.startTime||null, endTime: rule.endTime||null,
    notes: rule.notes||"", date: dk, ruleId: rule.id,
    checkedInAt: null, createdAt: serverTimestamp(),
  }));
  await batch.commit();
}

const RULE_LABEL = { weekly:"Weekly", biweekly:"Every 2 weeks", monthly:"Monthly" };
function ruleDayLabel(rule) {
  if (rule.mode === "monthly") return `Day ${rule.monthDay} of each month`;
  return (rule.days||[]).slice().sort().map(i=>DOW[i]).join(", ");
}

// Shared by AdminScheduleTab's weekly calendar and AdminSetup's per-client
// shortcut — same recurring-shift creation, just reached from two places.
async function createScheduleRule(fields) {
  const ruleRef = await addDoc(collection(db,"scheduleRules"), {
    ...fields, active: true, createdAt: serverTimestamp(),
  });
  await topUpRule({ id: ruleRef.id, ...fields });
}
const toggleRuleActive = (rule) => updateDoc(doc(db,"scheduleRules",rule.id), { active: !rule.active });
const deleteRule = (id) => {
  if (window.confirm("Delete this recurring shift? Already-generated dates stay on the calendar unless removed individually."))
    deleteDoc(doc(db,"scheduleRules",id));
};

// Read-only — reachable from the main Admin nav. Adding/editing/removing
// shifts and recurring rules only happens from Admin Setup → Clients (see
// the "📅 Schedule" section there), so office staff have one place to
// change the schedule instead of two that can drift out of sync.
function AdminScheduleTab({ clients, teamMembers }) {
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [shifts,    setShifts]    = useState([]);
  const [rules,     setRules]     = useState([]);

  const weekEnd     = addDays(weekStart, 6);
  const weekStartKey = dateKey(weekStart);
  const weekEndKey   = dateKey(weekEnd);

  useEffect(() => {
    const q = query(collection(db,"schedule"), where("date",">=",weekStartKey), where("date","<=",weekEndKey), orderBy("date"));
    return onSnapshot(q, snap => setShifts(snap.docs.map(d=>({id:d.id,...d.data()}))));
  }, [weekStartKey, weekEndKey]);

  useEffect(() => {
    return onSnapshot(collection(db,"scheduleRules"), snap => setRules(snap.docs.map(d=>({id:d.id,...d.data()}))));
  }, []);

  // Every time the active-rule set changes (including on first load), make
  // sure each Active rule's occurrences reach the rolling horizon — this is
  // the only place generation happens after a rule's initial creation, so
  // it relies on an admin (or team member, via the same effect below)
  // opening a schedule screen periodically rather than a background job.
  useEffect(() => {
    rules.filter(r=>r.active).forEach(r => { topUpRule(r).catch(()=>{}); });
  }, [rules]);

  const byDate = {};
  shifts.forEach(s => { (byDate[s.date] ||= []).push(s); });
  const weekDates = Array.from({length:7}, (_,i)=>dateKey(addDays(weekStart,i)));

  return (
    <div style={{ padding:"16px", overflowY:"auto", flex:1 }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"16px", flexWrap:"wrap", gap:"10px" }}>
        <div style={{ display:"flex", alignItems:"center", gap:"8px" }}>
          <button style={S.btnSm(C.offWhite,C.textMid)} onClick={()=>setWeekStart(w=>addDays(w,-7))}>← Prev</button>
          <div style={{ fontWeight:"700", fontSize:"14px", minWidth:"170px", textAlign:"center", color:C.white }}>
            {fmtDateKey(weekStartKey)} – {fmtDateKey(weekEndKey)}
          </div>
          <button style={S.btnSm(C.offWhite,C.textMid)} onClick={()=>setWeekStart(w=>addDays(w,7))}>Next →</button>
          <button style={S.btnSm(C.offWhite,C.textMid)} onClick={()=>setWeekStart(startOfWeek(new Date()))}>Today</button>
        </div>
        <div style={{ fontSize:"12px", color:"#A8C0E8" }}>To add or change shifts, go to Admin Setup → Clients.</div>
      </div>

      <div style={{ display:"flex", flexDirection:"column", gap:"14px" }}>
        {weekDates.map(dk => (
          <div key={dk} style={{ background:C.white, borderRadius:"12px", border:`1px solid ${C.border}`, overflow:"hidden" }}>
            <div style={{ padding:"10px 16px", background:dk===todayKey()?"#EEF3FB":C.offWhite, fontWeight:"700", fontSize:"13px", color:dk===todayKey()?C.accent:C.textDark }}>
              {fmtDateKey(dk)}{dk===todayKey() && "  ·  Today"}
            </div>
            {(byDate[dk]||[]).length===0 ? (
              <div style={{ padding:"14px 16px", fontSize:"13px", color:C.textMid }}>No shifts scheduled.</div>
            ) : byDate[dk].map(s => (
              <div key={s.id} style={{ padding:"12px 16px", borderTop:`1px solid ${C.border}`, display:"flex", alignItems:"center", gap:"12px" }}>
                <Avatar src={teamMembers.find(m=>m.id===s.memberId)?.photoUrl} name={s.memberName} size={34} />
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:"600", fontSize:"14px" }}>
                    {s.memberName} <span style={{ color:C.textMid, fontWeight:"400" }}>→</span> {s.clientName}
                    {s.ruleId && <span title="From a recurring shift" style={{ marginLeft:"6px", fontSize:"12px" }}>🔁</span>}
                  </div>
                  <div style={{ fontSize:"12px", color:C.textMid }}>
                    {s.startTime || s.endTime ? `${fmtTime12(s.startTime)}${s.endTime?` – ${fmtTime12(s.endTime)}`:""}` : "All day"}
                    {s.notes && ` · ${s.notes}`}
                  </div>
                </div>
                {s.checkedInAt && <span style={S.badge(C.green)}>✓ {fmtDate(s.checkedInAt)}</span>}
              </div>
            ))}
          </div>
        ))}
      </div>

      <div style={{ ...S.sectionTitle, marginTop:"24px" }}>Recurring Shifts</div>
      <div style={{ background:C.white, borderRadius:"12px", border:`1px solid ${C.border}`, overflow:"hidden" }}>
        {rules.length===0 ? (
          <div style={{ padding:"14px 16px", fontSize:"13px", color:C.textMid }}>No recurring shifts set up.</div>
        ) : rules.map(r => (
          <div key={r.id} style={{ padding:"12px 16px", borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", gap:"12px" }}>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontWeight:"600", fontSize:"14px" }}>{r.memberName} <span style={{ color:C.textMid, fontWeight:"400" }}>→</span> {r.clientName}</div>
              <div style={{ fontSize:"12px", color:C.textMid }}>
                {RULE_LABEL[r.mode]} · {ruleDayLabel(r)} · since {fmtDateKey(r.rangeStart)}
                {(r.startTime || r.endTime) && ` · ${fmtTime12(r.startTime)}${r.endTime?` – ${fmtTime12(r.endTime)}`:""}`}
              </div>
            </div>
            <span style={S.badge(r.active?C.green:C.textMid)}>{r.active?"Active":"Inactive"}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MemberScheduleTab({ member, onOpenClient, onBack, onSignOut, logoUrl, lang="en", toggleLang }) {
  const [shifts, setShifts] = useState([]);

  useEffect(() => {
    if (!member?.id) return;
    // Equality-only filter (memberId) — Firestore's automatic single-field
    // indexes cover this without needing a composite index deployed to
    // every buyer's own project; the date>=today + sort happens client-side.
    const q = query(collection(db,"schedule"), where("memberId","==",member.id));
    return onSnapshot(q, snap => {
      const today = todayKey();
      const docs = snap.docs.map(d=>({id:d.id,...d.data()})).filter(s=>s.date>=today);
      docs.sort((a,b)=>a.date<b.date?-1:a.date>b.date?1:0);
      setShifts(docs);
    });
  }, [member?.id]);

  // Backstop for the same top-up AdminScheduleTab does — so this member's
  // upcoming shifts don't dry up just because no admin has opened the
  // Schedule tab recently. Only tops up rules that are theirs and Active.
  useEffect(() => {
    if (!member?.id) return;
    const q = query(collection(db,"scheduleRules"), where("memberId","==",member.id), where("active","==",true));
    getDocs(q).then(snap => snap.docs.forEach(d => topUpRule({ id:d.id, ...d.data() }).catch(()=>{})));
  }, [member?.id]);

  const byDate = {};
  shifts.forEach(s => { (byDate[s.date] ||= []).push(s); });
  const dateKeys = Object.keys(byDate).sort();

  return (
    <div style={S.app}>
      <div style={S.header}>
        <div style={S.headerLeft}>
          <img src={logoUrl || DEFAULT_LOGO_URL} alt={BRAND.name} style={{ height:"32px", objectFit:"contain", flexShrink:0, ...(logoUrl ? {} : { filter:"brightness(0) invert(1)" }) }} />
          <span style={{ fontSize:"13px", color:"#A8C0E8", whiteSpace:"nowrap" }}>{t(lang,"mySchedule")}</span>
        </div>
        <div style={S.headerRight}>
          <LangToggle lang={lang} onToggle={toggleLang} />
          <button style={S.logoutBtn} onClick={onBack}>{t(lang,"backClients")}</button>
          <button style={S.logoutBtn} onClick={onSignOut}>{t(lang,"signOut")}</button>
        </div>
      </div>
    <div style={S.body}>
      {dateKeys.length===0 && (
        <div style={{ textAlign:"center", padding:"40px 0", color:C.textMid, fontSize:"13px" }}>{t(lang,"noUpcomingShifts")}</div>
      )}
      <div style={{ display:"flex", flexDirection:"column", gap:"14px" }}>
        {dateKeys.map(dk => (
          <div key={dk}>
            <div style={{ fontWeight:"700", fontSize:"13px", color:dk===todayKey()?C.accent:C.textDark, marginBottom:"8px" }}>
              {fmtDateKey(dk)}{dk===todayKey() && `  ·  ${t(lang,"today")}`}
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:"10px" }}>
              {byDate[dk].map(s => (
                <div key={s.id} style={{ background:C.white, borderRadius:"12px", border:`1px solid ${C.border}`, padding:"14px 16px" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:"10px", marginBottom:"6px" }}>
                    <div onClick={()=>onOpenClient(s.clientId)} style={{ fontWeight:"700", fontSize:"15px", cursor:"pointer", color:C.accent }}>{s.clientName} →</div>
                  </div>
                  <div style={{ fontSize:"13px", color:C.textMid, marginBottom:s.notes?"4px":0 }}>
                    {s.startTime || s.endTime ? `${fmtTime12(s.startTime)}${s.endTime?` – ${fmtTime12(s.endTime)}`:""}` : t(lang,"allDay")}
                  </div>
                  {s.notes && <div style={{ fontSize:"13px", color:C.textMid }}>{s.notes}</div>}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
    <BrandFooterBar/>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// VENDOR (CONTRACTOR) ADMIN PORTAL
// ─────────────────────────────────────────────────────────────────────────
// A contractor's own limited admin view — Your Crew (their own staff, who
// log in through the same PIN-based Team Member screen everyone else
// uses), Schedule (set shifts for that crew at the one client they're
// contracted to), Scorecard (read-only, computed from the same
// schedule/checklist data the client's own Time Card already relies on),
// and Contract (read-only, their current vendorContracts row).
function VendorScheduleSection({ client, crew }) {
  const [rules, setRules] = useState([]);
  const [shiftForm, setShiftForm] = useState(null);
  const [shiftErr, setShiftErr] = useState("");
  const [savingShift, setSavingShift] = useState(false);

  useEffect(() => {
    const q = query(collection(db,"scheduleRules"), where("clientId","==",client.id));
    return onSnapshot(q, snap => setRules(snap.docs.map(d=>({id:d.id,...d.data()})).filter(r=>crew.some(m=>m.id===r.memberId))));
  }, [client.id, crew]);

  const openShiftForm = () => setShiftForm({ memberIds: new Set(crew[0]?.id?[crew[0].id]:[]), mode:"weekly", days:new Set(), monthDay:new Date().getDate(), rangeStart: todayKey(), startTime:"", endTime:"" });
  const toggleShiftDay = (i) => { const days = new Set(shiftForm.days); days.has(i)?days.delete(i):days.add(i); setShiftForm(f=>({...f,days})); };
  const toggleShiftMember = (id) => { const ids = new Set(shiftForm.memberIds); ids.has(id)?ids.delete(id):ids.add(id); setShiftForm(f=>({...f,memberIds:ids})); };

  const submitShiftForm = async () => {
    const selected = crew.filter(m=>shiftForm.memberIds.has(m.id));
    if (selected.length===0) { setShiftErr("Pick at least one crew member."); return; }
    if (!shiftForm.rangeStart) { setShiftErr("Pick a start date."); return; }
    if ((shiftForm.mode==="weekly"||shiftForm.mode==="biweekly") && shiftForm.days.size===0) { setShiftErr("Pick at least one day of the week."); return; }
    if (shiftForm.mode==="monthly") {
      const d = Number(shiftForm.monthDay);
      if (!d || d<1 || d>31) { setShiftErr("Pick a valid day of the month (1–31)."); return; }
    }
    setSavingShift(true); setShiftErr("");
    try {
      for (const member of selected) {
        await createScheduleRule({
          memberId: member.id, memberName: member.name,
          clientId: client.id, clientName: client.name,
          startTime: shiftForm.startTime||null, endTime: shiftForm.endTime||null, notes: "",
          mode: shiftForm.mode, rangeStart: shiftForm.rangeStart,
          days: shiftForm.mode==="monthly" ? null : Array.from(shiftForm.days),
          monthDay: shiftForm.mode==="monthly" ? Number(shiftForm.monthDay) : null,
        });
      }
      setShiftForm(null);
    } catch (e) {
      setShiftErr("Failed to save: " + e.message);
    }
    setSavingShift(false);
  };

  if (crew.length === 0) return (
    <div style={{ background:C.white, borderRadius:"10px", border:`1px solid ${C.border}`, padding:"20px", fontSize:"13px", color:C.textMid }}>
      Add a crew member first, then come back here to set their schedule.
    </div>
  );

  return (
    <div style={{ background:C.white, borderRadius:"10px", border:`1px solid ${C.border}`, padding:"16px" }}>
      {rules.length===0 && !shiftForm && <div style={{ fontSize:"13px", color:C.textMid, marginBottom:"10px" }}>No recurring shifts set up yet.</div>}
      {rules.map(r=>(
        <div key={r.id} style={{ background:C.offWhite, borderRadius:"10px", padding:"10px 12px", marginBottom:"8px", display:"flex", alignItems:"center", gap:"10px" }}>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontWeight:"600", fontSize:"13px" }}>{r.memberName}</div>
            <div style={{ fontSize:"11px", color:C.textMid }}>
              {RULE_LABEL[r.mode]} · {ruleDayLabel(r)} · since {fmtDateKey(r.rangeStart)}
              {(r.startTime || r.endTime) && ` · ${fmtTime12(r.startTime)}${r.endTime?` – ${fmtTime12(r.endTime)}`:""}`}
            </div>
          </div>
          <span style={S.badge(r.active?C.green:C.textMid)}>{r.active?"Active":"Inactive"}</span>
          <button
            style={{ ...S.btnSm(r.active?C.offWhite:C.green, r.active?C.textMid:C.white), border:r.active?`1.5px solid ${C.border}`:"none" }}
            onClick={()=>toggleRuleActive(r)}>
            {r.active?"Deactivate":"Activate"}
          </button>
          <button style={{ background:"transparent", border:"none", color:C.red, cursor:"pointer", fontSize:"16px" }} onClick={()=>deleteRule(r.id)}>✕</button>
        </div>
      ))}

      {shiftForm ? (
        <div style={{ background:C.offWhite, borderRadius:"10px", padding:"12px" }}>
          {shiftErr && <div style={S.err}>{shiftErr}</div>}
          <div style={{ fontSize:"11px", color:C.textMid, marginBottom:"3px" }}>Crew member(s) — pick more than one if this property is cleaned by several people at once</div>
          <div style={{ display:"flex", gap:"6px", marginBottom:"10px", flexWrap:"wrap" }}>
            {crew.map(m=>(
              <button key={m.id} onClick={()=>toggleShiftMember(m.id)}
                style={{ padding:"6px 12px", borderRadius:"8px", border:`1.5px solid ${shiftForm.memberIds.has(m.id)?C.accent:C.border}`, background:shiftForm.memberIds.has(m.id)?C.accent:C.white, color:shiftForm.memberIds.has(m.id)?C.white:C.textMid, fontWeight:"600", fontSize:"12px", cursor:"pointer" }}>
                {m.name}
              </button>
            ))}
          </div>
          <div style={{ display:"flex", gap:"6px", marginBottom:"10px", flexWrap:"wrap" }}>
            {["weekly","biweekly","monthly"].map(m=>(
              <button key={m} onClick={()=>setShiftForm(f=>({...f,mode:m}))}
                style={{ padding:"6px 12px", borderRadius:"8px", border:`1.5px solid ${shiftForm.mode===m?C.accent:C.border}`, background:shiftForm.mode===m?"#EEF3FB":C.white, color:shiftForm.mode===m?C.accent:C.textMid, fontWeight:"600", fontSize:"12px", cursor:"pointer" }}>
                {m==="weekly"?"Weekly":m==="biweekly"?"Every 2 weeks":"Monthly"}
              </button>
            ))}
          </div>
          {shiftForm.mode==="monthly" ? (
            <div style={{ marginBottom:"10px" }}>
              <div style={{ fontSize:"11px", color:C.textMid, marginBottom:"3px" }}>Day of month</div>
              <input style={{ ...S.input, width:"90px" }} type="number" min="1" max="31" value={shiftForm.monthDay}
                onChange={e=>setShiftForm(f=>({...f,monthDay:e.target.value}))} />
            </div>
          ) : (
            <div style={{ display:"flex", gap:"6px", marginBottom:"10px", flexWrap:"wrap" }}>
              {DOW.map((d,i)=>(
                <button key={d} onClick={()=>toggleShiftDay(i)}
                  style={{ width:"38px", padding:"7px 0", borderRadius:"8px", border:`1.5px solid ${shiftForm.days.has(i)?C.accent:C.border}`, background:shiftForm.days.has(i)?C.accent:C.white, color:shiftForm.days.has(i)?C.white:C.textMid, fontWeight:"600", fontSize:"11px", cursor:"pointer" }}>
                  {d}
                </button>
              ))}
            </div>
          )}
          <div style={{ marginBottom:"10px" }}>
            <div style={{ fontSize:"11px", color:C.textMid, marginBottom:"3px" }}>Starts</div>
            <input style={{ ...S.input, marginBottom:0, width:"160px" }} type="date" value={shiftForm.rangeStart} onChange={e=>setShiftForm(f=>({...f,rangeStart:e.target.value}))} />
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"10px", marginBottom:"10px" }}>
            <div>
              <div style={{ fontSize:"11px", color:C.textMid, marginBottom:"3px" }}>Start time (optional)</div>
              <input style={{ ...S.input, marginBottom:0 }} type="time" value={shiftForm.startTime} onChange={e=>setShiftForm(f=>({...f,startTime:e.target.value}))} />
            </div>
            <div>
              <div style={{ fontSize:"11px", color:C.textMid, marginBottom:"3px" }}>End time (optional)</div>
              <input style={{ ...S.input, marginBottom:0 }} type="time" value={shiftForm.endTime} onChange={e=>setShiftForm(f=>({...f,endTime:e.target.value}))} />
            </div>
          </div>
          <div style={{ display:"flex", gap:"8px" }}>
            <button style={S.btnSm()} onClick={submitShiftForm} disabled={savingShift}>{savingShift?"Saving…":"Save Recurring Shift"}</button>
            <button style={S.btnSm(C.offWhite,C.textMid)} onClick={()=>setShiftForm(null)}>Cancel</button>
          </div>
        </div>
      ) : (
        <button style={S.btnSm()} onClick={openShiftForm}>+ Add Recurring Shift</button>
      )}
    </div>
  );
}

function VendorScorecardTab({ client, crew }) {
  const [visits, setVisits] = useState([]);
  const [checklist, setChecklist] = useState([]);
  const crewIds = crew.map(m=>m.id);

  useEffect(() => {
    const q = query(collection(db,"schedule"), where("clientId","==",client.id));
    return onSnapshot(q, snap => setVisits(snap.docs.map(d=>d.data()).filter(v=>crewIds.includes(v.memberId))));
  }, [client.id, crew]);

  useEffect(() => onSnapshot(collection(db,"clients",client.id,"checklist"), snap => setChecklist(snap.docs.map(d=>d.data()))), [client.id]);

  const past = visits.filter(v=>v.date <= todayKey());
  const completed = past.filter(v=>v.checkedInAt).length;
  const missed = past.filter(v=>!v.checkedInAt && v.missedNotifiedAt).length;
  const checklistPct = checklist.length ? Math.round(checklist.filter(i=>i.done).length / checklist.length * 100) : null;

  if (crew.length === 0) return (
    <div style={{ background:C.white, borderRadius:"10px", border:`1px solid ${C.border}`, padding:"20px", fontSize:"13px", color:C.textMid }}>
      Numbers show up here once you've added crew and they start clocking in.
    </div>
  );

  return (
    <div style={{ background:C.white, borderRadius:"10px", border:`1px solid ${C.border}`, padding:"20px" }}>
      <div style={{ ...S.sectionTitle, marginBottom:"16px" }}>Your scorecard at {client.name}</div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(130px, 1fr))", gap:"16px" }}>
        <div>
          <div style={{ fontSize:"24px", fontWeight:"700", color:C.navy }}>{past.length}</div>
          <div style={{ fontSize:"12px", color:C.textMid }}>Visits scheduled</div>
        </div>
        <div>
          <div style={{ fontSize:"24px", fontWeight:"700", color:C.green }}>{completed}</div>
          <div style={{ fontSize:"12px", color:C.textMid }}>Completed</div>
        </div>
        <div>
          <div style={{ fontSize:"24px", fontWeight:"700", color:missed>0?C.red:C.textDark }}>{missed}</div>
          <div style={{ fontSize:"12px", color:C.textMid }}>Missed</div>
        </div>
        <div>
          <div style={{ fontSize:"24px", fontWeight:"700", color:C.navy }}>{checklistPct===null?"—":`${checklistPct}%`}</div>
          <div style={{ fontSize:"12px", color:C.textMid }}>Checklist complete</div>
        </div>
      </div>
      {past.length===0 && <div style={{ fontSize:"13px", color:C.textMid, marginTop:"16px" }}>No visits logged yet — numbers show up here once your crew starts clocking in.</div>}
    </div>
  );
}

function VendorContractTab({ contract }) {
  return (
    <div style={{ background:C.white, borderRadius:"10px", border:`1px solid ${C.border}`, padding:"20px", maxWidth:"480px" }}>
      <div style={{ ...S.sectionTitle, marginBottom:"10px" }}>Your contract</div>
      {contract ? (
        <>
          <div style={{ fontSize:"14px", marginBottom:"8px" }}>Started <b>{fmtDateKey(contract.startDate)}</b></div>
          <span style={S.badge(C.green)}>Current</span>
        </>
      ) : (
        <div style={{ fontSize:"13px", color:C.textMid }}>No active contract on file — contact your admin.</div>
      )}
    </div>
  );
}

// A finger/mouse-drawn signature captured to a canvas, exported as a PNG
// blob on Save. Coordinates are scaled from the canvas's rendered CSS size
// to its actual pixel buffer, so drawing stays accurate at any screen width
// (the buffer is a fixed 320x140, but the element itself is responsive).
function SignaturePad({ onSave, onCancel, saving }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const lastPos = useRef(null);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const point = e.touches ? e.touches[0] : e;
    return {
      x: (point.clientX - rect.left) * (canvas.width / rect.width),
      y: (point.clientY - rect.top) * (canvas.height / rect.height),
    };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; lastPos.current = getPos(e); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const pos = getPos(e);
    ctx.strokeStyle = C.textDark; ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.lineJoin = "round";
    ctx.beginPath(); ctx.moveTo(lastPos.current.x, lastPos.current.y); ctx.lineTo(pos.x, pos.y); ctx.stroke();
    lastPos.current = pos;
  };
  const end = () => { drawing.current = false; };
  const clear = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0,0,c.width,c.height); };
  const save = () => { canvasRef.current.toBlob(blob => blob && onSave(blob), "image/png"); };

  return (
    <div>
      <canvas
        ref={canvasRef} width={320} height={140}
        style={{ border:`1.5px solid ${C.border}`, borderRadius:"8px", background:C.white, touchAction:"none", width:"100%", maxWidth:"320px", display:"block" }}
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
      />
      <div style={{ display:"flex", gap:"8px", marginTop:"8px" }}>
        <button type="button" style={S.btnSm(C.offWhite,C.textMid)} onClick={clear}>Clear</button>
        <button type="button" style={S.btnSm(C.green)} onClick={save} disabled={saving}>{saving?"Saving…":"Save Signature"}</button>
        <button type="button" style={S.btnSm(C.offWhite,C.textMid)} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// A contractor's assigned Work Orders — each one lists its assigned crew and
// their sign-off status. Signing is meant to happen on location: whoever's
// on-site (the contractor, usually) hands the device to the named crew
// member to draw their own signature. One signature per assigned crew
// member; a work order counts as fully signed once everyone assigned has.
// The contractor's view of their Work Orders — Admin creates one for them
// unassigned, and it's the contractor (not Admin) who decides which of
// their own crew it goes to. Once assigned, signing happens only from the
// crew member's own Team Member login (see the "workorders" tab in the
// shared team/client portal, ~role==="team" && member.vendorId) — this view
// is read-only status after that point.
function VendorWorkOrdersTab({ vendorId, vendorName, client, crew }) {
  const [workOrders, setWorkOrders] = useState([]);
  const [assigningId, setAssigningId] = useState(null); // workOrder id currently being assigned
  const [pickedCrewIds, setPickedCrewIds] = useState(() => new Set());
  const [savingAssign, setSavingAssign] = useState(false);

  useEffect(() => {
    const q = query(collection(db,"workOrders"), where("vendorId","==",vendorId));
    return onSnapshot(q, snap => setWorkOrders(snap.docs.map(d=>({id:d.id,...d.data()}))));
  }, [vendorId]);

  const togglePicked = (id) => setPickedCrewIds(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  const startAssigning = (woId) => { setAssigningId(woId); setPickedCrewIds(new Set()); };
  const confirmAssign = async (wo) => {
    if (pickedCrewIds.size === 0) return;
    setSavingAssign(true);
    const chosen = crew.filter(m => pickedCrewIds.has(m.id));
    await updateDoc(doc(db,"workOrders",wo.id), {
      assignedCrew: chosen.map(m => ({ id: m.id, name: m.name })),
      assignedCrewIds: chosen.map(m => m.id),
    });
    setSavingAssign(false); setAssigningId(null);
  };

  if (workOrders.length===0) return <div style={{ color:C.textMid, fontSize:"13px" }}>No work orders yet.</div>;

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:"12px" }}>
      {workOrders.map(wo=>{
        const signedIds = new Set((wo.signatures||[]).map(s=>s.crewId));
        const crewCount = (wo.assignedCrew||[]).length;
        return (
          <div key={wo.id} style={{ ...S.memberCard, flexDirection:"column", alignItems:"stretch", gap:"8px" }}>
            <div style={{ fontWeight:"600", fontSize:"14px" }}>{wo.title}</div>
            {wo.description && <div style={{ fontSize:"13px", color:C.textMid }}>{wo.description}</div>}
            {wo.pdfUrl && <a href={wo.pdfUrl} target="_blank" rel="noreferrer" style={{ fontSize:"13px", color:C.accent }}>📄 View work order PDF</a>}
            {crewCount===0 ? (
              assigningId===wo.id ? (
                <div style={{ marginTop:"6px" }}>
                  {crew.length===0 && <div style={{ fontSize:"12px", color:C.textMid }}>Add a crew member first (Your Crew tab).</div>}
                  {crew.map(m=>(
                    <label key={m.id} style={{ display:"flex", alignItems:"center", gap:"8px", fontSize:"13px", padding:"4px 0", cursor:"pointer" }}>
                      <input type="checkbox" checked={pickedCrewIds.has(m.id)} onChange={()=>togglePicked(m.id)} />
                      {m.name}
                    </label>
                  ))}
                  <div style={{ display:"flex", gap:"8px", marginTop:"8px" }}>
                    <button style={S.btnSm(C.green)} onClick={()=>confirmAssign(wo)} disabled={savingAssign || pickedCrewIds.size===0}>{savingAssign?"Assigning…":"Confirm"}</button>
                    <button style={S.btnSm(C.offWhite,C.textMid)} onClick={()=>setAssigningId(null)}>Cancel</button>
                  </div>
                </div>
              ) : (
                <button style={{ ...S.btnSm(C.orange), alignSelf:"flex-start" }} onClick={()=>startAssigning(wo.id)}>Assign to crew</button>
              )
            ) : (
              <div style={{ display:"flex", flexDirection:"column", gap:"6px", marginTop:"4px" }}>
                {(wo.assignedCrew||[]).map(c=>{
                  const sig = (wo.signatures||[]).find(s=>s.crewId===c.id);
                  return (
                    <div key={c.id} style={{ display:"flex", alignItems:"center", gap:"8px", fontSize:"13px" }}>
                      <span style={{ flex:1 }}>{c.name}</span>
                      {sig ? (
                        <span style={{ color:C.green, fontWeight:"600" }}>✅ Signed by {sig.clientName || "client"} · {fmtDateKey(sig.signedAt)}</span>
                      ) : (
                        <span style={{ color:C.amber, fontWeight:"600" }}>⏳ Waiting for signature</span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            <WorkOrderActions wo={wo} vendorName={vendorName} clientName={client?.name} prefillEmail={client?.reportEmails?.[0]} />
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// VENDOR HOME — urgent-info brief for the contractor admin: unassigned
// work orders surfaced as a red alert (crew can't start until someone
// picks who's doing it), today's schedule/check-in status, work orders
// awaiting a client signature, and crew/contract quick links. Real data
// only — same workOrders/schedule/checklist queries VendorWorkOrdersTab
// and VendorScorecardTab already use.
// ─────────────────────────────────────────────────────────────────────────
function VendorHomeTab({ client, vendor, crew, contract, vendorId, onGoTo }) {
  const [workOrders,    setWorkOrders]    = useState([]);
  const [todaySchedule, setTodaySchedule] = useState([]);
  const crewIds = crew.map(m=>m.id);

  useEffect(() => {
    const q = query(collection(db,"workOrders"), where("vendorId","==",vendorId));
    return onSnapshot(q, snap => setWorkOrders(snap.docs.map(d=>({id:d.id,...d.data()}))));
  }, [vendorId]);

  useEffect(() => {
    const q = query(collection(db,"schedule"), where("clientId","==",client.id), where("date","==",todayKey()));
    return onSnapshot(q, snap => setTodaySchedule(snap.docs.map(d=>d.data()).filter(v=>crewIds.includes(v.memberId))));
  }, [client.id, crew]);

  const unassigned = workOrders.filter(wo => (wo.assignedCrew||[]).length===0);
  const awaitingSignature = workOrders.filter(wo => {
    const assigned = wo.assignedCrew||[];
    if (assigned.length===0) return false;
    const signedIds = new Set((wo.signatures||[]).map(s=>s.crewId));
    return assigned.some(c=>!signedIds.has(c.id));
  });
  const checkedIn = todaySchedule.filter(s=>s.checkedInAt).length;

  return (
    <div>
      <div style={{ fontSize:"20px", fontWeight:"700", color:C.textDark, marginBottom:"2px" }}>{vendor?.companyName || "Your crew"}</div>
      <div style={{ fontSize:"13px", color:C.textMid, marginBottom:"18px" }}>at {client.name}</div>

      {unassigned.length>0 && (
        <div style={{ ...S.homeCard, background:`linear-gradient(135deg, ${C.red}, #8A2020)` }} onClick={()=>onGoTo("workorders")}>
          <div style={{ fontSize:"11px", opacity:0.85, textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:"4px" }}>Needs attention</div>
          <div style={{ fontSize:"16px", fontWeight:"700" }}>{unassigned.length} work order{unassigned.length===1?"":"s"} need{unassigned.length===1?"s":""} a crew</div>
        </div>
      )}

      <div style={S.homeTileGrid}>
        <div style={S.homeTile} onClick={()=>onGoTo("crew")}>
          <div style={{ fontSize:"20px" }}>👤</div>
          <div style={S.homeTileLabel}>Your Crew</div>
          <div style={S.homeTileSub}>{crew.length} member{crew.length===1?"":"s"}</div>
        </div>
        <div style={S.homeTile} onClick={()=>onGoTo("schedule")}>
          <div style={{ fontSize:"20px" }}>📅</div>
          <div style={S.homeTileLabel}>Today's visits</div>
          <div style={S.homeTileSub}>{todaySchedule.length ? `${checkedIn}/${todaySchedule.length} checked in` : "None scheduled"}</div>
        </div>
        <div style={S.homeTile} onClick={()=>onGoTo("workorders")}>
          <div style={{ fontSize:"20px" }}>📋</div>
          <div style={S.homeTileLabel}>Work Orders</div>
          <div style={S.homeTileSub}>{awaitingSignature.length>0 ? `${awaitingSignature.length} awaiting signature` : `${workOrders.length} total`}</div>
        </div>
        <div style={S.homeTile} onClick={()=>onGoTo("contract")}>
          <div style={{ fontSize:"20px" }}>📄</div>
          <div style={S.homeTileLabel}>Contract</div>
          <div style={S.homeTileSub}>{contract ? "Current" : "No active contract"}</div>
        </div>
      </div>
    </div>
  );
}

const VENDOR_NAV_TABS = [
  { id:"home",       icon:"🏠", short:"Home",       label:"🏠 Home" },
  { id:"crew",       icon:"👤", short:"Crew",       label:"👤 Your Crew" },
  { id:"schedule",   icon:"📅", short:"Schedule",   label:"📅 Schedule" },
  { id:"workorders", icon:"📋", short:"Work Orders", label:"📋 Work Orders" },
  { id:"scorecard",  icon:"📊", short:"Scorecard",  label:"📊 Scorecard" },
];

function VendorAdminPortal({ membership, clients, onSignOut }) {
  const [tab, setTab] = useState("home");
  // Same bottom-nav-on-mobile / top-bar-on-desktop pattern as AdminPortal
  // and Portal. "Contract" isn't a primary nav slot (viewed rarely) —
  // reached from the Home tile instead, with a "‹ Home" back-link.
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);
  const [contracts, setContracts] = useState([]);
  const [vendor, setVendor] = useState(null);
  const [newCrewName, setNewCrewName] = useState("");
  const [newCrewPin, setNewCrewPin] = useState("");
  const [savingCrew, setSavingCrew] = useState(false);
  const [editingCrewPin, setEditingCrewPin] = useState(null); // { id, value }

  const client = clients.find(c => c.id === membership.clientId);
  const [crew, setCrew] = useState([]);
  const currentContract = contracts.find(c => !c.endDate);

  useEffect(() => onSnapshot(doc(db,"vendors",membership.vendorId), s => setVendor(s.exists() ? s.data() : null)), [membership.vendorId]);

  useEffect(() => {
    const q = query(collection(db,"vendorContracts"), where("vendorId","==",membership.vendorId));
    return onSnapshot(q, snap => setContracts(snap.docs.map(d=>({id:d.id,...d.data()}))));
  }, [membership.vendorId]);

  // Scoped to this vendor's own crew only — a Vendor Admin session used to
  // receive the *entire* company's teamMembers collection (every other
  // vendor's crew, plus direct staff) and filter it client-side just to
  // find its own. Querying by vendorId directly means a contractor's
  // browser never downloads another contractor's (or the buyer's own
  // staff's) names/photos/PINs in the first place.
  useEffect(() => {
    const q = query(collection(db,"teamMembers"), where("vendorId","==",membership.vendorId));
    return onSnapshot(q, snap => setCrew(snap.docs.map(d=>({id:d.id,...d.data()}))));
  }, [membership.vendorId]);

  const addCrewMember = async () => {
    if (!newCrewName.trim() || !newCrewPin.trim()) return;
    setSavingCrew(true);
    await addDoc(collection(db,"teamMembers"), {
      name: newCrewName.trim(), pin: newCrewPin.trim(), photoUrl: null,
      vendorId: membership.vendorId, createdAt: Date.now(),
    });
    setNewCrewName(""); setNewCrewPin(""); setSavingCrew(false);
  };

  if (!client) return <div style={{ ...S.app, display:"flex", alignItems:"center", justifyContent:"center", minHeight:"100vh", color:C.textMid }}>Loading…</div>;

  return (
    <div style={S.app}>
      <div style={S.header}>
        <div style={S.headerLeft}>
          <img src={DEFAULT_LOGO_URL} alt={BRAND.name} style={{ height:"32px", objectFit:"contain", flexShrink:0, filter:"brightness(0) invert(1)" }} />
          <div style={{ minWidth:0, overflow:"hidden" }}>
            <div style={{ fontWeight:"700", fontSize:"14px", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{vendor?.companyName || "Contractor"}</div>
            <div style={{ fontSize:"12px", color:"#A8C0E8", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>at {client.name}</div>
          </div>
        </div>
        <div style={S.headerRight}>
          <div style={S.pill(C.orange+"33", "#C8820A")}>{membership.name}</div>
          <button style={S.logoutBtn} onClick={onSignOut}>Sign Out</button>
        </div>
      </div>

      {!isMobile && (
        <div style={S.tabs}>
          {VENDOR_NAV_TABS.map(t=>(
            <div key={t.id} style={S.tab(tab===t.id, C.orange)} onClick={()=>setTab(t.id)}>{t.label}</div>
          ))}
        </div>
      )}

      <div style={{ ...S.body, ...(isMobile ? { paddingBottom:"80px" } : {}) }}>
        {tab==="home" && (
          <VendorHomeTab client={client} vendor={vendor} crew={crew} contract={currentContract} vendorId={membership.vendorId} onGoTo={setTab} />
        )}
        {tab==="contract" && (
          <div style={S.backRow} onClick={()=>setTab("home")}>‹ Home</div>
        )}
        {tab==="crew" && (<>
          <div style={S.sectionTitle}>Your crew</div>
          <div style={{ fontSize:"12px", color:C.textMid, marginTop:"-4px", marginBottom:"12px" }}>
            They sign in through the regular Team Member PIN screen — no account needed.
          </div>
          {crew.length===0 && <div style={{ color:C.textMid,fontSize:"13px",marginBottom:"14px" }}>No crew added yet.</div>}
          {crew.map(m=>(
            <div key={m.id} style={{ ...S.memberCard, flexWrap:"wrap", gap:"10px" }}>
              <div style={{ flex:1, minWidth:"120px" }}>
                <div style={{ fontWeight:"600", fontSize:"14px" }}>{m.name}</div>
                <div style={{ fontSize:"12px", color:C.textMid }}>PIN: {"•".repeat(m.pin?.length||4)}</div>
              </div>
              {editingCrewPin?.id === m.id ? (
                <div style={{ display:"flex", gap:"6px", alignItems:"center" }}>
                  <input
                    style={{ ...S.input, marginBottom:0, width:"90px", padding:"7px 10px" }}
                    type="password" placeholder="New PIN"
                    value={editingCrewPin.value}
                    onChange={e=>setEditingCrewPin(p=>({...p, value:e.target.value}))}
                    maxLength={8} autoFocus
                  />
                  <button style={S.btnSm(C.green)} onClick={async ()=>{
                    if (!editingCrewPin.value.trim()) return;
                    await updateDoc(doc(db,"teamMembers",m.id),{ pin: editingCrewPin.value.trim() });
                    setEditingCrewPin(null);
                  }}>Save</button>
                  <button style={S.btnSm(C.offWhite, C.textMid)} onClick={()=>setEditingCrewPin(null)}>✕</button>
                </div>
              ) : (
                <button style={S.btnSm("#EEF3FB", C.accent)} onClick={()=>setEditingCrewPin({ id:m.id, value:m.pin||"" })}>Change PIN</button>
              )}
              <button onClick={()=>{ if (window.confirm(`Remove ${m.name}?`)) deleteDoc(doc(db,"teamMembers",m.id)); }} style={{ background:"transparent",border:"none",color:C.red,cursor:"pointer",fontSize:"18px" }}>×</button>
            </div>
          ))}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"8px" }}>
            <input style={{ ...S.input, marginBottom:0 }} placeholder="Full name" value={newCrewName} onChange={e=>setNewCrewName(e.target.value)} />
            <input style={{ ...S.input, marginBottom:0 }} type="password" placeholder="PIN" value={newCrewPin} onChange={e=>setNewCrewPin(e.target.value)} maxLength={8} />
          </div>
          <button style={{ ...S.btn, marginTop:"10px" }} onClick={addCrewMember} disabled={savingCrew}>{savingCrew?"Saving…":"+ Add Crew Member"}</button>
        </>)}

        {tab==="schedule" && <VendorScheduleSection client={client} crew={crew} />}
        {tab==="workorders" && <VendorWorkOrdersTab vendorId={membership.vendorId} vendorName={vendor?.companyName} client={client} crew={crew} />}
        {tab==="scorecard" && <VendorScorecardTab client={client} crew={crew} />}
        {tab==="contract" && <VendorContractTab contract={currentContract} />}
      </div>

      {isMobile && (
        <div style={S.bottomNav}>
          {VENDOR_NAV_TABS.map(t=>(
            <div key={t.id} style={S.bottomNavItem(tab===t.id)} onClick={()=>setTab(t.id)}>
              <div style={{ fontSize:"20px" }}>{t.icon}</div>
              <div style={{ fontSize:"10px", fontWeight:tab===t.id?"700":"500" }}>{t.short}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// ROOT APP
// ─────────────────────────────────────────────────────────────────────────
export default function App() {
  const [screen,       setScreen]       = useState("login");
  const [role,         setRole]         = useState(null);
  const [member,       setMember]       = useState(null);
  const [activeClient, setActiveClient] = useState(null);
  const [clients,      setClients]      = useState([]);
  const [teamMembers,  setTeamMembers]  = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [logoUrl,      setLogoUrl]      = useState(null);
  const [adminPin,       setAdminPin]       = useState(null);
  const [adminSetupPin,  setAdminSetupPin]  = useState(null);
  const [estimatorPin,   setEstimatorPin]   = useState(null);
  const [adminEmail,     setAdminEmail]     = useState(null);
  const [authLoaded,     setAuthLoaded]     = useState(false);
  const [businessInfo,   setBusinessInfo]   = useState(null);
  const [portalInitialTab, setPortalInitialTab] = useState("general");
  // Which AdminPortal section to land on when returning from the separate
  // AdminSetup screen — set by AdminSetup's own bottom nav so "Done"/a nav
  // tap goes back to a specific section instead of always resetting to
  // Home (AdminPortal's `activeView` is local state, recreated fresh each
  // time it mounts, so this is the only way to steer where it lands).
  const [adminInitialView, setAdminInitialView] = useState("home");
  // Same idea, one level down — lets Admin Home's "+ New Work Order"
  // land straight on AdminSetup's Work Orders tab instead of its default.
  const [adminSetupInitialTab, setAdminSetupInitialTab] = useState(null);
  const [myClientIds,    setMyClientIds]    = useState(null); // null = not loaded yet, [] = loaded, none assigned
  // A Contractor (Vendor Admin) is a real, non-anonymous Firebase Auth
  // account — tracked separately from the anonymous-session `role`/`screen`
  // state machine above. `membership` stays null for everyone else
  // (anonymous sessions, and the existing real-admin-via-email path, which
  // has no memberships doc of its own).
  const [authUser,   setAuthUser]   = useState(null);
  const [membership, setMembership] = useState(null);
  const [lang, setLang] = useState(() => localStorage.getItem(LANG_KEY) || "en");
  const toggleLang = () => setLang(l => { const next = l==="en"?"es":"en"; localStorage.setItem(LANG_KEY, next); return next; });

  // Detect QR check-in URL params
  const params    = new URLSearchParams(window.location.search);
  const qrCid     = params.get("cid");
  const qrArea    = params.get("area");
  const isCheckin = !!(qrCid && qrArea);

  useEffect(() => {
    const onError = (e) => reportError(e.message, e.error?.stack);
    const onRejection = (e) => reportError(String(e.reason?.message || e.reason), e.reason?.stack);
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);

  useEffect(() => {
    const u1 = onSnapshot(collection(db,"clients"),     s=>{ setClients(s.docs.map(d=>({id:d.id,...d.data()})).sort((a,b)=>(a.createdAt||0)-(b.createdAt||0))); setLoading(false); });
    const u3 = onSnapshot(doc(db,"settings","branding"), s=>setLogoUrl(s.exists() ? s.data().logoUrl : null));
    const u4 = onSnapshot(doc(db,"settings","auth"), s=>{
      const data = s.exists() ? s.data() : {};
      setAdminPin(data.adminPin || null);
      setAdminSetupPin(data.adminSetupPin || null);
      setEstimatorPin(data.estimatorPin || null);
      setAdminEmail(data.adminEmail || null);
      setAuthLoaded(true);
    });
    const u5 = onSnapshot(doc(db,"settings","business"), s=>setBusinessInfo(s.exists() ? s.data() : null));
    return ()=>{ u1(); u3(); u4(); u5(); };
  }, []);

  // The full company-wide roster (every team member's name, photo, and
  // PIN) is only ever subscribed to for Admin, who's the one role that
  // actually manages the whole thing. Everyone else (a not-yet-signed-in
  // visitor, a signed-in Team Member, a Client, a Vendor Admin — who gets
  // their own scoped query for just their own crew, see VendorAdminPortal)
  // never has this data land in their browser at all. Team sign-in and the
  // QR photo check-in flow instead verify a PIN server-side via
  // teamMemberLogin (see TeamMemberLogin / PhotoCheckin), which returns
  // only the one matching member's own info.
  const teamRosterLoaded = role === "admin";
  useEffect(() => {
    if (!teamRosterLoaded) { setTeamMembers([]); return; }
    return onSnapshot(collection(db,"teamMembers"), s=>setTeamMembers(s.docs.map(d=>({id:d.id,...d.data()}))));
  }, [teamRosterLoaded]);

  // Team members only see clients they're actively scheduled with — not
  // the whole client list. Equality-only filters (memberId, active), same
  // reasoning as every other scheduleRules query this session: no
  // composite index needed since there's no orderBy alongside them.
  useEffect(() => {
    if (role !== "team" || !member?.id) { setMyClientIds(null); return; }
    const q = query(collection(db,"scheduleRules"), where("memberId","==",member.id), where("active","==",true));
    return onSnapshot(q, snap => setMyClientIds([...new Set(snap.docs.map(d=>d.data().clientId))]));
  }, [role, member?.id]);

  useEffect(() => onAuthStateChanged(auth, setAuthUser), []);

  // Only a real (non-anonymous) sign-in can own a membership doc — the
  // existing anonymous session everyone else uses never has one, and
  // signInAnonymously never fires this with a non-anonymous user.
  useEffect(() => {
    if (!authUser || authUser.isAnonymous) { setMembership(null); return; }
    return onSnapshot(doc(db,"memberships",authUser.uid), s => setMembership(s.exists() ? { id:s.id, ...s.data() } : null));
  }, [authUser?.uid, authUser?.isAnonymous]);

  // The PWA title/manifest baked in at build time defaults to the generic
  // template placeholder — once a buyer sets their real company name here,
  // reflect it in the tab title immediately with no rebuild needed.
  useEffect(() => {
    document.title = `${businessInfo?.companyName || BRAND.name} Portal`;
  }, [businessInfo]);

  // Keep activeClient fresh
  useEffect(()=>{ if(activeClient){ const f=clients.find(c=>c.id===activeClient.id); if(f) setActiveClient(f); } },[clients]); // eslint-disable-line

  // Keep the license service's Stripe subscription quantities in sync with
  // the buyer's actual roster. Debounced so rapid add/remove edits in Admin
  // Setup don't fire a sync per keystroke; skipped until the initial
  // teamMembers snapshot has loaded, and now that teamMembers is only ever
  // populated for an Admin session (see teamRosterLoaded above), gated on
  // that too, so a Team Member's, Client's, or Vendor Admin's session
  // (where it's always []) never reports a false "0 seats" sync. A Vendor
  // Admin adding/removing their own crew no longer triggers an immediate
  // sync themselves — it's corrected the next time an Admin session loads,
  // same debounced-eventual-consistency pattern as everything else here.
  const staffSeatCount = teamMembers.filter(m => !m.vendorId).length;
  const contractorSeatCount = teamMembers.filter(m => m.vendorId).length;
  useEffect(() => {
    if (loading || !teamRosterLoaded) return;
    const t = setTimeout(() => syncSeatCount(staffSeatCount, contractorSeatCount), 2000);
    return () => clearTimeout(t);
  }, [staffSeatCount, contractorSeatCount, loading, teamRosterLoaded]);

  const signOut = () => {
    setScreen("login"); setRole(null); setMember(null); setActiveClient(null);
    // A real admin sign-in (signInWithEmailAndPassword) replaces the default
    // anonymous session — drop back to a fresh anonymous one on sign-out so
    // no authenticated admin session lingers in the browser afterward.
    if (auth.currentUser && !auth.currentUser.isAnonymous) {
      firebaseSignOut(auth).then(() => signInAnonymously(auth)).catch(() => {});
    }
  };

  if (loading || !authLoaded) return <div style={S.spinner}>⏳ Connecting…</div>;

  // A signed-in Contractor (Vendor Admin) bypasses the anonymous-session
  // screen state machine entirely — this is the one role in the app with a
  // real per-user identity. Placed before the QR check-in bypass since it's
  // unrelated to that flow, but ordering between the two doesn't matter.
  if (membership?.role === "vendor_admin") {
    return <VendorAdminPortal membership={membership} clients={clients} onSignOut={signOut} />;
  }

  // Field cleaners scanning a QR code to document a job must always work,
  // even if the buyer's own subscription has lapsed — so this bypasses the
  // PortalAccessGate entirely rather than being wrapped by it below.
  if (isCheckin) return <PhotoCheckin clientId={qrCid} area={qrArea} logoUrl={logoUrl} lang={lang} toggleLang={toggleLang} />;

  let content = null;

  if (screen==="adminSetup") content = (
    <AdminSetup
      onDone={()=>setScreen("adminPortal")}
      onGoTo={(view)=>{ setAdminInitialView(view); setScreen("adminPortal"); }}
      initialTab={adminSetupInitialTab}
      logoUrl={logoUrl} adminSetupPin={adminSetupPin} estimatorPin={estimatorPin} adminEmail={adminEmail} businessInfo={businessInfo} />
  );

  else if (screen==="login") content = (
    <LoginScreen
      clients={clients}
      logoUrl={logoUrl}
      adminPin={adminPin}
      adminSetupPin={adminSetupPin}
      adminEmail={adminEmail}
      onLoginAdmin={()=>{ setRole("admin"); setAdminInitialView("home"); setScreen("adminPortal"); }}
      onLoginTeam={(m)=>{ setMember(m); setRole("team"); setScreen("clientSelector"); }}
      onLoginClient={(c)=>{ setRole("client"); setActiveClient(c); setScreen("portal"); }}
      onAdminSetup={()=>setScreen("adminSetup")}
      lang={lang} toggleLang={toggleLang}
    />
  );

  else if (screen==="adminPortal") content = (
    <AdminPortal clients={clients} teamMembers={teamMembers} onSignOut={signOut}
      onSetup={(tab)=>{ setAdminSetupInitialTab(tab||null); setScreen("adminSetup"); }}
      logoUrl={logoUrl} initialView={adminInitialView} />
  );

  else if (screen==="clientSelector") content = (
    <ClientSelector member={member} clients={clients.filter(c=>(myClientIds||[]).includes(c.id))} clientsLoaded={myClientIds!==null}
      onSelect={c=>{ setPortalInitialTab("timecard"); setActiveClient(c); setScreen("portal"); }}
      onSchedule={()=>setScreen("teamSchedule")}
      onSignOut={signOut}
      lang={lang} toggleLang={toggleLang} />
  );

  else if (screen==="teamSchedule") content = (
    <MemberScheduleTab member={member} logoUrl={logoUrl}
      onOpenClient={clientId=>{
        const c = clients.find(cl=>cl.id===clientId);
        if (!c) return;
        setPortalInitialTab("timecard");
        setActiveClient(c);
        setScreen("portal");
      }}
      onBack={()=>setScreen("clientSelector")}
      onSignOut={signOut}
      lang={lang} toggleLang={toggleLang} />
  );

  else if (screen==="portal") content = (
    <Portal role={role} member={member} activeClient={activeClient}
      onSwitchClient={()=>setScreen("clientSelector")}
      onSignOut={signOut}
      onLogoUpdate={url=>setActiveClient(c=>({...c,logoUrl:url}))}
      logoUrl={logoUrl}
      initialTab={portalInitialTab}
      lang={lang} toggleLang={toggleLang} />
  );

  return (
    <PortalAccessGate S={S} C={C} seatCount={teamMembers.length}>
      {content}
    </PortalAccessGate>
  );
}
